import { workspaceHref } from "@/cowork/workspace-route"
import { createEffect, createMemo, createResource, For, onCleanup, onMount, Show, untrack, type JSX } from "solid-js"
import { createStore } from "solid-js/store"
import { Portal } from "solid-js/web"
import { DocxPreview } from "@/components/docx-preview"
import { ApprovalRequestForm, ReportShareDialog } from "@/components/cowork-report-share"
import { ReportPreviewLink, CopyableReportLink } from "@/components/report-preview-link"
import { reportDisplayTitle, reportFormatLabel } from "@/cowork/report-preview"
import { approvalRecipients } from "@/cowork/approval-recipients"
import { ReportHtmlPreview } from "@/components/report-html-preview"
import { ReportDeleteButton } from "@/components/report-delete-button"
import { WorkflowActivity, WorkflowTabIndicator } from "@/components/workflow-activity"
import { workflowCompletion, type ResultTab } from "@/cowork/workflow-activity"
import { FileInspection } from "@/components/file-inspection"
import { ExtractionDataFields } from "@/components/file-extracted-data"
import {
  createAccountingDocument,
  approvePaymentMatch,
  createAccountingExportPackage,
  createAccountingTaxReport,
  deleteAccountingExportFile,
  deleteAccountingTaxReport,
  generateAccountingPostingSuggestion,
  generatePaymentMatches,
  getAccountingProjectSummary,
  getAccountingLedgerView,
  getAccountingReadiness,
  getFile,
  getFileContentBlob,
  downloadAccountingExportFile,
  getSessionContextByRemote,
  downloadArtifact,
  deleteArtifact,
  deleteFile,
  listArtifactsForWorkflow,
  listAccountingDocuments,
  listAccountingReviews,
  listAccountingTaxReports,
  listExtractions,
  listFiles,
  listPaymentMatches,
  listPaymentTransactions,
  listAccountingExportPackages,
  listTransformations,
  processAccountingDocument,
  approveAccountingReview,
  importPaymentTransactions,
  rejectAccountingReview,
  rejectPaymentMatch,
  updateAccountingExportManualStatus,
  uploadFile,
  type CoworkArtifact,
  type CoworkApproval,
  type CoworkAccountingDocument,
  type CoworkAccountingReview,
  type CoworkAccountingReadiness,
  type CoworkAccountingSummary,
  type CoworkAccountingExportFile,
  type CoworkAccountingExportPackage,
  type CoworkAccountingTaxReport,
  type CoworkSuggestedPostingGroup,
  type CoworkLedgerView,
  type CoworkLedgerJournalLine,
  type CoworkLedgerTrialBalanceRow,
  type CoworkExtraction,
  type CoworkFile,
  type CoworkPaymentMatch,
  type CoworkPaymentTransaction,
  type CoworkTransformation,
  updateSessionAccountingContext,
} from "@/cowork/api"
import { shouldShowAccountingMode } from "@/cowork/accounting-mode"
import { shouldRefreshCoworkFiles } from "@/cowork/file-events"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { DialogBody, DialogHeader, DialogTitle, DialogV2 } from "@opencode-ai/ui/v2/dialog-v2"
import { useLanguage } from "@/context/language"
import { formatDateTime } from "@/utils/date"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { TooltipV2 } from "@opencode-ai/ui/v2/tooltip-v2"

export function CoworkFilesPanel(props: { sessionID: string; mode?: CoworkMode }) {
  const language = useLanguage()
  const [store, setStore] = createStore({
    filter: "",
    activeFileID: "",
    tab: "files" as PipelineTab,
    accountingTab: "overview" as AccountingTab,
    ledgerView: "journal" as LedgerViewTab,
    taxView: "summary" as TaxViewTab,
    paymentView: "matched" as PaymentViewTab,
    selectedReviewID: "",
    selectedExtractionID: "",
    accountingFilter: "",
    taxReportType: "kmd",
    reportVisited: false,
    reportRefresh: 0,
    activeResultTabs: [] as ResultTab[],
    accountingUploadBusy: false,
    paymentAmount: "",
    paymentCurrency: "EUR",
    paymentCounterparty: "",
    paymentReference: "",
    paymentDescription: "",
  })
  const [context] = createResource(
    () => props.sessionID,
    (sessionID) => getSessionContextByRemote(sessionID),
  )
  const [accountingSummary] = createResource(
    () => {
      const value = context()
      return value ? { projectID: value.project_id, workflowID: value.workflow_id } : undefined
    },
    (value) => getAccountingProjectSummary(value.projectID, value.workflowID).catch(() => undefined),
  )
  const [files, filesActions] = createResource(context, (value) => {
    if (!value) return []
    return listFiles(value.project_id, value.workflow_id ?? undefined)
  })
  const [extractions, extractionActions] = createResource(context, (value) => {
    if (!value) return []
    return listExtractions(value.project_id, value.workflow_id)
  })
  const [transformations, transformationActions] = createResource(context, (value) => {
    if (!value) return []
    return listTransformations(value.project_id, value.workflow_id)
  })
  const reportContext = createMemo(() => {
    const value = context()
    if (!value || !store.reportVisited) return undefined
    return { projectID: value.project_id, workflowID: value.workflow_id, refresh: store.reportRefresh }
  })
  const [artifacts, artifactActions] = createResource(reportContext, (value) => {
    if (!value) return []
    value.refresh
    return listArtifactsForWorkflow(value.projectID, value.workflowID)
  })
  const [accountingDocuments, accountingDocumentActions] = createResource(context, (value) => {
    if (!value?.workflow_id) return []
    return listAccountingDocuments(value.workflow_id)
  })
  const [readiness, readinessActions] = createResource(context, (value) => {
    if (!value?.workflow_id) return undefined
    return getAccountingReadiness(value.workflow_id)
  })
  const [accountingReviews, accountingReviewActions] = createResource(context, (value) => {
    if (!value?.workflow_id) return []
    return listAccountingReviews(value.workflow_id)
  })
  const [payments, paymentActions] = createResource(context, (value) => {
    if (!value?.workflow_id) return []
    return listPaymentTransactions(value.workflow_id)
  })
  const [paymentMatches, paymentMatchActions] = createResource(context, (value) => {
    if (!value?.workflow_id) return []
    return listPaymentMatches(value.workflow_id)
  })
  const [exportPackages, exportPackageActions] = createResource(context, (value) => {
    if (!value?.workflow_id) return []
    return listAccountingExportPackages(value.workflow_id)
  })
  const [ledgerView, ledgerViewActions] = createResource(context, (value) => {
    if (!value?.workflow_id) return undefined
    return getAccountingLedgerView(value.workflow_id)
  })
  const [taxReports, taxReportActions] = createResource(context, (value) => {
    if (!value?.workflow_id) return []
    return listAccountingTaxReports(value.workflow_id)
  })
  const [accountingArtifacts, accountingArtifactActions] = createResource(context, (value) => {
    if (!value?.workflow_id) return []
    return listArtifactsForWorkflow(value.project_id, value.workflow_id)
  })
  const filteredFiles = createMemo(() => {
    const query = store.filter.trim().toLowerCase()
    if (!query) return files() ?? []
    return (files() ?? []).filter((file) => file.filename.toLowerCase().includes(query))
  })
  const activeFile = createMemo(
    () => filteredFiles().find((file) => file.id === store.activeFileID) ?? filteredFiles()[0],
  )
  const workflow = createMemo(() => {
    const metadata = context()?.session_metadata?.workflow
    if (!metadata || typeof metadata !== "object") return ""
    const workflowRecord = metadata as { name?: unknown }
    return typeof workflowRecord.name === "string" ? workflowRecord.name : ""
  })
  const accountingVisible = createMemo(() => shouldShowAccountingMode(accountingSummary()))
  const mode = createMemo<CoworkMode>(() => {
    if (props.mode === "reports") return "reports"
    return props.mode === "accounting" && accountingVisible() ? "accounting" : "workflow"
  })

  createEffect(() => {
    if (mode() === "reports") setStore("reportVisited", true)
  })

  const refetchWorkflow = () => {
    void filesActions.refetch()
    void extractionActions.refetch()
    void transformationActions.refetch()
  }

  const selectTab = (tab: PipelineTab) => {
    if (tab === "report" || tab === "output") setStore("reportVisited", true)
    setStore("tab", tab)
  }

  const activityScope = createMemo(() => {
    if (context.error) return
    const value = context.latest
    return value?.remote_opencode_session_id === props.sessionID ? value : undefined
  })
  const completedTabs = createMemo(() => {
    const scope = activityScope()
    return workflowCompletion(scope ? {
      company_id: scope.company_id, project_id: scope.project_id, period_id: scope.period_id,
      files: files.error ? [] : files() ?? [], extractions: extractions.error ? [] : extractions() ?? [],
      transformations: transformations.error ? [] : transformations() ?? [], artifacts: artifacts.error ? [] : artifacts() ?? [], approvals: [],
    } : undefined)
  })
  const refreshResults = (tabs: ResultTab[]) => {
    if (!activityScope()) return
    const refreshes: unknown[] = []
    if (tabs.includes("files")) refreshes.push(filesActions.refetch())
    if (tabs.includes("extractions")) refreshes.push(extractionActions.refetch())
    if (tabs.includes("transformations") || tabs.includes("output")) refreshes.push(transformationActions.refetch())
    if (tabs.includes("report")) {
      if (store.reportVisited) refreshes.push(artifactActions.refetch())
      else setStore("reportVisited", true)
    }
    if (mode() === "accounting") {
      refreshes.push(accountingDocumentActions.refetch(), accountingReviewActions.refetch(), readinessActions.refetch(),
        ledgerViewActions.refetch(), taxReportActions.refetch(), accountingArtifactActions.refetch(), exportPackageActions.refetch())
    }
    void Promise.allSettled(refreshes)
  }

  const removeFile = async (file: CoworkFile) => {
    if (activeFile()?.id === file.id) setStore("activeFileID", "")
    await deleteFile(file.project_id, file.id)
    await filesActions.refetch()
    const next = filteredFiles().find((item) => item.id !== file.id)
    setStore("activeFileID", next?.id ?? "")
  }

  const uploadAccountingDocument = async (file: File) => {
    const value = context()
    if (!value?.workflow_id) return
    setStore("accountingUploadBusy", true)
    try {
      const uploaded = await uploadFile(
        value.project_id,
        file,
        value.period_id ?? value.workflow_id ?? undefined,
        value.session_id,
      )
      await createAccountingDocument(value.workflow_id, uploaded.id)
      await filesActions.refetch()
      await accountingDocumentActions.refetch()
      await accountingReviewActions.refetch()
      await readinessActions.refetch()
    } finally {
      setStore("accountingUploadBusy", false)
    }
  }

  const addFileToAccounting = async (file: CoworkFile) => {
    const value = context()
    if (!value?.workflow_id) return
    await createAccountingDocument(value.workflow_id, file.id)
    await accountingDocumentActions.refetch()
    await accountingReviewActions.refetch()
    await readinessActions.refetch()
  }

  const processAccountingRecord = async (document: CoworkAccountingDocument) => {
    await processAccountingDocument(document.id)
    await accountingDocumentActions.refetch()
    await accountingReviewActions.refetch()
    await readinessActions.refetch()
  }

  const suggestAccountingPostings = async (document: CoworkAccountingDocument) => {
    await generateAccountingPostingSuggestion(document.id)
    await accountingDocumentActions.refetch()
    await accountingReviewActions.refetch()
    await readinessActions.refetch()
  }

  const approveReview = async (review: CoworkAccountingReview) => {
    await approveAccountingReview(review.id)
    await accountingDocumentActions.refetch()
    await accountingReviewActions.refetch()
    await readinessActions.refetch()
  }

  const rejectReview = async (review: CoworkAccountingReview) => {
    await rejectAccountingReview(review.id)
    await accountingDocumentActions.refetch()
    await accountingReviewActions.refetch()
    await readinessActions.refetch()
  }

  const importPayment = async () => {
    const value = context()
    const amount = Number(store.paymentAmount)
    if (!value?.workflow_id || !Number.isFinite(amount) || amount === 0) return
    await importPaymentTransactions(value.workflow_id, [
      {
        amount,
        currency: store.paymentCurrency || "EUR",
        counterparty_name: store.paymentCounterparty || undefined,
        reference: store.paymentReference || undefined,
        description: store.paymentDescription || undefined,
      },
    ])
    setStore("paymentAmount", "")
    setStore("paymentCounterparty", "")
    setStore("paymentReference", "")
    setStore("paymentDescription", "")
    await paymentActions.refetch()
  }

  const matchPayments = async () => {
    const value = context()
    if (!value?.workflow_id) return
    await generatePaymentMatches(value.workflow_id)
    await paymentActions.refetch()
    await paymentMatchActions.refetch()
    await accountingDocumentActions.refetch()
    await readinessActions.refetch()
  }

  const approveMatch = async (match: CoworkPaymentMatch) => {
    await approvePaymentMatch(match.id)
    await paymentActions.refetch()
    await paymentMatchActions.refetch()
    await accountingDocumentActions.refetch()
    await readinessActions.refetch()
  }

  const rejectMatch = async (match: CoworkPaymentMatch) => {
    await rejectPaymentMatch(match.id)
    await paymentActions.refetch()
    await paymentMatchActions.refetch()
  }

  const createExportPackage = async () => {
    const value = context()
    if (!value?.workflow_id) return
    await createAccountingExportPackage(value.workflow_id)
    await exportPackageActions.refetch()
    await ledgerViewActions.refetch()
    await readinessActions.refetch()
  }

  const createTaxReport = async () => {
    const value = context()
    if (!value?.workflow_id) return
    await createAccountingTaxReport(value.workflow_id, {
      report_type: store.taxReportType,
      jurisdiction: "EE",
      reporting_period: value.workflow_name ?? undefined,
    })
    await taxReportActions.refetch()
  }

  const removeTaxReport = async (report: CoworkAccountingTaxReport) => {
    await deleteAccountingTaxReport(report.id)
    await taxReportActions.refetch()
    await readinessActions.refetch()
  }

  const downloadTaxArtifact = async (artifact: CoworkArtifact) => {
    const result = await downloadArtifact(artifact.project_id, artifact.id)
    const url = URL.createObjectURL(result.blob)
    const link = document.createElement("a")
    link.href = url
    link.download = result.filename
    link.click()
    URL.revokeObjectURL(url)
  }

  const removeTaxArtifact = async (artifact: CoworkArtifact) => {
    await deleteArtifact(artifact.project_id, artifact.id)
    await accountingArtifactActions.refetch()
  }

  const markExportSubmitted = async (pkg: CoworkAccountingExportPackage) => {
    await updateAccountingExportManualStatus(pkg.id, "submitted_manually")
    await exportPackageActions.refetch()
    await readinessActions.refetch()
  }

  const downloadExportFile = async (file: CoworkAccountingExportFile) => {
    const result = await downloadAccountingExportFile(file.id)
    const bytes = Uint8Array.from(atob(result.content), (char) => char.charCodeAt(0))
    const blob = new Blob([bytes], { type: result.content_type || "application/octet-stream" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = result.filename
    link.click()
    URL.revokeObjectURL(url)
  }

  const deleteExportFile = async (file: CoworkAccountingExportFile) => {
    await deleteAccountingExportFile(file.id)
    await exportPackageActions.refetch()
  }

  const selectAccountingTab = (tab: AccountingTab, filter = "") => {
    setStore("accountingTab", tab)
    setStore("accountingFilter", filter)
  }

  const selectReviewContext = (review: CoworkAccountingReview) => {
    const value = context()
    setStore("selectedReviewID", review.id)
    window.dispatchEvent(
      new CustomEvent("cowork:accounting-context-selected", {
        detail: {
          reviewID: review.id,
          documentID: review.document_id,
          postingGroupID: review.posting_group_id,
          sourceFilename: review.document?.source_filename,
        },
      }),
    )
    if (!value) return
    void updateSessionAccountingContext(value.project_id, value.session_id, {
      selected_tab: "review",
      selected_review_id: review.id,
      selected_document_id: review.document_id,
      selected_posting_group_id: review.posting_group_id ?? undefined,
      source_filename: review.document?.source_filename ?? undefined,
    })
  }

  onMount(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail
      if (!shouldRefreshCoworkFiles(detail, context(), props.sessionID)) return
      refetchWorkflow()
    }
    const focusHandler = () => refetchWorkflow()
    const openFilesHandler = () => setStore({ tab: "files", activeFileID: "" })
    const openViewHandler = (event: Event) => {
      const detail = (
        event as CustomEvent<{ sessionID?: string; tab?: AccountingTab; filter?: string; reviewID?: string }>
      ).detail
      if (detail?.sessionID && detail.sessionID !== props.sessionID) return
      if (detail?.tab) selectAccountingTab(detail.tab, detail.filter ?? "")
      if (detail?.reviewID) setStore("selectedReviewID", detail.reviewID)
    }
    window.addEventListener("cowork-files-uploaded", handler)
    window.addEventListener("focus", focusHandler)
    window.addEventListener("cowork:files-tab-open", openFilesHandler)
    window.addEventListener("cowork:accounting-open-view", openViewHandler)
    onCleanup(() => {
      window.removeEventListener("cowork-files-uploaded", handler)
      window.removeEventListener("focus", focusHandler)
      window.removeEventListener("cowork:files-tab-open", openFilesHandler)
      window.removeEventListener("cowork:accounting-open-view", openViewHandler)
    })
  })

  return (
    <div class="flex h-full min-h-0 flex-col bg-v2-background-bg-base text-v2-text-text-base">
      <Show when={activityScope()}>{(scope) => <div class="shrink-0 border-b border-v2-border-border-base px-4 py-2">
        <WorkflowActivity companyID={scope().company_id} projectID={scope().project_id} periodID={scope().period_id ?? scope().workflow_id}
          remoteSessionID={props.sessionID} onResultsSaved={refreshResults} onActivity={(tabs) => setStore("activeResultTabs", tabs)} />
      </div>}</Show>
      <Show when={mode() === "accounting"}>
        <AccountingLayout
          clientName={context()?.project_name ?? ""}
          periodName={context()?.workflow_name ?? workflow()}
          sessionMetadata={context()?.session_metadata ?? {}}
          summary={accountingSummary()}
          readiness={readiness()}
          summaryLoading={accountingSummary.loading}
          readinessLoading={readiness.loading}
          files={files() ?? []}
          documents={accountingDocuments() ?? []}
          extractions={extractions() ?? []}
          reviews={accountingReviews() ?? []}
          payments={payments() ?? []}
          paymentMatches={paymentMatches() ?? []}
          exportPackages={exportPackages() ?? []}
          ledger={ledgerView()}
          ledgerLoading={ledgerView.loading}
          ledgerView={store.ledgerView}
          taxView={store.taxView}
          paymentView={store.paymentView}
          taxReports={taxReports() ?? []}
          taxArtifacts={(accountingArtifacts() ?? []).filter(isTaxReportArtifact)}
          taxReportType={store.taxReportType}
          documentsLoading={accountingDocuments.loading}
          reviewsLoading={accountingReviews.loading}
          paymentsLoading={payments.loading || paymentMatches.loading}
          exportPackagesLoading={exportPackages.loading}
          taxReportsLoading={taxReports.loading}
          uploadBusy={store.accountingUploadBusy}
          hasWorkflow={!!context()?.workflow_id}
          selectedExtractionID={store.selectedExtractionID}
          onUpload={uploadAccountingDocument}
          onAddToAccounting={addFileToAccounting}
          onProcess={processAccountingRecord}
          onSuggestPostings={suggestAccountingPostings}
          onApproveReview={approveReview}
          onRejectReview={rejectReview}
          paymentForm={{
            amount: store.paymentAmount,
            currency: store.paymentCurrency,
            counterparty: store.paymentCounterparty,
            reference: store.paymentReference,
            description: store.paymentDescription,
          }}
          onPaymentFormChange={(field, value) => setStore(field, value)}
          onImportPayment={importPayment}
          onMatchPayments={matchPayments}
          onApproveMatch={approveMatch}
          onRejectMatch={rejectMatch}
          onCreateExportPackage={createExportPackage}
          onMarkExportSubmitted={markExportSubmitted}
          onDownloadExportFile={downloadExportFile}
          onDeleteExportFile={deleteExportFile}
          onLedgerViewChange={(value) => setStore("ledgerView", value)}
          onTaxViewChange={(value) => setStore("taxView", value)}
          onPaymentViewChange={(value) => setStore("paymentView", value)}
          onCreateTaxReport={createTaxReport}
          onTaxReportTypeChange={(value) => setStore("taxReportType", value)}
          onDeleteTaxReport={removeTaxReport}
          onDownloadTaxArtifact={downloadTaxArtifact}
          onDeleteTaxArtifact={removeTaxArtifact}
          onSelectReview={selectReviewContext}
          selectedReviewID={store.selectedReviewID}
          filter={store.accountingFilter}
          onFilterChange={(value) => setStore("accountingFilter", value)}
          activeTab={store.accountingTab}
          onTabChange={(tab) => selectAccountingTab(tab)}
          onSelectExtraction={(id) => setStore("selectedExtractionID", id)}
        />
      </Show>
      <Show when={mode() === "reports"}>
        <ReportTab
          artifacts={artifacts() ?? []}
          loading={artifacts.loading}
          onRefresh={() => {
            setStore("reportVisited", true)
            setStore("reportRefresh", store.reportRefresh + 1)
          }}
          onDelete={async (artifact) => {
            await deleteArtifact(artifact.project_id, artifact.id)
            await artifactActions.refetch()
          }}
        />
      </Show>
      <Show when={mode() === "workflow"}>
        <div class="flex h-12 shrink-0 items-center gap-2 border-b border-v2-border-border-base px-3">
          <PipelineTabButton
            active={store.tab === "files"}
            complete={completedTabs().files} working={store.activeResultTabs.includes("files")}
            label={language.t("cowork.pipeline.files")}
            onClick={() => selectTab("files")}
          />
          <PipelineTabButton
            active={store.tab === "extractions"}
            complete={completedTabs().extractions} working={store.activeResultTabs.includes("extractions")}
            label={language.t("cowork.pipeline.extractions")}
            onClick={() => selectTab("extractions")}
          />
          <PipelineTabButton
            active={store.tab === "transformations"}
            complete={completedTabs().transformations} working={store.activeResultTabs.includes("transformations")}
            label={language.t("cowork.pipeline.transformations")}
            onClick={() => selectTab("transformations")}
          />
          <PipelineTabButton
            active={store.tab === "output"}
            complete={completedTabs().output} working={store.activeResultTabs.includes("output")}
            label={language.t("cowork.pipeline.output")}
            onClick={() => selectTab("output")}
          />
          <PipelineTabButton
            active={store.tab === "report"}
            complete={completedTabs().report} working={store.activeResultTabs.includes("report")}
            label={language.t("cowork.pipeline.report")}
            onClick={() => selectTab("report")}
          />
        </div>
        <Show when={store.tab === "files"}>
          <FilesTab
            workflow={workflow()}
            filter={store.filter}
            setFilter={(value) => setStore("filter", value)}
            files={filteredFiles()}
            totalFiles={files()?.length}
            activeFile={activeFile()}
            periodID={context()?.workflow_id}
            selectFile={(fileID) => setStore("activeFileID", fileID)}
            hasContext={!!context()}
            onDelete={removeFile}
          />
        </Show>
        <Show when={store.tab === "extractions"}>
          <RecordsTab
            title={language.t("cowork.pipeline.extractions")}
            records={extractions() ?? []}
            kind="extraction"
            files={files() ?? []}
          />
        </Show>
        <Show when={store.tab === "transformations"}>
          <RecordsTab
            title={language.t("cowork.pipeline.transformations")}
            records={transformations() ?? []}
            kind="transformation"
          />
        </Show>
        <Show when={store.tab === "output"}>
          <OutputTab transformations={transformations() ?? []} artifacts={artifacts() ?? []} />
        </Show>
        <Show when={store.tab === "report"}>
          <ReportTab
            artifacts={artifacts() ?? []}
            loading={artifacts.loading}
            onRefresh={() => {
              setStore("reportVisited", true)
              setStore("reportRefresh", store.reportRefresh + 1)
            }}
            onDelete={async (artifact) => {
              await deleteArtifact(artifact.project_id, artifact.id)
              await artifactActions.refetch()
            }}
          />
        </Show>
      </Show>
    </div>
  )
}

type CoworkMode = "workflow" | "accounting" | "reports"
type PipelineTab = "files" | "extractions" | "transformations" | "output" | "report"
type AccountingTab = "overview" | "documents" | "extractions" | "payments" | "review" | "ledger" | "tax" | "emta"
type LedgerViewTab = "journal" | "trialBalance"
type TaxViewTab = "returns" | "summary" | "reconciliation" | "report"
type PaymentViewTab = "matched" | "unpaid" | "unmatched"
type TaxArtifactPreview = { artifact: CoworkArtifact; text: string; rows: string[][]; json?: unknown }
type DocumentSortKey =
  | "file"
  | "type"
  | "counterparty"
  | "number"
  | "date"
  | "currency"
  | "gross"
  | "status"
  | "confidence"
  | "postings"
type SortDirection = "asc" | "desc"
type DocumentFilterKey = "type" | "counterparty" | "currency" | "status" | "postings"

function AccountingLayout(props: {
  clientName: string
  periodName: string
  sessionMetadata: Record<string, unknown>
  summary: CoworkAccountingSummary | undefined
  readiness: CoworkAccountingReadiness | undefined
  summaryLoading: boolean
  readinessLoading: boolean
  files: CoworkFile[]
  documents: CoworkAccountingDocument[]
  extractions: CoworkExtraction[]
  reviews: CoworkAccountingReview[]
  payments: CoworkPaymentTransaction[]
  paymentMatches: CoworkPaymentMatch[]
  exportPackages: CoworkAccountingExportPackage[]
  ledger: CoworkLedgerView | undefined
  ledgerLoading: boolean
  ledgerView: LedgerViewTab
  taxView: TaxViewTab
  paymentView: PaymentViewTab
  taxReports: CoworkAccountingTaxReport[]
  taxArtifacts: CoworkArtifact[]
  taxReportType: string
  documentsLoading: boolean
  reviewsLoading: boolean
  paymentsLoading: boolean
  exportPackagesLoading: boolean
  taxReportsLoading: boolean
  uploadBusy: boolean
  hasWorkflow: boolean
  selectedExtractionID: string
  onUpload: (file: File) => Promise<void>
  onAddToAccounting: (file: CoworkFile) => Promise<void>
  onProcess: (document: CoworkAccountingDocument) => Promise<void>
  onSuggestPostings: (document: CoworkAccountingDocument) => Promise<void>
  onApproveReview: (review: CoworkAccountingReview) => Promise<void>
  onRejectReview: (review: CoworkAccountingReview) => Promise<void>
  paymentForm: { amount: string; currency: string; counterparty: string; reference: string; description: string }
  onPaymentFormChange: (
    field: "paymentAmount" | "paymentCurrency" | "paymentCounterparty" | "paymentReference" | "paymentDescription",
    value: string,
  ) => void
  onImportPayment: () => Promise<void>
  onMatchPayments: () => Promise<void>
  onApproveMatch: (match: CoworkPaymentMatch) => Promise<void>
  onRejectMatch: (match: CoworkPaymentMatch) => Promise<void>
  onCreateExportPackage: () => Promise<void>
  onMarkExportSubmitted: (pkg: CoworkAccountingExportPackage) => Promise<void>
  onDownloadExportFile: (file: CoworkAccountingExportFile) => Promise<void>
  onDeleteExportFile: (file: CoworkAccountingExportFile) => Promise<void>
  onLedgerViewChange: (view: LedgerViewTab) => void
  onTaxViewChange: (view: TaxViewTab) => void
  onPaymentViewChange: (view: PaymentViewTab) => void
  onCreateTaxReport: () => Promise<void>
  onTaxReportTypeChange: (value: string) => void
  onDeleteTaxReport: (report: CoworkAccountingTaxReport) => Promise<void>
  onDownloadTaxArtifact: (artifact: CoworkArtifact) => Promise<void>
  onDeleteTaxArtifact: (artifact: CoworkArtifact) => Promise<void>
  onSelectReview: (review: CoworkAccountingReview) => void
  selectedReviewID: string
  filter: string
  onFilterChange: (value: string) => void
  activeTab: AccountingTab
  onTabChange: (tab: AccountingTab) => void
  onSelectExtraction: (id: string) => void
}) {
  const language = useLanguage()
  const [tableStore, setTableStore] = createStore({
    documentSortKey: "date" as DocumentSortKey,
    documentSortDirection: "desc" as SortDirection,
    documentFilters: {} as Record<DocumentFilterKey, string[]>,
    openDocumentFilter: "" as DocumentFilterKey | "",
  })
  const tabs = () => accountingTabs(props.summary?.tabs)
  const filteredDocuments = createMemo(() =>
    filterDocumentsByColumns(props.documents, tableStore.documentFilters, language.t),
  )
  const sortedDocuments = createMemo(() =>
    sortAccountingDocuments(
      filteredDocuments(),
      tableStore.documentSortKey,
      tableStore.documentSortDirection,
      language.t,
    ),
  )
  const documentFilterOptions = createMemo(() => documentColumnFilterOptions(props.documents, language.t))
  const filteredReviews = createMemo(() => props.reviews)
  const filteredPayments = createMemo(() => props.payments)
  const filteredPaymentMatches = createMemo(() => props.paymentMatches)
  const unmatchedPayments = createMemo(() => {
    const matchedPaymentIDs = new Set(filteredPaymentMatches().map((match) => match.payment_id))
    return filteredPayments().filter((payment) => !matchedPaymentIDs.has(payment.id))
  })
  const unpaidDocuments = createMemo(() => {
    const matchedDocumentIDs = new Set(filteredPaymentMatches().map((match) => match.document_id))
    return props.documents.filter((document) => isPayableDocument(document) && !matchedDocumentIDs.has(document.id))
  })
  const setDocumentSort = (key: DocumentSortKey) => {
    if (tableStore.documentSortKey === key) {
      setTableStore("documentSortDirection", tableStore.documentSortDirection === "asc" ? "desc" : "asc")
      return
    }
    setTableStore("documentSortKey", key)
    setTableStore("documentSortDirection", key === "date" || key === "gross" || key === "confidence" ? "desc" : "asc")
  }

  const toggleDocumentColumnFilter = (key: DocumentFilterKey, value: string) => {
    const current = tableStore.documentFilters[key] ?? []
    const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    setTableStore("documentFilters", key, next)
  }

  createEffect(() => {
    if (!tableStore.openDocumentFilter) return
    const close = (event: MouseEvent) => {
      const target = event.target as Element | null
      if (target?.closest("[data-document-filter-root]")) return
      setTableStore("openDocumentFilter", "")
    }
    window.document.addEventListener("pointerdown", close)
    onCleanup(() => window.document.removeEventListener("pointerdown", close))
  })

  return (
    <div class="min-h-0 flex-1 overflow-auto p-5">
      <div class="mb-5">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 class="text-20-semibold text-v2-text-text-base">{language.t("cowork.accounting.title")}</h2>
            <p class="mt-1 text-13-regular text-v2-text-text-muted">{language.t("cowork.accounting.description")}</p>
          </div>
        </div>
      </div>
      <Show
        when={!props.summaryLoading}
        fallback={<p class="text-13-regular text-v2-text-text-muted">{language.t("common.loading")}</p>}
      >
        <div class="mb-5 flex gap-2 overflow-x-auto border-b border-v2-border-border-base pb-3">
          <For each={tabs()}>
            {(tab) => (
              <button
                type="button"
                class="flex shrink-0 items-center gap-2 rounded-md border border-transparent px-3 py-1.5 text-13-medium text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                classList={{
                  "border-v2-border-border-base bg-v2-background-bg-layer-03 text-v2-text-text-base":
                    props.activeTab === tab,
                }}
                onClick={() => props.onTabChange(tab)}
              >
                <span>{language.t(`cowork.accounting.tab.${tab}`)}</span>
                <Show when={tab !== "overview"}>
                  <span class="rounded-full bg-v2-background-bg-layer-01 px-2 py-0.5 text-11-medium text-v2-text-text-muted">
                    {props.summary?.summary[accountingSummaryKey(tab)] ?? 0}
                  </span>
                </Show>
              </button>
            )}
          </For>
        </div>
      </Show>
      <Show when={props.activeTab === "overview"}>
        <AccountingClientSummary
          clientName={props.clientName}
          periodName={props.periodName}
          metadata={props.sessionMetadata}
        />
        <AccountingWorkflowSteps />
        <AccountingSummaryCards
          documents={props.documents}
          reviews={props.reviews}
          payments={props.payments}
          paymentMatches={props.paymentMatches}
        />
        <AccountingOverviewPanel
          readiness={props.readiness}
          loading={props.readinessLoading}
          onNavigate={(tab) => props.onTabChange(tab)}
        />
      </Show>
      <Show when={props.activeTab === "documents"}>
        <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01">
          <div class="flex flex-wrap items-center justify-between gap-3 border-b border-v2-border-border-base px-4 py-3">
            <div>
              <h3 class="text-15-semibold text-v2-text-text-base">{language.t("cowork.accounting.documents.title")}</h3>
              <p class="mt-1 text-12-regular text-v2-text-text-muted">
                {language.t("cowork.accounting.documents.description")}
              </p>
            </div>
          </div>
          <Show
            when={props.hasWorkflow}
            fallback={
              <p class="p-4 text-13-regular text-v2-text-text-muted">
                {language.t("cowork.accounting.documents.noWorkflow")}
              </p>
            }
          >
            <Show
              when={!props.documentsLoading}
              fallback={<p class="p-4 text-13-regular text-v2-text-text-muted">{language.t("common.loading")}</p>}
            >
              <DocumentColumnFilters
                options={documentFilterOptions()}
                selected={tableStore.documentFilters}
                openKey={tableStore.openDocumentFilter}
                onOpenChange={(key) => setTableStore("openDocumentFilter", key)}
                onToggle={toggleDocumentColumnFilter}
              />
              <Show
                when={sortedDocuments().length > 0}
                fallback={
                  <p class="p-4 text-13-regular text-v2-text-text-muted">
                    {language.t("cowork.accounting.documents.empty")}
                  </p>
                }
              >
                <AccountingTableToolbar
                  title={language.t("cowork.accounting.documents.visibleRows", { count: sortedDocuments().length })}
                  rows={recordsToTableRows(
                    Object.keys(documentRowsForCsv(sortedDocuments(), language.t)[0] ?? {}),
                    documentRowsForCsv(sortedDocuments(), language.t),
                  )}
                  onDownload={() =>
                    downloadAccountingCsv("accounting-documents.csv", documentRowsForCsv(sortedDocuments(), language.t))
                  }
                />
                <div class="max-h-[70vh] overflow-auto">
                  <table class="w-full min-w-[960px] border-collapse text-left text-12-regular">
                    <thead class="sticky top-0 z-10 bg-v2-background-bg-layer-01 text-v2-text-text-muted">
                      <tr class="border-b border-v2-border-border-base">
                        <SortableDocumentHeader
                          label={language.t("cowork.accounting.documents.column.file")}
                          sortKey="file"
                          activeKey={tableStore.documentSortKey}
                          direction={tableStore.documentSortDirection}
                          onSort={setDocumentSort}
                        />
                        <SortableDocumentHeader
                          label={language.t("cowork.accounting.documents.column.type")}
                          sortKey="type"
                          activeKey={tableStore.documentSortKey}
                          direction={tableStore.documentSortDirection}
                          onSort={setDocumentSort}
                        />
                        <SortableDocumentHeader
                          label={language.t("cowork.accounting.documents.column.counterparty")}
                          sortKey="counterparty"
                          activeKey={tableStore.documentSortKey}
                          direction={tableStore.documentSortDirection}
                          onSort={setDocumentSort}
                        />
                        <SortableDocumentHeader
                          label={language.t("cowork.accounting.documents.column.number")}
                          sortKey="number"
                          activeKey={tableStore.documentSortKey}
                          direction={tableStore.documentSortDirection}
                          onSort={setDocumentSort}
                        />
                        <SortableDocumentHeader
                          label={language.t("cowork.accounting.documents.column.date")}
                          sortKey="date"
                          activeKey={tableStore.documentSortKey}
                          direction={tableStore.documentSortDirection}
                          onSort={setDocumentSort}
                        />
                        <SortableDocumentHeader
                          label={language.t("cowork.accounting.documents.column.currency")}
                          sortKey="currency"
                          activeKey={tableStore.documentSortKey}
                          direction={tableStore.documentSortDirection}
                          onSort={setDocumentSort}
                        />
                        <SortableDocumentHeader
                          label={language.t("cowork.accounting.documents.column.gross")}
                          sortKey="gross"
                          activeKey={tableStore.documentSortKey}
                          direction={tableStore.documentSortDirection}
                          onSort={setDocumentSort}
                        />
                        <SortableDocumentHeader
                          label={language.t("cowork.accounting.documents.column.status")}
                          sortKey="status"
                          activeKey={tableStore.documentSortKey}
                          direction={tableStore.documentSortDirection}
                          onSort={setDocumentSort}
                        />
                        <SortableDocumentHeader
                          label={language.t("cowork.accounting.documents.column.confidence")}
                          sortKey="confidence"
                          activeKey={tableStore.documentSortKey}
                          direction={tableStore.documentSortDirection}
                          onSort={setDocumentSort}
                        />
                        <SortableDocumentHeader
                          label={language.t("cowork.accounting.documents.column.postings")}
                          sortKey="postings"
                          activeKey={tableStore.documentSortKey}
                          direction={tableStore.documentSortDirection}
                          onSort={setDocumentSort}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      <For each={sortedDocuments()}>
                        {(document) => (
                          <tr class="border-b border-v2-border-border-base last:border-b-0">
                            <CopyableDocumentCell
                              value={documentSourceLabel(document)}
                              className="max-w-[220px] text-v2-text-text-base"
                            />
                            <CopyableDocumentCell value={documentTypeLabel(document.document_type, language.t)} />
                            <CopyableDocumentCell
                              value={document.counterparty_name || language.t("cowork.accounting.documents.unknown")}
                            />
                            <CopyableDocumentCell
                              value={document.document_number || language.t("cowork.accounting.documents.unknown")}
                            />
                            <CopyableDocumentCell
                              value={
                                document.document_date
                                  ? formatRecordDate(document.document_date)
                                  : language.t("cowork.accounting.documents.unknown")
                              }
                            />
                            <CopyableDocumentCell
                              value={document.currency || language.t("cowork.accounting.documents.unknown")}
                            />
                            <CopyableDocumentCell value={formatAccountingAmount(document)} />
                            <CopyableDocumentCell
                              value={accountingStatusLabel(document.processing_status, language.t)}
                            />
                            <CopyableDocumentCell value={documentConfidence(document)} />
                            <CopyableDocumentCell value={postingStatusLabel(document, language.t)} />
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </Show>
            </Show>
          </Show>
        </section>
      </Show>
      <Show when={props.activeTab === "extractions"}>
        <AccountingExtractionsPanel
          extractions={props.extractions.filter(isAccountingExtraction)}
          files={props.files}
          selectedID={props.selectedExtractionID}
          onSelect={props.onSelectExtraction}
        />
      </Show>
      <Show when={props.activeTab === "review"}>
        <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01">
          <div class="border-b border-v2-border-border-base px-4 py-3">
            <h3 class="text-15-semibold text-v2-text-text-base">{language.t("cowork.accounting.review.title")}</h3>
            <p class="mt-1 text-12-regular text-v2-text-text-muted">
              {language.t("cowork.accounting.review.description")}
            </p>
          </div>
          <Show
            when={!props.reviewsLoading}
            fallback={<p class="p-4 text-13-regular text-v2-text-text-muted">{language.t("common.loading")}</p>}
          >
            <Show
              when={filteredReviews()[0]}
              fallback={
                <p class="p-4 text-13-regular text-v2-text-text-muted">
                  {language.t("cowork.accounting.review.empty")}
                </p>
              }
            >
              {(review) => (
                <div class="p-4">
                  <div class="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <For each={filteredReviews()}>
                      {(item) => (
                        <AccountingReviewMiniCard
                          review={item}
                          selected={(props.selectedReviewID || review().id) === item.id}
                          onSelect={() => props.onSelectReview(item)}
                        />
                      )}
                    </For>
                  </div>
                  <AccountingReviewReport
                    clientName={props.clientName}
                    clientMetadata={accountingClientMetadata(props.sessionMetadata)}
                    review={selectedReview(filteredReviews(), props.selectedReviewID) ?? review()}
                    paymentMatch={props.paymentMatches.find(
                      (match) =>
                        match.document_id ===
                        (selectedReview(filteredReviews(), props.selectedReviewID) ?? review()).document_id,
                    )}
                    onSelect={() =>
                      props.onSelectReview(selectedReview(filteredReviews(), props.selectedReviewID) ?? review())
                    }
                    onApprove={() =>
                      props.onApproveReview(selectedReview(filteredReviews(), props.selectedReviewID) ?? review())
                    }
                    onReject={() =>
                      props.onRejectReview(selectedReview(filteredReviews(), props.selectedReviewID) ?? review())
                    }
                  />
                </div>
              )}
            </Show>
          </Show>
        </section>
      </Show>
      <Show when={props.activeTab === "payments"}>
        <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01">
          <div class="flex flex-wrap items-center justify-between gap-3 border-b border-v2-border-border-base px-4 py-3">
            <div>
              <h3 class="text-15-semibold text-v2-text-text-base">{language.t("cowork.accounting.payments.title")}</h3>
              <p class="mt-1 text-12-regular text-v2-text-text-muted">
                {language.t("cowork.accounting.payments.description")}
              </p>
            </div>
          </div>
          <Show
            when={!props.paymentsLoading}
            fallback={<p class="p-4 text-13-regular text-v2-text-text-muted">{language.t("common.loading")}</p>}
          >
            <div class="space-y-4 p-4">
              <PaymentViewTabs
                active={props.paymentView}
                matched={filteredPaymentMatches().length}
                unpaid={unpaidDocuments().length}
                unmatched={unmatchedPayments().length}
                onChange={props.onPaymentViewChange}
              />
              <div>
                <Show when={props.paymentView === "matched"}>
                  <h4 class="mb-2 text-13-medium text-v2-text-text-base">
                    {language.t("cowork.accounting.payments.matches")}
                  </h4>
                  <For
                    each={filteredPaymentMatches()}
                    fallback={
                      <p class="text-13-regular text-v2-text-text-muted">
                        {language.t("cowork.accounting.payments.noMatches")}
                      </p>
                    }
                  >
                    {(match) => <PaymentMatchCard match={match} />}
                  </For>
                </Show>
                <Show when={props.paymentView === "unpaid"}>
                  <h4 class="mb-2 text-13-medium text-v2-text-text-base">
                    {language.t("cowork.accounting.payments.unpaidInvoices")}
                  </h4>
                  <div class="grid gap-2">
                    <For
                      each={unpaidDocuments()}
                      fallback={
                        <p class="text-13-regular text-v2-text-text-muted">
                          {language.t("cowork.accounting.payments.noUnpaidInvoices")}
                        </p>
                      }
                    >
                      {(document) => <UnpaidInvoiceCard document={document} />}
                    </For>
                  </div>
                </Show>
                <Show when={props.paymentView === "unmatched"}>
                  <h4 class="mb-2 text-13-medium text-v2-text-text-base">
                    {language.t("cowork.accounting.payments.unmatched")}
                  </h4>
                  <div class="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    <For
                      each={unmatchedPayments()}
                      fallback={
                        <p class="text-13-regular text-v2-text-text-muted">
                          {language.t("cowork.accounting.payments.noUnmatchedPayments")}
                        </p>
                      }
                    >
                      {(payment) => <PaymentTransactionCard payment={payment} />}
                    </For>
                  </div>
                </Show>
              </div>
            </div>
          </Show>
        </section>
      </Show>
      <Show when={props.activeTab === "ledger"}>
        <AccountingLedgerPanel
          ledger={props.ledger}
          loading={props.ledgerLoading}
          view={props.ledgerView}
          onViewChange={props.onLedgerViewChange}
          documents={props.documents}
          reviews={props.reviews}
          taxReports={props.taxReports}
          paymentMatches={props.paymentMatches}
          exportPackages={props.exportPackages}
          exportPackagesLoading={props.exportPackagesLoading}
          hasWorkflow={props.hasWorkflow}
          onCreateExportPackage={props.onCreateExportPackage}
          onMarkExportSubmitted={props.onMarkExportSubmitted}
          onDownloadExportFile={props.onDownloadExportFile}
          onDeleteExportFile={props.onDeleteExportFile}
        />
      </Show>
      <Show when={props.activeTab === "tax"}>
        <AccountingTaxReportPanel
          reports={props.taxReports}
          artifacts={props.taxArtifacts}
          view={props.taxView}
          loading={props.taxReportsLoading}
          onViewChange={props.onTaxViewChange}
          onDeleteReport={props.onDeleteTaxReport}
          onDownloadArtifact={props.onDownloadTaxArtifact}
          onDeleteArtifact={props.onDeleteTaxArtifact}
        />
      </Show>
      <Show when={props.activeTab === "emta"}>
        <AccountingEmptyTab
          title={language.t("cowork.accounting.tab.emta")}
          description={language.t("cowork.accounting.emta.empty")}
        />
      </Show>
    </div>
  )
}

function accountingTabs(tabs: string[] | undefined): AccountingTab[] {
  const fallback: AccountingTab[] = [
    "overview",
    "documents",
    "extractions",
    "payments",
    "review",
    "ledger",
    "tax",
    "emta",
  ]
  if (!tabs?.length) return fallback
  const allowed = new Set(fallback)
  const values = tabs.filter((tab): tab is AccountingTab => allowed.has(tab as AccountingTab))
  const merged: AccountingTab[] = ["overview", ...values.filter((tab) => tab !== "overview")]
  return merged.length ? merged : fallback
}

function AccountingEmptyTab(props: { title: string; description: string }) {
  return (
    <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-5">
      <h3 class="text-15-semibold text-v2-text-text-base">{props.title}</h3>
      <p class="mt-2 text-13-regular text-v2-text-text-muted">{props.description}</p>
    </section>
  )
}

function AccountingTableToolbar(props: { title: string; onDownload: () => void; rows?: string[][] }) {
  const language = useLanguage()
  return (
    <div class="flex items-center justify-between gap-3 border-b border-v2-border-border-base px-3 py-2">
      <span class="text-12-medium text-v2-text-text-muted">{props.title}</span>
      <div class="flex items-center gap-1">
        <Show when={props.rows}>{(rows) => <TableExpandButton title={props.title} rows={rows()} />}</Show>
        <button
          type="button"
          class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
          title={language.t("cowork.accounting.table.downloadCsv")}
          aria-label={language.t("cowork.accounting.table.downloadCsv")}
          onClick={props.onDownload}
        >
          <IconV2 name="download" size="small" />
        </button>
      </div>
    </div>
  )
}

function AccountingExtractionsPanel(props: {
  extractions: CoworkExtraction[]
  files: CoworkFile[]
  selectedID: string
  onSelect: (id: string) => void
}) {
  const language = useLanguage()
  const [store, setStore] = createStore({ filter: "" })
  const filteredExtractions = createMemo(() => {
    const query = store.filter.trim().toLowerCase()
    if (!query) return props.extractions
    return props.extractions.filter((record) => {
      const title = extractionDocumentTitle(record, props.files).toLowerCase()
      const schema = (record.schema_name || "").toLowerCase()
      const status = (record.status || "").toLowerCase()
      return title.includes(query) || schema.includes(query) || status.includes(query)
    })
  })
  const selected = createMemo(
    () => filteredExtractions().find((record) => record.id === props.selectedID) ?? filteredExtractions()[0],
  )
  return (
    <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01">
      <div class="border-b border-v2-border-border-base px-4 py-3">
        <h3 class="text-15-semibold text-v2-text-text-base">{language.t("cowork.accounting.extractions.title")}</h3>
        <p class="mt-1 text-12-regular text-v2-text-text-muted">
          {language.t("cowork.accounting.extractions.description")}
        </p>
      </div>
      <Show
        when={props.extractions.length > 0}
        fallback={
          <p class="p-4 text-13-regular text-v2-text-text-muted">{language.t("cowork.accounting.extractions.empty")}</p>
        }
      >
        <div class="grid h-[70vh] min-h-[520px] grid-cols-[320px_minmax(0,1fr)] overflow-hidden">
          <aside class="flex min-h-0 flex-col border-r border-v2-border-border-base p-3">
            <input
              class="mb-3 h-9 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-13-regular text-v2-text-text-base outline-none"
              placeholder={language.t("cowork.accounting.extractions.filter")}
              value={store.filter}
              onInput={(event) => setStore("filter", event.currentTarget.value)}
            />
            <div class="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain pr-1 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
              <For
                each={filteredExtractions()}
                fallback={
                  <p class="px-2 py-1 text-12-regular text-v2-text-text-muted">
                    {language.t("cowork.accounting.extractions.empty")}
                  </p>
                }
              >
                {(record) => (
                  <button
                    type="button"
                    class="block w-full rounded-lg border border-v2-border-border-base px-3 py-2 text-left hover:bg-v2-background-bg-layer-02"
                    classList={{ "bg-v2-background-bg-layer-03": selected()?.id === record.id }}
                    onClick={() => props.onSelect(record.id)}
                  >
                    <p class="truncate text-13-medium text-v2-text-text-base">
                      {extractionDocumentTitle(record, props.files)}
                    </p>
                  </button>
                )}
              </For>
            </div>
          </aside>
          <div class="min-h-0 min-w-0 overflow-y-auto p-4 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
            <Show when={selected()}>
              {(record) => (
                <div>
                  <div class="mb-4">
                    <h4 class="text-14-semibold text-v2-text-text-base">
                      {extractionDocumentTitle(record(), props.files)}
                    </h4>
                    <p class="mt-1 text-12-regular text-v2-text-text-muted">{record().status}</p>
                  </div>
                  <AccountingExtractionData record={record()} files={props.files} />
                </div>
              )}
            </Show>
          </div>
        </div>
      </Show>
    </section>
  )
}

export function AccountingExtractionData(props: { record: CoworkExtraction; files: CoworkFile[] }) {
  const language = useLanguage()
  const keyValues = createMemo(() => accountingExtractionKeyValues(extractionTables(props.record)))
  const tables = createMemo(() => accountingExtractionDisplayTables(extractionTables(props.record)))
  const normalized = createMemo(() => normalizedExtractionFromRecord(props.record, keyValues(), tables(), language.t))
  const fallbackTables = createMemo(() => (normalized().tables.length ? [] : tables()))
  return (
    <>
      <AccountingNormalizedExtractionView record={props.record} files={props.files} extraction={normalized()} />
      <AccountingExtractionKeyValues rows={keyValues()} />
      <For
        each={fallbackTables()}
        fallback={
          <Show when={!normalized().tables.length}>
            <p class="text-13-regular text-v2-text-text-muted">
              {language.t("cowork.accounting.extractions.noTables")}
            </p>
          </Show>
        }
      >
        {(table) => <AccountingExtractionTable table={table} />}
      </For>
    </>
  )
}

function AccountingExtractionTable(props: { table: ExtractedTable }) {
  const language = useLanguage()
  return (
    <section class="mb-4 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3 last:mb-0">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h5 class="text-13-medium text-v2-text-text-base">
          {accountingExtractionTableName(props.table.name, language.t)}
        </h5>
        <div class="flex items-center gap-1">
          <TableExpandButton
            title={accountingExtractionTableName(props.table.name, language.t)}
            rows={recordsToTableRows(props.table.headers, props.table.rows)}
          />
          <button
            type="button"
            class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
            title={language.t("cowork.accounting.table.downloadCsv")}
            aria-label={language.t("cowork.accounting.table.downloadCsv")}
            onClick={() =>
              downloadAccountingCsv(`${props.table.name || "accounting-extraction"}.csv`, props.table.rows)
            }
          >
            <IconV2 name="download" size="small" />
          </button>
        </div>
      </div>
      <div class="max-h-[460px] overflow-auto">
        <table class="w-full min-w-[720px] border-collapse text-left text-12-regular">
          <thead class="sticky top-0 bg-v2-background-bg-base text-v2-text-text-muted">
            <tr class="border-b border-v2-border-border-base">
              <For each={props.table.headers}>{(header) => <th class="px-2 py-1 font-medium">{header}</th>}</For>
            </tr>
          </thead>
          <tbody>
            <For each={props.table.rows}>
              {(row) => (
                <tr class="border-b border-v2-border-border-base last:border-b-0">
                  <For each={props.table.headers}>
                    {(header) => <td class="px-2 py-1 text-v2-text-text-base">{row[header] || ""}</td>}
                  </For>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </section>
  )
}

function AccountingNormalizedExtractionView(props: {
  record: CoworkExtraction
  files: CoworkFile[]
  extraction: NormalizedExtraction | undefined
}) {
  const language = useLanguage()
  return (
    <Show when={props.extraction}>
      {(extraction) => (
        <section class="mb-4 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
          <div class="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h5 class="text-13-medium text-v2-text-text-base">
                {language.t("cowork.accounting.extractions.accountingFacts")}
              </h5>
              <p class="mt-0.5 text-11-regular text-v2-text-text-muted">
                {documentTypeLabel(extraction().documentType, language.t)}
              </p>
            </div>
            <ExtractionQualityBadge quality={extraction().quality} />
          </div>
          <ExtractionWarningList warnings={extraction().quality.warnings} />
          <div class="space-y-4">
            <AccountingFactGrid
              title={language.t("cowork.accounting.extractions.section.document")}
              rows={extraction().documentRows}
            />
            <Show when={isInvoiceLikeExtraction(extraction().documentType)}>
              <div class="grid gap-4 xl:grid-cols-2">
                <AccountingFactGrid
                  title={language.t("cowork.accounting.extractions.party.seller")}
                  rows={extraction().sellerRows}
                />
                <AccountingFactGrid
                  title={language.t("cowork.accounting.extractions.party.buyer")}
                  rows={extraction().buyerRows}
                />
              </div>
              <AccountingFactGrid
                title={language.t("cowork.accounting.extractions.section.amounts")}
                rows={extraction().amountRows}
                emphasizedKeys={["amount_due"]}
              />
              <AccountingFactGrid
                title={language.t("cowork.accounting.extractions.section.payment")}
                rows={extraction().paymentRows}
              />
            </Show>
            <Show when={extraction().tables.length > 0}>
              <For each={extraction().tables}>{(table) => <AccountingExtractionTable table={table} />}</For>
            </Show>
          </div>
        </section>
      )}
    </Show>
  )
}

function AccountingFactGrid(props: { title: string; rows: ExtractionFact[]; emphasizedKeys?: string[] }) {
  return (
    <Show when={props.rows.length > 0}>
      <div>
        <h6 class="mb-2 border-b border-v2-border-border-base pb-1 text-12-medium text-v2-text-text-muted">
          {props.title}
        </h6>
        <div class="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          <For each={props.rows}>
            {(row) => (
              <div
                class="rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 p-2"
                classList={{
                  "border-v2-border-border-muted bg-v2-background-bg-layer-03": props.emphasizedKeys?.includes(row.key),
                }}
              >
                <p class="text-11-medium uppercase tracking-wide text-v2-text-text-muted">{row.label}</p>
                <p class="mt-1 whitespace-pre-wrap text-13-medium text-v2-text-text-base">{row.value}</p>
                <Show when={row.source}>
                  {(source) => <p class="mt-1 text-11-regular text-v2-text-text-muted">{source()}</p>}
                </Show>
              </div>
            )}
          </For>
        </div>
      </div>
    </Show>
  )
}

function ExtractionQualityBadge(props: { quality: ExtractionQuality }) {
  const language = useLanguage()
  return (
    <Show when={props.quality.warnings.length > 0 || props.quality.overallConfidence !== undefined}>
      <div
        class="rounded-md border border-v2-border-border-base px-2 py-1 text-11-regular text-v2-text-text-muted"
        classList={{ "border-amber-500/60 text-amber-500": props.quality.warnings.length > 0 }}
      >
        <Show
          when={props.quality.warnings.length > 0}
          fallback={
            props.quality.overallConfidence !== undefined
              ? language.t("cowork.accounting.extractions.confidence", {
                  confidence: Math.round(props.quality.overallConfidence),
                })
              : ""
          }
        >
          {language.t("cowork.accounting.extractions.warnings", { count: props.quality.warnings.length })}
        </Show>
      </div>
    </Show>
  )
}

function ExtractionWarningList(props: { warnings: string[] }) {
  return (
    <Show when={props.warnings.length > 0}>
      <div class="mb-3 rounded-md border border-amber-500/50 bg-amber-500/10 p-2 text-12-regular text-amber-500">
        <For each={props.warnings}>{(warning) => <p>{warning}</p>}</For>
      </div>
    </Show>
  )
}

function AccountingExtractionKeyValues(props: { rows: Array<Record<string, string>> }) {
  const language = useLanguage()
  const sections = createMemo(() => accountingExtractionSections(props.rows, language.t))
  return (
    <Show when={props.rows.length > 0}>
      <section class="mb-4 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
        <div class="mb-3 flex items-center justify-between gap-3">
          <h5 class="text-13-medium text-v2-text-text-base">
            {language.t("cowork.accounting.extractions.sourceFields")}
          </h5>
          <div class="flex items-center gap-1">
            <TableExpandButton
              title={language.t("cowork.accounting.extractions.sourceFields")}
              rows={recordsToTableRowsAuto(props.rows)}
            />
            <button
              type="button"
              class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
              title={language.t("cowork.accounting.table.downloadCsv")}
              aria-label={language.t("cowork.accounting.table.downloadCsv")}
              onClick={() => downloadAccountingCsv("extracted-fields.csv", props.rows)}
            >
              <IconV2 name="download" size="small" />
            </button>
          </div>
        </div>
        <div class="space-y-4">
          <For each={sections()}>
            {(section) => (
              <div>
                <h6 class="mb-2 border-b border-v2-border-border-base pb-1 text-12-medium text-v2-text-text-muted">
                  {section.title}
                </h6>
                <Show
                  when={section.key === "other"}
                  fallback={
                    <div class="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                      <For each={section.rows}>{(row) => <AccountingExtractionFieldCard row={row} />}</For>
                    </div>
                  }
                >
                  <AccountingExtractionFieldTable rows={section.rows} />
                </Show>
              </div>
            )}
          </For>
        </div>
      </section>
    </Show>
  )
}

function AccountingExtractionFieldTable(props: { rows: Array<Record<string, string>> }) {
  const language = useLanguage()
  const headers = createMemo(() => [
    language.t("cowork.accounting.extractions.column.field"),
    language.t("cowork.accounting.extractions.column.value"),
    language.t("cowork.accounting.extractions.column.source"),
  ])
  const rows = createMemo(() =>
    props.rows.map((row) => [
      extractionFieldLabel(row, language.t),
      extractionFieldValue(row),
      extractionFieldSource(row),
    ]),
  )
  return (
    <div class="rounded-md border border-v2-border-border-base">
      <div class="flex justify-end border-b border-v2-border-border-base p-1">
        <TableExpandButton
          title={language.t("cowork.accounting.extractions.section.other")}
          rows={[headers(), ...rows()]}
        />
      </div>
      <div class="max-h-[420px] overflow-auto">
        <table class="w-full min-w-[720px] border-collapse text-left text-12-regular">
          <thead class="sticky top-0 bg-v2-background-bg-layer-01 text-v2-text-text-muted">
            <tr class="border-b border-v2-border-border-base">
              <th class="px-3 py-2 font-medium">{language.t("cowork.accounting.extractions.column.field")}</th>
              <th class="px-3 py-2 font-medium">{language.t("cowork.accounting.extractions.column.value")}</th>
              <th class="px-3 py-2 font-medium">{language.t("cowork.accounting.extractions.column.source")}</th>
            </tr>
          </thead>
          <tbody>
            <For each={props.rows}>
              {(row) => (
                <tr class="border-b border-v2-border-border-base last:border-b-0">
                  <td class="w-56 px-3 py-2 align-top text-v2-text-text-muted">
                    {extractionFieldLabel(row, language.t)}
                  </td>
                  <td class="px-3 py-2 align-top text-v2-text-text-base">{extractionFieldValue(row)}</td>
                  <td class="w-28 px-3 py-2 align-top text-v2-text-text-muted">{extractionFieldSource(row)}</td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </div>
  )
}

function AccountingExtractionFieldCard(props: { row: Record<string, string> }) {
  const language = useLanguage()
  return (
    <div class="rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 p-2">
      <p class="min-w-0 text-11-medium uppercase tracking-wide text-v2-text-text-muted">
        {extractionFieldLabel(props.row, language.t)}
      </p>
      <p class="mt-1 whitespace-pre-wrap text-13-medium text-v2-text-text-base">{extractionFieldValue(props.row)}</p>
      <Show when={extractionFieldSource(props.row)}>
        {(source) => <p class="mt-1 text-11-regular text-v2-text-text-muted">{source()}</p>}
      </Show>
    </div>
  )
}

function isAccountingExtraction(record: CoworkExtraction) {
  return (
    record.schema_name === "accounting_document_analysis" ||
    String(record.result_json?.source ?? "").includes("accounting")
  )
}

function extractionDocumentTitle(record: CoworkExtraction, files: CoworkFile[]) {
  const file = files.find((item) => item.id === record.file_id)
  return (
    file?.filename ||
    metadataString(record.result_json.document as Record<string, unknown> | undefined, "document_number") ||
    record.schema_name ||
    record.id
  )
}

type ExtractionFact = { key: string; label: string; value: string; source?: string; confidence?: number }
type ExtractionQuality = { overallConfidence?: number; warnings: string[]; conflicts: string[] }
type NormalizedExtraction = {
  documentType: string
  documentRows: ExtractionFact[]
  sellerRows: ExtractionFact[]
  buyerRows: ExtractionFact[]
  amountRows: ExtractionFact[]
  paymentRows: ExtractionFact[]
  tables: ExtractedTable[]
  quality: ExtractionQuality
}

function normalizedExtractionFromRecord(
  record: CoworkExtraction,
  keyValues: Array<Record<string, string>>,
  tables: ExtractedTable[],
  translate: (key: string, params?: Record<string, string | number>) => string,
): NormalizedExtraction {
  const data = record.result_json
  const document = isRecord(data["document"]) ? data["document"] : {}
  const metadata = isRecord(data["document_metadata"]) ? data["document_metadata"] : {}
  const analysis = agentAnalysis(metadata)
  const normalized = firstRecord(data["normalized"], analysis["normalized"], metadata["normalized"]) ?? {}
  const normalizedData = firstRecord(data["data"], normalized["data"], analysis["data"]) ?? {}
  const identity = firstRecord(data["identity"], normalized["identity"], analysis["identity"]) ?? {}
  const documentType = normalizedDocumentType(
    metadataString(identity, "document_type") ||
      metadataString(document, "document_type") ||
      metadataString(normalized, "document_type") ||
      (isRecord(data["invoice"]) ? "invoice" : isRecord(data["receipt"]) ? "receipt" : "") ||
      record.schema_name ||
      "",
  )
  const invoice = firstRecord(data["invoice"], normalized["invoice"], normalizedData["invoice"], analysis["invoice"]) ?? {}
  const receipt = firstRecord(data["receipt"], normalized["receipt"], normalizedData["receipt"], analysis["receipt"]) ?? {}
  const trialBalance =
    firstRecord(normalized["trial_balance"], normalizedData["trial_balance"], analysis["trial_balance"]) ?? {}
  const accountTrialBalance =
    firstRecord(
      normalized["account_trial_balance"],
      normalizedData["account_trial_balance"],
      analysis["account_trial_balance"],
    ) ?? {}
  const journal = firstRecord(normalized["journal"], normalizedData["journal"], analysis["journal"]) ?? {}
  const bankStatement =
    firstRecord(normalized["bank_statement"], normalizedData["bank_statement"], analysis["bank_statement"]) ?? {}
  const quality = extractionQuality(record, normalized, analysis)
  const baseDocumentRows = compactFacts(
    [
      extractionFact(
        "document_type",
        translate("cowork.accounting.extractions.fact.documentType"),
        documentTypeLabel(documentType, translate),
      ),
      extractionFact(
        "document_date",
        translate("cowork.accounting.documents.column.date"),
        normalizedValue(firstRecord(invoice["document"], receipt["document"]), [
          "invoice_date",
          "receipt_date",
          "date",
        ]) ||
          metadataString(document, "issue_date") ||
          metadataString(document, "document_date") ||
          keyValueBySemantic(keyValues, ["document_date", "invoice_date", "receipt_date"]),
      ),
      extractionFact(
        "period",
        translate("cowork.accounting.extractions.fact.period"),
        normalizedValue(firstRecord(trialBalance, accountTrialBalance, journal, bankStatement), [
          "period",
          "reporting_period",
          "statement_period",
        ]) || keyValueBySemantic(keyValues, ["period", "reporting_period"]),
      ),
      extractionFact(
        "currency",
        translate("cowork.accounting.documents.column.currency"),
        normalizedValue(firstRecord(invoice["document"], receipt["document"], bankStatement), ["currency"]) ||
          metadataString(document, "currency") ||
          keyValueBySemantic(keyValues, ["currency"]),
      ),
    ],
    keyValues,
  )
  const invoiceDocument = firstRecord(invoice["document"], receipt["document"]) ?? {}
  const documentRows = compactFacts(
    [
      ...baseDocumentRows,
      extractionFact(
        "document_number",
        translate("cowork.accounting.documents.column.number"),
        normalizedValue(invoiceDocument, ["invoice_number", "receipt_number", "document_number"]) ||
          metadataString(document, "document_number") ||
          keyValueBySemantic(keyValues, ["document_number", "invoice_number", "receipt_number"]),
      ),
      extractionFact(
        "due_date",
        translate("cowork.accounting.review.dueDate"),
        normalizedValue(invoiceDocument, ["due_date"]) ||
          keyValueBySemantic(keyValues, ["due_date", "payment_due_date", "payment_deadline"]),
      ),
      extractionFact(
        "customer_number",
        translate("cowork.accounting.extractions.fact.customerNumber"),
        normalizedValue(invoiceDocument, ["customer_number"]) || keyValueBySemantic(keyValues, ["customer_number"]),
      ),
      extractionFact(
        "contract_number",
        translate("cowork.accounting.extractions.fact.contractNumber"),
        normalizedValue(invoiceDocument, ["contract_number"]) || keyValueBySemantic(keyValues, ["contract_number"]),
      ),
    ],
    keyValues,
  )
  const seller = firstRecord(invoice["seller"], receipt["merchant"], normalized["seller"], analysis["seller"]) ?? {}
  const buyer = firstRecord(invoice["buyer"], normalized["buyer"], analysis["buyer"]) ?? {}
  const amounts = firstRecord(invoice["amounts"], receipt["amounts"], normalized["amounts"], analysis["amounts"]) ?? {}
  const payment = firstRecord(invoice["payment"], receipt["payment"], normalized["payment"], analysis["payment"]) ?? {}
  const sellerRows = partyFacts(seller, keyValues, "seller", translate)
  const buyerRows = removeDuplicatedBuyerFacts(sellerRows, partyFacts(buyer, keyValues, "buyer", translate))
  return {
    documentType,
    documentRows,
    sellerRows,
    buyerRows,
    amountRows: amountFacts(amounts, document, keyValues, translate),
    paymentRows: paymentFacts(payment, keyValues, translate),
    tables: extractionTypeTables(documentType, tables, normalized, normalizedData),
    quality,
  }
}

function normalizedDocumentType(type: string) {
  const value = type.toLowerCase()
  if (
    ["purchase_invoice", "sales_invoice", "invoice", "insurance_invoice", "leasing_invoice", "credit_note"].includes(
      value,
    )
  )
    return value
  if (value.includes("receipt") || value.includes("payment_confirmation")) return "receipt"
  if (value.includes("account_trial") || value.includes("account_turnover")) return "account_trial_balance"
  if (value.includes("trial_balance")) return "trial_balance"
  if (value.includes("journal") || value.includes("päevaraamat") || value.includes("paevaraamat")) return "journal"
  if (value.includes("bank_statement") || value.includes("statement")) return "bank_statement"
  return value || "unknown_accounting_document"
}

function isInvoiceLikeExtraction(type: string) {
  return [
    "invoice",
    "purchase_invoice",
    "sales_invoice",
    "insurance_invoice",
    "leasing_invoice",
    "credit_note",
    "receipt",
  ].includes(type)
}

function extractionFact(
  key: string,
  label: string,
  value: unknown,
  source?: string,
  confidence?: number,
): ExtractionFact | undefined {
  const text = extractionScalar(value)
  return text ? { key, label, value: text, source, confidence } : undefined
}

function compactFacts(values: Array<ExtractionFact | undefined>, keyValues: Array<Record<string, string>>) {
  return values.flatMap((value) => {
    if (!value) return []
    const sourceRow = keyValues.find((row) => (row.semantic_key || "") === value.key)
    return [
      {
        ...value,
        source: value.source || extractionFieldSource(sourceRow ?? {}),
        confidence: value.confidence ?? extractionFieldConfidence(sourceRow ?? {}),
      },
    ]
  })
}

function normalizedValue(record: Record<string, unknown> | undefined, keys: string[]) {
  if (!record) return ""
  for (const key of keys) {
    const value = record[key]
    if (isRecord(value)) {
      const text = extractionScalar(
        value["value"] ??
          value["value_normalized"] ??
          value["normalized_value"] ??
          value["value_raw"] ??
          value["raw_value"],
      )
      if (text) return text
    }
    const text = extractionScalar(value)
    if (text) return text
  }
  return ""
}

function extractionScalar(value: unknown) {
  if (value === undefined || value === null || value === "") return ""
  if (typeof value === "string") return normalizeDocumentDate(value)
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return ""
}

function keyValueBySemantic(rows: Array<Record<string, string>>, keys: string[]) {
  const normalizedKeys = keys.map((key) => key.toLowerCase())
  const row = rows.find((item) => {
    const haystack =
      `${item.semantic_key ?? ""} ${item.field ?? ""} ${item.label ?? ""} ${item.name ?? ""}`.toLowerCase()
    return normalizedKeys.some((key) => haystack.includes(key))
  })
  return row ? extractionFieldValue(row) : ""
}

function keyValueByLabel(rows: Array<Record<string, string>>, labels: string[]) {
  const normalizedLabels = labels.map((label) => label.toLowerCase())
  const row = rows.find((item) => {
    const haystack = `${item.field ?? ""} ${item.label ?? ""} ${item.name ?? ""}`.toLowerCase()
    return normalizedLabels.some((label) => haystack.includes(label))
  })
  return row ? extractionFieldValue(row) : ""
}

function partyFacts(
  record: Record<string, unknown>,
  keyValues: Array<Record<string, string>>,
  prefix: "seller" | "buyer",
  translate: (key: string, params?: Record<string, string | number>) => string,
) {
  const isSeller = prefix === "seller"
  const labelFallback = (sellerLabels: string[], buyerLabels: string[]) =>
    keyValueByLabel(keyValues, isSeller ? sellerLabels : buyerLabels)
  return compactFacts(
    [
      extractionFact(
        `${prefix}_name`,
        translate("cowork.accounting.extractions.fact.name"),
        normalizedValue(record, ["name"]) ||
          keyValueBySemantic(keyValues, [
            `${prefix}_name`,
            isSeller ? "supplier_name" : "buyer_name",
            isSeller ? "vendor_name" : "customer_name",
          ]) ||
          labelFallback(["tarnija", "müüja", "saaja"], ["ostja", "maksja", "customer", "buyer"]),
      ),
      extractionFact(
        `${prefix}_registration_number`,
        translate("cowork.accounting.extractions.fact.registrationNumber"),
        normalizedValue(record, ["registration_number", "reg_number"]) ||
          keyValueBySemantic(keyValues, [
            `${prefix}_registration_number`,
            isSeller ? "supplier_registration_number" : "buyer_registration_number",
          ]) ||
          labelFallback(
            ["reg. nr", "registrikood", "reg. kood"],
            ["ostja reg", "maksja reg", "buyer reg", "customer reg"],
          ),
      ),
      extractionFact(
        `${prefix}_vat_number`,
        translate("cowork.accounting.extractions.fact.vatNumber"),
        normalizedValue(record, ["vat_number", "vat_id"]) ||
          keyValueBySemantic(keyValues, [
            `${prefix}_vat_number`,
            isSeller ? "supplier_vat_number" : "buyer_vat_number",
          ]) ||
          labelFallback(
            ["kmkr", "vat", "tax id"],
            ["ostja kmkr", "maksja kmkr", "buyer vat", "customer vat", "ostja vat"],
          ),
      ),
      extractionFact(
        `${prefix}_address`,
        translate("cowork.accounting.extractions.fact.address"),
        normalizedValue(record, ["address", "legal_address"]) ||
          keyValueBySemantic(keyValues, [`${prefix}_address`, isSeller ? "supplier_address" : "buyer_address"]) ||
          labelFallback(
            ["juriidiline aadress", "supplier address", "seller address"],
            ["ostja aadress", "maksja aadress", "buyer address", "customer address"],
          ),
      ),
      extractionFact(
        `${prefix}_email`,
        translate("cowork.accounting.extractions.fact.email"),
        normalizedValue(record, ["email"]) ||
          keyValueBySemantic(keyValues, [`${prefix}_email`]) ||
          labelFallback(["e-post", "email"], ["ostja e-post", "maksja e-post", "buyer email", "customer email"]),
      ),
      extractionFact(
        `${prefix}_phone`,
        translate("cowork.accounting.extractions.fact.phone"),
        normalizedValue(record, ["phone", "telephone"]) ||
          keyValueBySemantic(keyValues, [`${prefix}_phone`]) ||
          labelFallback(["telefon", "phone"], ["ostja telefon", "maksja telefon", "buyer phone", "customer phone"]),
      ),
    ],
    keyValues,
  )
}

function removeDuplicatedBuyerFacts(sellerRows: ExtractionFact[], buyerRows: ExtractionFact[]) {
  const sellerByKey = new Map(sellerRows.map((row) => [row.key.replace(/^seller_/, ""), row.value]))
  return buyerRows.filter((row) => {
    const key = row.key.replace(/^buyer_/, "")
    if (!["registration_number", "vat_number", "email", "phone"].includes(key)) return true
    return sellerByKey.get(key) !== row.value
  })
}

function amountFacts(
  record: Record<string, unknown>,
  document: Record<string, unknown>,
  keyValues: Array<Record<string, string>>,
  translate: (key: string, params?: Record<string, string | number>) => string,
) {
  return compactFacts(
    [
      extractionFact(
        "net_amount",
        translate("cowork.accounting.review.net"),
        normalizedValue(record, ["net_amount", "net"]) ||
          metadataString(document, "net_amount") ||
          keyValueBySemantic(keyValues, ["net_amount", "net"]),
      ),
      extractionFact(
        "vat_amount",
        translate("cowork.accounting.review.vat"),
        normalizedValue(record, ["vat_amount", "vat"]) ||
          metadataString(document, "vat_amount") ||
          keyValueBySemantic(keyValues, ["vat_amount", "vat"]),
      ),
      extractionFact(
        "gross_amount",
        translate("cowork.accounting.extractions.fact.invoiceTotal"),
        normalizedValue(record, ["gross_amount", "gross", "invoice_total"]) ||
          metadataString(document, "gross_amount") ||
          keyValueBySemantic(keyValues, ["gross_amount", "invoice_total"]),
      ),
      extractionFact(
        "previous_balance",
        translate("cowork.accounting.extractions.fact.previousBalance"),
        normalizedValue(record, ["previous_balance", "outstanding_balance"]) ||
          keyValueBySemantic(keyValues, ["previous_balance", "outstanding_balance"]) ||
          keyValueByLabel(keyValues, ["saldo seisuga"]),
      ),
      extractionFact(
        "amount_due",
        translate("cowork.accounting.extractions.fact.amountDue"),
        normalizedValue(record, ["amount_due", "payable_amount"]) ||
          keyValueBySemantic(keyValues, ["amount_due", "payable_amount"]) ||
          keyValueByLabel(keyValues, ["kokku tasuda"]),
      ),
    ],
    keyValues,
  )
}

function paymentFacts(
  record: Record<string, unknown>,
  keyValues: Array<Record<string, string>>,
  translate: (key: string, params?: Record<string, string | number>) => string,
) {
  return compactFacts(
    [
      extractionFact(
        "payment_status",
        translate("cowork.accounting.extractions.fact.paymentStatus"),
        normalizedValue(record, ["payment_status", "status"]),
      ),
      extractionFact(
        "bank_name",
        translate("cowork.accounting.extractions.fact.bank"),
        normalizedValue(record, ["bank_name", "bank"]) ||
          keyValueBySemantic(keyValues, ["bank_name", "bank"]) ||
          keyValueByLabel(keyValues, ["pank"]),
      ),
      extractionFact(
        "iban",
        translate("cowork.accounting.extractions.fact.iban"),
        normalizedValue(record, ["iban", "account_number"]) ||
          keyValueBySemantic(keyValues, ["iban", "account_number"]) ||
          keyValueByLabel(keyValues, ["kontonumber", "iban"]),
      ),
      extractionFact(
        "swift",
        translate("cowork.accounting.extractions.fact.swift"),
        normalizedValue(record, ["swift", "bic"]) ||
          keyValueBySemantic(keyValues, ["swift", "bic"]) ||
          keyValueByLabel(keyValues, ["swift", "bic"]),
      ),
      extractionFact(
        "reference",
        translate("cowork.accounting.extractions.fact.paymentReference"),
        normalizedValue(record, ["reference", "payment_reference"]) ||
          keyValueBySemantic(keyValues, ["payment_reference", "reference"]),
      ),
    ],
    keyValues,
  )
}

function extractionTypeTables(
  type: string,
  tables: ExtractedTable[],
  normalized: Record<string, unknown>,
  data: Record<string, unknown>,
) {
  const normalizedTables = normalizedTablesForType(type, normalized, data)
  if (normalizedTables.length > 0) return normalizedTables
  return tables.filter((table) => !["document_facts", "key_values", "source_lines"].includes(table.name))
}

function normalizedTablesForType(type: string, normalized: Record<string, unknown>, data: Record<string, unknown>) {
  const source =
    firstRecord(
      normalized["invoice"],
      normalized["receipt"],
      normalized["trial_balance"],
      normalized["account_trial_balance"],
      normalized["journal"],
      normalized["bank_statement"],
      data["invoice"],
      data["receipt"],
      data["trial_balance"],
      data["account_trial_balance"],
      data["journal"],
      data["bank_statement"],
    ) ?? {}
  const candidates: Array<[string, unknown]> = [
    ["invoice_lines", source["line_items"]],
    ["vat_summary", source["vat_summary"]],
    ["delivery_details", source["delivery_details"] ?? source["additional_tables"]],
    [type === "journal" ? "journal" : "trial_balance", source["rows"]],
    ["transactions", source["transactions"]],
  ]
  return candidates.flatMap(([name, rows]) =>
    Array.isArray(rows) && rows.length > 0
      ? [{ name, headers: extractionTableColumns(undefined, rows), rows: extractionRowsAsRecords(rows) }]
      : [],
  )
}

function extractionRowsAsRecords(rows: unknown[]) {
  return rows.flatMap((row) => {
    if (!isRecord(row)) return []
    return [Object.fromEntries(Object.entries(row).map(([key, value]) => [key, extractionTableCell(value)]))]
  })
}

function extractionQuality(
  record: CoworkExtraction,
  normalized: Record<string, unknown>,
  analysis: Record<string, unknown>,
): ExtractionQuality {
  const quality =
    firstRecord(
      record.result_json["quality"],
      normalized["quality"],
      analysis["quality"],
      analysis["extraction_quality"],
    ) ?? {}
  const warnings = metadataStringList(quality, "warnings")
  const conflicts = metadataStringList(quality, "conflicts")
  const confidenceRaw = quality["overall_confidence"] ?? quality["confidence"] ?? record.confidence
  const confidence =
    typeof confidenceRaw === "number" ? (confidenceRaw <= 1 ? confidenceRaw * 100 : confidenceRaw) : undefined
  return { overallConfidence: confidence, warnings: Array.from(new Set(warnings)), conflicts }
}

function metadataStringList(record: Record<string, unknown>, key: string) {
  const value = record[key]
  if (!Array.isArray(value)) return []
  return value.map((item) => (typeof item === "string" ? item : metadataInline(item))).filter(Boolean)
}

function firstRecord(...values: unknown[]) {
  return values.find(isRecord)
}

function extractionTableColumns(columns: unknown, rows: unknown[]) {
  if (Array.isArray(columns)) return columns.map(extractionTableCell).filter(Boolean)
  const keys = new Set<string>()
  for (const row of rows) {
    if (!isRecord(row)) continue
    for (const key of Object.keys(row)) keys.add(key)
  }
  return Array.from(keys)
}

function extractionTableCell(value: unknown) {
  if (value === undefined || value === null) return ""
  if (typeof value === "string") return value.startsWith("# ") ? value.slice(2) : value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return JSON.stringify(value)
}

function accountingExtractionDisplayTables(tables: ExtractedTable[]) {
  return tables
    .filter((table) => table.name !== "document_facts" && table.name !== "key_values")
    .map(expandRawCellsTable)
    .filter((table) => table.rows.length > 0)
}

function expandRawCellsTable(table: ExtractedTable): ExtractedTable {
  if (!table.headers.includes("raw_cells")) return table
  const expandedRows = table.rows.map((row) => ({ ...row, ...rawCellsColumns(row.raw_cells) }))
  const generatedHeaders = Array.from(
    new Set(expandedRows.flatMap((row) => Object.keys(row).filter((key) => /^\d+$/.test(key)))),
  )
  if (generatedHeaders.length === 0) return table
  const headers = [...table.headers.filter((header) => header !== "raw_cells"), ...generatedHeaders]
  return { ...table, headers, rows: expandedRows.map(withoutRawCells) }
}

function rawCellsColumns(value: string | undefined) {
  if (!value) return {}
  try {
    const parsed = JSON.parse(value) as unknown
    if (!Array.isArray(parsed)) return {}
    return Object.fromEntries(parsed.map((cell, index) => [String(index + 1), extractionTableCell(cell)]))
  } catch {
    return {}
  }
}

function withoutRawCells(row: Record<string, string>) {
  const copy = { ...row }
  delete copy.raw_cells
  return copy
}

function accountingExtractionKeyValues(tables: ExtractedTable[]) {
  const keyValueTable = tables.find((table) => table.name === "key_values")
  return keyValueTable ? cleanKeyValueRows(keyValueTable.rows) : []
}

function cleanKeyValueRows(rows: Array<Record<string, string>>) {
  return rows.flatMap((row) => {
    const field = row.field || row.label || row.name || ""
    if (!field) return []
    if (field === "agent_analysis.key_values") return parseEmbeddedKeyValues(row.value)
    if (field === "agent_analysis.comments" || field === "agent_analysis.terms") return [row]
    if (field.startsWith("document.")) return []
    if (field.startsWith("agent_analysis.")) return []
    return [row]
  })
}

function parseEmbeddedKeyValues(value: string | undefined) {
  if (!value) return []
  try {
    const parsed = JSON.parse(value) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.flatMap((item) => {
      if (!isRecord(item)) return []
      const field =
        metadataString(item, "field") ||
        metadataString(item, "label") ||
        metadataString(item, "name") ||
        metadataString(item, "key")
      const rowValue =
        metadataString(item, "value") ||
        metadataString(item, "value_raw") ||
        metadataString(item, "raw_value") ||
        metadataString(item, "value_normalized") ||
        metadataString(item, "normalized_value")
      if (!field || !rowValue) return []
      const row: Record<string, string> = { field, value: rowValue }
      for (const key of [
        "label",
        "semantic_key",
        "value_raw",
        "raw_value",
        "value_normalized",
        "normalized_value",
        "page",
        "source",
        "source_label",
        "source_text",
        "confidence",
        "currency",
      ]) {
        const extra = metadataString(item, key)
        if (extra) row[key] = extra
      }
      if (!row.source && row.page) row.source = `page ${row.page}`
      return [row]
    })
  } catch {
    return []
  }
}

type AccountingExtractionSectionKey =
  | "document"
  | "supplier"
  | "customer"
  | "payment"
  | "amounts"
  | "references"
  | "comments"
  | "other"

const ACCOUNTING_EXTRACTION_SECTION_ORDER: AccountingExtractionSectionKey[] = [
  "document",
  "supplier",
  "customer",
  "payment",
  "amounts",
  "references",
  "comments",
  "other",
]

function accountingExtractionSections(rows: Array<Record<string, string>>, translate: (key: string) => string) {
  const grouped = new Map<AccountingExtractionSectionKey, Array<Record<string, string>>>()
  for (const row of rows) {
    const key = accountingExtractionSectionKey(row)
    grouped.set(key, [...(grouped.get(key) ?? []), row])
  }
  return ACCOUNTING_EXTRACTION_SECTION_ORDER.flatMap((key) => {
    const sectionRows = grouped.get(key) ?? []
    if (sectionRows.length === 0) return []
    return [{ key, title: translate(`cowork.accounting.extractions.section.${key}`), rows: sectionRows }]
  })
}

function accountingExtractionSectionKey(row: Record<string, string>): AccountingExtractionSectionKey {
  const value = `${row.semantic_key ?? ""} ${row.field ?? ""} ${row.label ?? ""} ${row.name ?? ""}`.toLowerCase()
  if (matchesAny(value, ["comment", "terms", "instruction", "kommentaar", "selgitus"])) return "comments"
  if (
    matchesAny(value, [
      "amount",
      "net",
      "gross",
      "total",
      "balance",
      "vat",
      "tax",
      "summa",
      "kokku",
      "saldo",
      "tasuda",
      "käibemaks",
      "nds",
      "ндс",
      "итого",
    ])
  )
    return "amounts"
  if (matchesAny(value, ["bank", "iban", "swift", "bic", "account_number", "payment_method", "pank", "kontonumber"]))
    return "payment"
  if (
    matchesAny(value, [
      "supplier",
      "seller",
      "vendor",
      "counterparty",
      "tarnija",
      "legal_address",
      "juriidiline",
      "phone",
      "email",
      "e-post",
      "telefon",
    ])
  )
    return "supplier"
  if (matchesAny(value, ["customer", "buyer", "client", "ostja", "kliendi", "aadress", "address"])) return "customer"
  if (matchesAny(value, ["reference", "delivery", "po", "location", "kohaletoimet", "saateleht"])) return "references"
  if (
    matchesAny(value, [
      "document",
      "invoice",
      "date",
      "currency",
      "type",
      "arve",
      "kuupäev",
      "maksekuupäev",
      "valuuta",
      "номер",
      "дата",
    ])
  )
    return "document"
  return "other"
}

function matchesAny(value: string, needles: string[]) {
  return needles.some((needle) => value.includes(needle))
}

function extractionFieldLabel(row: Record<string, string>, translate: (key: string) => string) {
  if (row.field === "agent_analysis.comments" || row.field === "agent_analysis.terms")
    return translate("cowork.accounting.extractions.section.comments")
  return row.field || row.label || row.name || row.semantic_key || ""
}

function extractionFieldValue(row: Record<string, string>) {
  return row.value || row.value_raw || row.raw_value || row.value_normalized || row.normalized_value || ""
}

function extractionFieldSource(row: Record<string, string>) {
  if (row.source) return row.source
  if (row.source_label) return row.source_label
  if (row.page) return `page ${row.page}`
  return ""
}

function extractionFieldConfidence(row: Record<string, string>) {
  const raw = row.confidence
  if (!raw) return
  const parsed = Number(raw)
  if (!Number.isFinite(parsed)) return
  return parsed <= 1 ? parsed * 100 : parsed
}

function accountingExtractionTableName(name: string, translate: (key: string) => string) {
  if (name === "key_values") return translate("cowork.accounting.extractions.keyValues")
  if (name === "line_items") return translate("cowork.accounting.extractions.lineItems")
  if (name === "receipt_lines") return translate("cowork.accounting.extractions.receiptLines")
  if (name === "invoice_lines") return translate("cowork.accounting.extractions.invoiceLines")
  if (name === "vat_summary") return translate("cowork.accounting.extractions.vatSummary")
  if (name === "delivery_lines" || name === "delivery_details")
    return translate("cowork.accounting.extractions.deliveryDetails")
  if (name === "source_lines") return translate("cowork.accounting.extractions.sourceLines")
  return name.replaceAll("_", " ").replace(/^./, (char) => char.toUpperCase())
}

function PaymentViewTabs(props: {
  active: PaymentViewTab
  matched: number
  unpaid: number
  unmatched: number
  onChange: (view: PaymentViewTab) => void
}) {
  const language = useLanguage()
  const tabs: Array<[PaymentViewTab, string, number]> = [
    ["matched", language.t("cowork.accounting.payments.view.matched"), props.matched],
    ["unpaid", language.t("cowork.accounting.payments.view.unpaid"), props.unpaid],
    ["unmatched", language.t("cowork.accounting.payments.view.unmatched"), props.unmatched],
  ]
  return (
    <div class="flex flex-wrap gap-2 border-b border-v2-border-border-base pb-3">
      <For each={tabs}>
        {([view, label, count]) => (
          <button
            type="button"
            class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-13-medium text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
            classList={{ "bg-v2-background-bg-layer-03 text-v2-text-text-base": props.active === view }}
            onClick={() => props.onChange(view)}
          >
            {label} <span class="ml-2 rounded-full bg-v2-background-bg-base px-2 py-0.5 text-12-medium">{count}</span>
          </button>
        )}
      </For>
    </div>
  )
}

function UnpaidInvoiceCard(props: { document: CoworkAccountingDocument }) {
  const language = useLanguage()
  const dueDate = () => documentDueDate(props.document)
  return (
    <article class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
      <div class="grid gap-2 md:grid-cols-[.8fr_1fr_1fr_.8fr_.7fr_1fr]">
        <PaymentFact
          label={language.t("cowork.accounting.review.dueDate")}
          value={dueDate() ? formatRecordDate(dueDate()) : language.t("cowork.accounting.payments.dueDateMissing")}
        />
        <PaymentFact
          label={language.t("cowork.accounting.documents.column.number")}
          value={props.document.document_number || documentSourceLabel(props.document)}
        />
        <PaymentFact
          label={language.t("cowork.accounting.documents.column.counterparty")}
          value={props.document.counterparty_name || ""}
        />
        <PaymentFact
          label={language.t("cowork.accounting.payments.amount")}
          value={formatAccountingAmount(props.document)}
        />
        <PaymentFact
          label={language.t("cowork.accounting.payments.daysOverdue")}
          value={daysOverdueLabel(dueDate(), language.t)}
        />
        <PaymentFact
          label={language.t("cowork.accounting.documents.column.status")}
          value={props.document.review_status || props.document.processing_status}
        />
      </div>
    </article>
  )
}

function PaymentFact(props: { label: string; value: string }) {
  return (
    <div class="min-w-0">
      <p class="text-11-medium uppercase tracking-wide text-v2-text-text-muted">{props.label}</p>
      <p class="mt-1 truncate text-13-medium text-v2-text-text-base">{props.value}</p>
    </div>
  )
}

function PaymentMatchCard(props: { match: CoworkPaymentMatch }) {
  const language = useLanguage()
  const document = () => props.match.document
  const payment = () => props.match.payment
  return (
    <article class="mb-3 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3 last:mb-0">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h5 class="text-13-semibold text-v2-text-text-base">{language.t("cowork.accounting.payments.matchTitle")}</h5>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">
            {language.t("cowork.accounting.payments.matchConfidence", { confidence: props.match.confidence ?? 0 })}
          </p>
        </div>
      </div>
      <div class="grid gap-3 lg:grid-cols-[1fr_auto_1fr]">
        <PaymentSideCard
          title={language.t("cowork.accounting.payments.documentSide")}
          rows={[
            [language.t("cowork.accounting.documents.column.file"), documentSourceLabel(document() ?? undefined)],
            [language.t("cowork.accounting.documents.column.number"), document()?.document_number || "-"],
            [
              language.t("cowork.accounting.payments.invoiceDate"),
              document()?.document_date ? formatRecordDate(document()?.document_date ?? "") : "-",
            ],
            [language.t("cowork.accounting.documents.column.counterparty"), document()?.counterparty_name || "-"],
            [
              language.t("cowork.accounting.documents.column.gross"),
              document() ? formatAccountingAmount(document() as CoworkAccountingDocument) : "-",
            ],
          ]}
        />
        <div class="flex items-center justify-center text-20-semibold text-v2-text-text-muted">↔</div>
        <PaymentSideCard
          title={language.t("cowork.accounting.payments.paymentSide")}
          rows={[
            [
              language.t("cowork.accounting.payments.counterparty"),
              payment()?.counterparty_name || payment()?.description || "-",
            ],
            [language.t("cowork.accounting.payments.reference"), payment()?.reference || "-"],
            [
              language.t("cowork.accounting.payments.paymentDate"),
              payment()?.transaction_date ? formatRecordDate(payment()?.transaction_date ?? "") : "-",
            ],
            [
              language.t("cowork.accounting.payments.amount"),
              payment() ? formatPaymentAmount(payment() as CoworkPaymentTransaction) : "-",
            ],
          ]}
        />
      </div>
      <details class="mt-3 text-12-regular text-v2-text-text-muted">
        <summary class="cursor-pointer list-none hover:text-v2-text-text-base [&::-webkit-details-marker]:hidden">
          {compactMatchReasons(props.match.match_reasons)}
        </summary>
        <AccountingReasonList items={props.match.match_reasons} />
      </details>
    </article>
  )
}

function PaymentSideCard(props: { title: string; rows: Array<[string, string]> }) {
  return (
    <div class="rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3">
      <h6 class="mb-2 text-12-semibold text-v2-text-text-base">{props.title}</h6>
      <div class="grid grid-cols-[.8fr_1.2fr] gap-x-3 gap-y-1 text-12-regular">
        <For each={props.rows}>
          {([label, value]) => (
            <>
              <span class="text-v2-text-text-muted">{label}</span>
              <span class="truncate text-v2-text-text-base">{value}</span>
            </>
          )}
        </For>
      </div>
    </div>
  )
}

function PaymentTransactionCard(props: { payment: CoworkPaymentTransaction }) {
  return (
    <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2">
      <div class="flex items-center justify-between gap-3">
        <p class="truncate text-13-medium text-v2-text-text-base">
          {props.payment.counterparty_name || props.payment.description || props.payment.reference || "-"}
        </p>
        <p class="shrink-0 text-13-medium text-v2-text-text-base">{formatPaymentAmount(props.payment)}</p>
      </div>
      <p class="mt-1 text-12-regular text-v2-text-text-muted">
        {props.payment.reference || props.payment.description || "-"}
      </p>
    </div>
  )
}

function SortableDocumentHeader(props: {
  label: string
  sortKey: DocumentSortKey
  activeKey: DocumentSortKey
  direction: SortDirection
  onSort: (key: DocumentSortKey) => void
}) {
  const active = () => props.activeKey === props.sortKey
  return (
    <th class="px-4 py-2 font-medium">
      <button
        type="button"
        class="inline-flex items-center gap-1 text-left hover:text-v2-text-text-base"
        onClick={() => props.onSort(props.sortKey)}
      >
        <span>{props.label}</span>
        <span class="text-10-medium" classList={{ "opacity-40": !active() }}>
          {active() ? (props.direction === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </th>
  )
}

function DocumentColumnFilters(props: {
  options: Record<DocumentFilterKey, string[]>
  selected: Record<DocumentFilterKey, string[]>
  openKey: DocumentFilterKey | ""
  onOpenChange: (key: DocumentFilterKey | "") => void
  onToggle: (key: DocumentFilterKey, value: string) => void
}) {
  const language = useLanguage()
  const columns: Array<[DocumentFilterKey, string]> = [
    ["type", language.t("cowork.accounting.documents.column.type")],
    ["counterparty", language.t("cowork.accounting.documents.column.counterparty")],
    ["currency", language.t("cowork.accounting.documents.column.currency")],
    ["status", language.t("cowork.accounting.documents.column.status")],
    ["postings", language.t("cowork.accounting.documents.column.postings")],
  ]
  return (
    <div class="border-b border-v2-border-border-base p-3">
      <div class="mb-2 text-12-medium text-v2-text-text-muted">
        {language.t("cowork.accounting.documents.columnFilters")}
      </div>
      <div class="flex flex-wrap gap-2">
        <For each={columns}>
          {([key, label]) => (
            <div class="relative" data-document-filter-root>
              <button
                type="button"
                class="rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2 text-12-regular text-v2-text-text-base hover:bg-v2-background-bg-layer-02"
                onClick={(event) => {
                  event.stopPropagation()
                  props.onOpenChange(props.openKey === key ? "" : key)
                }}
              >
                {label}
                <span class="ml-2 text-v2-text-text-muted">
                  {(props.selected[key] ?? []).length || language.t("cowork.accounting.documents.allValues")}
                </span>
              </button>
              <Show when={props.openKey === key}>
                <div class="absolute left-0 top-[calc(100%+0.5rem)] z-30 max-h-72 min-w-72 overflow-auto rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-3 shadow-lg">
                  <For
                    each={props.options[key]}
                    fallback={
                      <p class="text-v2-text-text-muted">{language.t("cowork.accounting.documents.noFilterValues")}</p>
                    }
                  >
                    {(value) => (
                      <label class="flex cursor-pointer items-center gap-2 py-1 text-v2-text-text-muted hover:text-v2-text-text-base">
                        <input
                          type="checkbox"
                          checked={(props.selected[key] ?? []).includes(value)}
                          onChange={() => props.onToggle(key, value)}
                        />
                        <span class="truncate">{value}</span>
                      </label>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          )}
        </For>
      </div>
    </div>
  )
}

function CopyableDocumentCell(props: { value: string; className?: string }) {
  const language = useLanguage()
  const [store, setStore] = createStore({ copied: false })
  const copy = (event: MouseEvent) => {
    event.stopPropagation()
    if (!props.value || props.value === "-") return
    void navigator.clipboard?.writeText(props.value).then(() => {
      setStore("copied", true)
      window.setTimeout(() => setStore("copied", false), 1200)
    })
  }
  return (
    <td class={`group px-4 py-3 text-v2-text-text-muted ${props.className ?? ""}`}>
      <div class="flex min-w-0 items-center gap-2">
        <span class="truncate">{props.value}</span>
        <button
          type="button"
          class="shrink-0 rounded p-0.5 text-v2-text-text-muted opacity-0 transition-opacity hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base group-hover:opacity-100 focus:opacity-100"
          aria-label={language.t("cowork.accounting.documents.copyCell")}
          title={language.t("cowork.accounting.documents.copyCell")}
          onClick={copy}
        >
          <IconV2 name={store.copied ? "check" : "outline-copy"} size="small" />
        </button>
      </div>
    </td>
  )
}

function AccountingSourcePackReconciliationPanel(props: {
  documents: CoworkAccountingDocument[]
  reviews: CoworkAccountingReview[]
  taxReports: CoworkAccountingTaxReport[]
  paymentMatches: CoworkPaymentMatch[]
}) {
  const language = useLanguage()
  const sections = createMemo(() =>
    sourcePackReconciliationSections(
      props.documents,
      props.reviews,
      props.taxReports,
      props.paymentMatches,
      language.t,
    ),
  )
  return (
    <section class="mb-4 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01">
      <div class="border-b border-v2-border-border-base px-4 py-3">
        <h3 class="text-15-semibold text-v2-text-text-base">{language.t("cowork.accounting.reconciliation.title")}</h3>
        <p class="mt-1 text-12-regular text-v2-text-text-muted">
          {language.t("cowork.accounting.reconciliation.description")}
        </p>
      </div>
      <div class="grid gap-3 p-4 lg:grid-cols-2 xl:grid-cols-3">
        <For each={sections()}>
          {(section) => (
            <article class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
              <h4 class="text-13-semibold text-v2-text-text-base">{section.title}</h4>
              <p class="mt-1 text-12-regular text-v2-text-text-muted">
                {section.status || language.t("cowork.accounting.reconciliation.notPrepared")}
              </p>
              <For each={section.items.slice(0, 6)}>
                {(item) => (
                  <p class="mt-2 border-t border-v2-border-border-base pt-2 text-12-regular text-v2-text-text-muted">
                    {item}
                  </p>
                )}
              </For>
            </article>
          )}
        </For>
      </div>
    </section>
  )
}

function AccountingOverviewPanel(props: {
  readiness: CoworkAccountingReadiness | undefined
  loading: boolean
  onNavigate: (tab: AccountingTab) => void
}) {
  const language = useLanguage()
  return (
    <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01">
      <div class="flex flex-wrap items-center justify-between gap-3 border-b border-v2-border-border-base px-4 py-3">
        <div>
          <h3 class="text-15-semibold text-v2-text-text-base">{language.t("cowork.accounting.overview.title")}</h3>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">
            {language.t("cowork.accounting.overview.description")}
          </p>
        </div>
        <span class="rounded-full border border-v2-border-border-base px-3 py-1 text-12-medium text-v2-text-text-muted">
          {language.t(
            props.readiness?.ready_to_close
              ? "cowork.accounting.overview.ready"
              : "cowork.accounting.overview.notReady",
          )}
        </span>
      </div>
      <Show
        when={!props.loading}
        fallback={<p class="p-4 text-13-regular text-v2-text-text-muted">{language.t("common.loading")}</p>}
      >
        <div class="grid gap-4 p-4 xl:grid-cols-2">
          <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
            <h4 class="mb-3 text-13-semibold text-v2-text-text-base">
              {language.t("cowork.accounting.overview.checks")}
            </h4>
            <For
              each={props.readiness?.checks ?? []}
              fallback={
                <p class="text-12-regular text-v2-text-text-muted">
                  {language.t("cowork.accounting.overview.noChecks")}
                </p>
              }
            >
              {(check) => (
                <div class="flex items-center justify-between gap-3 border-b border-v2-border-border-base py-2 text-12-regular last:border-b-0">
                  <span class="text-v2-text-text-base">
                    {language.t(`cowork.accounting.readiness.check.${check.key}`)}
                  </span>
                  <span class={check.passed ? "text-v2-text-text-success" : "text-v2-text-text-muted"}>
                    {check.current}/{check.total}
                  </span>
                </div>
              )}
            </For>
          </div>
          <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
            <h4 class="mb-3 text-13-semibold text-v2-text-text-base">
              {language.t("cowork.accounting.overview.warnings")}
            </h4>
            <For
              each={props.readiness?.warnings ?? []}
              fallback={
                <p class="text-12-regular text-v2-text-text-muted">
                  {language.t("cowork.accounting.overview.noWarnings")}
                </p>
              }
            >
              {(warning) => (
                <p class="border-b border-v2-border-border-base py-2 text-12-regular text-v2-text-text-muted last:border-b-0">
                  {language.t(`cowork.accounting.readiness.warning.${warning.key}`, { count: warning.count })}
                </p>
              )}
            </For>
          </div>
        </div>
        <div class="border-t border-v2-border-border-base p-4">
          <h4 class="mb-3 text-13-semibold text-v2-text-text-base">
            {language.t("cowork.accounting.overview.nextActions")}
          </h4>
          <div class="flex flex-wrap gap-2">
            <For
              each={props.readiness?.next_actions ?? []}
              fallback={
                <p class="text-12-regular text-v2-text-text-muted">
                  {language.t("cowork.accounting.overview.noActions")}
                </p>
              }
            >
              {(action) => (
                <button
                  type="button"
                  class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-13-medium text-v2-text-text-base hover:bg-v2-background-bg-layer-02"
                  onClick={() => props.onNavigate(action.tab as AccountingTab)}
                >
                  {language.t(`cowork.accounting.readiness.action.${action.key}`)}
                </button>
              )}
            </For>
          </div>
        </div>
      </Show>
    </section>
  )
}

function AccountingClientSummary(props: { clientName: string; periodName: string; metadata: Record<string, unknown> }) {
  const language = useLanguage()
  const client = createMemo(() => accountingClientMetadata(props.metadata))
  const rows = createMemo(
    () =>
      [
        [language.t("cowork.accounting.client.client"), props.clientName || metadataString(client(), "name")],
        [language.t("cowork.accounting.client.period"), props.periodName],
        [
          language.t("cowork.accounting.client.registrationCode"),
          metadataString(client(), "registration_code") ||
            metadataString(client(), "registry_code") ||
            metadataString(client(), "reg_code"),
        ],
        [
          language.t("cowork.accounting.client.vatNumber"),
          metadataString(client(), "vat_number") ||
            metadataString(client(), "vat_id") ||
            metadataString(client(), "kmkr"),
        ],
        [language.t("cowork.accounting.client.address"), metadataString(client(), "address")],
        [language.t("cowork.accounting.client.country"), metadataString(client(), "country")],
      ].filter(([, value]) => value) as Array<[string, string]>,
  )
  return (
    <section class="mb-5 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3">
        <h3 class="text-15-semibold text-v2-text-text-base">{language.t("cowork.accounting.client.title")}</h3>
      </div>
      <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <For each={rows()}>
          {([label, value]) => (
            <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
              <p class="text-11-medium uppercase tracking-wide text-v2-text-text-muted">{label}</p>
              <p class="mt-1 truncate text-13-medium text-v2-text-text-base">{value}</p>
            </div>
          )}
        </For>
      </div>
    </section>
  )
}

function AccountingLedgerPanel(props: {
  ledger: CoworkLedgerView | undefined
  loading: boolean
  view: LedgerViewTab
  onViewChange: (view: LedgerViewTab) => void
  documents: CoworkAccountingDocument[]
  reviews: CoworkAccountingReview[]
  taxReports: CoworkAccountingTaxReport[]
  paymentMatches: CoworkPaymentMatch[]
  exportPackages: CoworkAccountingExportPackage[]
  exportPackagesLoading: boolean
  hasWorkflow: boolean
  onCreateExportPackage: () => Promise<void>
  onMarkExportSubmitted: (pkg: CoworkAccountingExportPackage) => Promise<void>
  onDownloadExportFile: (file: CoworkAccountingExportFile) => Promise<void>
  onDeleteExportFile: (file: CoworkAccountingExportFile) => Promise<void>
}) {
  const language = useLanguage()
  const views: LedgerViewTab[] = ["journal", "trialBalance"]
  const summary = () => props.ledger?.summary
  return (
    <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01">
      <div class="border-b border-v2-border-border-base p-4">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 class="text-18-semibold text-v2-text-text-base">
              {language.t("cowork.accounting.ledger.title", { period: summary()?.workflow_name || "" })}
            </h3>
            <Show when={summary()}>
              {(value) => (
                <div class="mt-2 flex flex-wrap gap-2 text-12-regular text-v2-text-text-muted">
                  <span>
                    {language.t("cowork.accounting.ledger.header.entries", { count: value().journal_entries })}
                  </span>
                  <span>
                    {language.t("cowork.accounting.ledger.header.debit", {
                      amount: formatMoney(value().debit_total, "EUR"),
                    })}
                  </span>
                  <span>
                    {language.t("cowork.accounting.ledger.header.credit", {
                      amount: formatMoney(value().credit_total, "EUR"),
                    })}
                  </span>
                  <span class={value().balanced ? "text-v2-text-text-success" : "text-v2-text-text-error"}>
                    {value().balanced
                      ? language.t("cowork.accounting.ledger.balanced")
                      : language.t("cowork.accounting.ledger.imbalance", {
                          amount: formatMoney(Math.abs(value().imbalance), "EUR"),
                        })}
                  </span>
                </div>
              )}
            </Show>
          </div>
          <Show when={summary() && (summary()?.status_counts.needs_review ?? 0) > 0}>
            <button
              type="button"
              class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-13-medium text-v2-text-text-base hover:bg-v2-background-bg-layer-02"
              onClick={() =>
                window.dispatchEvent(new CustomEvent("cowork:accounting-open-view", { detail: { tab: "review" } }))
              }
            >
              {language.t("cowork.accounting.ledger.openReview", { count: summary()?.status_counts.needs_review ?? 0 })}
            </button>
          </Show>
        </div>
        <div class="mt-4 flex flex-wrap items-center justify-between gap-2">
          <div class="flex flex-wrap gap-2">
            <For each={views}>
              {(view) => (
                <button
                  type="button"
                  class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-13-medium text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                  classList={{ "bg-v2-background-bg-layer-03 text-v2-text-text-base": props.view === view }}
                  onClick={() => props.onViewChange(view)}
                >
                  {language.t(`cowork.accounting.ledger.view.${view}`)}
                </button>
              )}
            </For>
          </div>
          <div class="flex items-center gap-1">
            <Show when={props.ledger}>
              {(ledger) => (
                <TableExpandButton
                  title={language.t(`cowork.accounting.ledger.view.${props.view}`)}
                  rows={ledgerRowsForModal(props.view, ledger())}
                />
              )}
            </Show>
            <button
              type="button"
              class="rounded-md border border-v2-border-border-base p-1.5 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base disabled:opacity-50"
              title={language.t("cowork.accounting.table.downloadCsv")}
              aria-label={language.t("cowork.accounting.table.downloadCsv")}
              disabled={props.loading || !props.ledger}
              onClick={() => downloadLedgerCsv(props.view, props.ledger)}
            >
              <IconV2 name="download" size="small" />
            </button>
          </div>
        </div>
      </div>
      <Show
        when={!props.loading}
        fallback={<p class="p-4 text-13-regular text-v2-text-text-muted">{language.t("common.loading")}</p>}
      >
        <Show when={props.view === "journal"}>
          <LedgerJournalTable rows={props.ledger?.journal ?? []} />
        </Show>
        <Show when={props.view === "trialBalance"}>
          <LedgerTrialBalanceTable rows={props.ledger?.trial_balance ?? []} />
        </Show>
      </Show>
    </section>
  )
}

function LedgerJournalTable(props: { rows: CoworkLedgerJournalLine[] }) {
  const language = useLanguage()
  return (
    <div class="max-h-[70vh] overflow-auto">
      <table class="w-full min-w-[1280px] border-collapse text-left text-12-regular">
        <thead class="sticky top-0 z-10 bg-v2-background-bg-layer-01 text-v2-text-text-base">
          <tr>
            <LedgerHeaderCell class="w-14 text-right">
              {language.t("cowork.accounting.ledger.column.rowNumber")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="w-24">
              {language.t("cowork.accounting.ledger.column.journalDate")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="w-24 text-right">
              {language.t("cowork.accounting.ledger.column.journalDebit")}
            </LedgerHeaderCell>
            <LedgerHeaderCell>{language.t("cowork.accounting.ledger.column.debitAnalytics")}</LedgerHeaderCell>
            <LedgerHeaderCell class="w-24 text-right">
              {language.t("cowork.accounting.ledger.column.journalCredit")}
            </LedgerHeaderCell>
            <LedgerHeaderCell>{language.t("cowork.accounting.ledger.column.creditAnalytics")}</LedgerHeaderCell>
            <LedgerHeaderCell class="w-20 text-right">
              {language.t("cowork.accounting.ledger.column.quantity")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="w-16 text-center">
              {language.t("cowork.accounting.ledger.column.currencyShort")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="w-28 text-right">
              {language.t("cowork.accounting.ledger.column.foreignAmount")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="w-28 text-right">
              {language.t("cowork.accounting.ledger.column.journalAmount")}
            </LedgerHeaderCell>
            <LedgerHeaderCell>{language.t("cowork.accounting.ledger.column.info")}</LedgerHeaderCell>
            <LedgerHeaderCell class="w-16 text-center">
              {language.t("cowork.accounting.ledger.column.journalShort")}
            </LedgerHeaderCell>
          </tr>
        </thead>
        <tbody>
          <For
            each={props.rows}
            fallback={
              <tr>
                <td class="border border-v2-border-border-base px-3 py-3 text-v2-text-text-muted" colSpan={12}>
                  {language.t("cowork.accounting.ledger.empty")}
                </td>
              </tr>
            }
          >
            {(row, index) => (
              <tr class="border-b border-v2-border-border-base last:border-b-0">
                <LedgerCell class="text-right text-v2-text-text-muted">{index() + 1}</LedgerCell>
                <LedgerCell>{formatLedgerDate(row.date)}</LedgerCell>
                <LedgerCell class="text-right text-v2-text-text-base">{row.debit_account || ""}</LedgerCell>
                <LedgerCell>{ledgerAnalytics(row.debit_account_name, row.description)}</LedgerCell>
                <LedgerCell class="text-right text-v2-text-text-base">{row.credit_account || ""}</LedgerCell>
                <LedgerCell>
                  {ledgerAnalytics(row.credit_account_name, row.counterparty_name, row.document_reference)}
                </LedgerCell>
                <LedgerCell class="text-right">{ledgerMetadataValue(row.analytics_json, "quantity")}</LedgerCell>
                <LedgerCell class="text-center">
                  {row.currency && row.currency !== "EUR" ? row.currency : ""}
                </LedgerCell>
                <LedgerCell class="text-right">
                  {row.currency && row.currency !== "EUR" ? formatLedgerNumber(row.amount) : ""}
                </LedgerCell>
                <LedgerCell class="text-right text-v2-text-text-base">{formatLedgerNumber(row.amount)}</LedgerCell>
                <LedgerCell>{ledgerJournalInfo(row)}</LedgerCell>
                <LedgerCell class="text-center">
                  {ledgerMetadataValue(row.analytics_json, "journal") ||
                    ledgerMetadataValue(row.analytics_json, "journal_code")}
                </LedgerCell>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
  )
}

function LedgerTrialBalanceTable(props: { rows: CoworkLedgerTrialBalanceRow[] }) {
  const language = useLanguage()
  return (
    <div class="max-h-[70vh] overflow-auto">
      <table class="w-full min-w-[1080px] border-collapse text-left text-12-regular">
        <thead class="sticky top-0 z-10 bg-v2-background-bg-layer-01 text-v2-text-text-base">
          <tr>
            <LedgerHeaderCell class="text-center" colSpan={2}>
              {language.t("cowork.accounting.ledger.column.account")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="text-center" colSpan={2}>
              {language.t("cowork.accounting.ledger.column.openingBalance")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="text-center" colSpan={2}>
              {language.t("cowork.accounting.ledger.column.periodTurnover")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="text-center" colSpan={2}>
              {language.t("cowork.accounting.ledger.column.closingBalance")}
            </LedgerHeaderCell>
          </tr>
          <tr>
            <LedgerHeaderCell>{language.t("cowork.accounting.ledger.column.code")}</LedgerHeaderCell>
            <LedgerHeaderCell>{language.t("cowork.accounting.ledger.column.name")}</LedgerHeaderCell>
            <LedgerHeaderCell class="text-right">
              {language.t("cowork.accounting.ledger.column.debit")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="text-right">
              {language.t("cowork.accounting.ledger.column.credit")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="text-right">
              {language.t("cowork.accounting.ledger.column.debit")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="text-right">
              {language.t("cowork.accounting.ledger.column.credit")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="text-right">
              {language.t("cowork.accounting.ledger.column.debit")}
            </LedgerHeaderCell>
            <LedgerHeaderCell class="text-right">
              {language.t("cowork.accounting.ledger.column.credit")}
            </LedgerHeaderCell>
          </tr>
        </thead>
        <tbody>
          <For
            each={props.rows}
            fallback={
              <tr>
                <td class="border border-v2-border-border-base px-3 py-3 text-v2-text-text-muted" colSpan={8}>
                  {language.t("cowork.accounting.ledger.empty")}
                </td>
              </tr>
            }
          >
            {(row) => (
              <tr class="border-b border-v2-border-border-base last:border-b-0">
                <LedgerCell class="text-v2-text-text-base">{row.account}</LedgerCell>
                <LedgerCell>{row.account_name || ""}</LedgerCell>
                <LedgerCell class="text-right">
                  {row.opening_available ? formatLedgerNumber(row.opening_debit) : ""}
                </LedgerCell>
                <LedgerCell class="text-right">
                  {row.opening_available ? formatLedgerNumber(row.opening_credit) : ""}
                </LedgerCell>
                <LedgerCell class="text-right text-v2-text-text-base">
                  {formatLedgerNumber(row.period_debit)}
                </LedgerCell>
                <LedgerCell class="text-right text-v2-text-text-base">
                  {formatLedgerNumber(row.period_credit)}
                </LedgerCell>
                <LedgerCell class="text-right">{formatLedgerNumber(row.closing_debit)}</LedgerCell>
                <LedgerCell class="text-right">{formatLedgerNumber(row.closing_credit)}</LedgerCell>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
  )
}

function LedgerHeaderCell(props: { children: JSX.Element; class?: string; colSpan?: number }) {
  return (
    <th
      class={`border border-v2-border-border-base px-2 py-1.5 font-medium ${props.class ?? ""}`}
      colSpan={props.colSpan}
    >
      {props.children}
    </th>
  )
}

function LedgerCell(props: { children: JSX.Element; class?: string }) {
  return (
    <td
      class={`border border-v2-border-border-base px-2 py-1.5 align-top text-v2-text-text-muted ${props.class ?? ""}`}
    >
      {props.children}
    </td>
  )
}

function downloadLedgerCsv(view: LedgerViewTab, ledger: CoworkLedgerView | undefined) {
  if (!ledger) return
  if (view === "trialBalance") {
    downloadAccountingCsv("trial-balance.csv", ledger.trial_balance.map(trialBalanceRowForCsv))
    return
  }
  downloadAccountingCsv("journal.csv", ledger.journal.map(journalRowForCsv))
}

function ledgerRowsForModal(view: LedgerViewTab, ledger: CoworkLedgerView) {
  if (view === "trialBalance")
    return recordsToTableRows(trialBalanceCsvHeaders(), ledger.trial_balance.map(trialBalanceRowForCsv))
  return recordsToTableRows(journalCsvHeaders(), ledger.journal.map(journalRowForCsv))
}

function journalCsvHeaders() {
  return [
    "Jrk.nr.",
    "Kuupäev",
    "Deebet",
    "Analüütika deebet",
    "Kreedit",
    "Analüütika kreedit",
    "Kogus",
    "Val.",
    "Summa val.",
    "Summa",
    "Info",
    "Žur.",
  ]
}

function trialBalanceCsvHeaders() {
  return [
    "Kood",
    "Nimetus",
    "Saldo algus deebet",
    "Saldo algus kreedit",
    "Perioodi deebet",
    "Perioodi kreedit",
    "Saldo lõpp deebet",
    "Saldo lõpp kreedit",
  ]
}

function journalRowForCsv(row: CoworkLedgerJournalLine, index: number): Record<string, unknown> {
  return {
    "Jrk.nr.": index + 1,
    Kuupäev: formatLedgerDate(row.date),
    Deebet: row.debit_account || "",
    "Analüütika deebet": ledgerAnalytics(row.debit_account_name, row.description),
    Kreedit: row.credit_account || "",
    "Analüütika kreedit": ledgerAnalytics(row.credit_account_name, row.counterparty_name, row.document_reference),
    Kogus: ledgerMetadataValue(row.analytics_json, "quantity"),
    "Val.": row.currency && row.currency !== "EUR" ? row.currency : "",
    "Summa val.": row.currency && row.currency !== "EUR" ? formatLedgerNumber(row.amount) : "",
    Summa: formatLedgerNumber(row.amount),
    Info: ledgerJournalInfo(row),
    "Žur.":
      ledgerMetadataValue(row.analytics_json, "journal") || ledgerMetadataValue(row.analytics_json, "journal_code"),
  }
}

function trialBalanceRowForCsv(row: CoworkLedgerTrialBalanceRow): Record<string, unknown> {
  return {
    Kood: row.account,
    Nimetus: row.account_name || "",
    "Saldo algus deebet": row.opening_available ? formatLedgerNumber(row.opening_debit) : "",
    "Saldo algus kreedit": row.opening_available ? formatLedgerNumber(row.opening_credit) : "",
    "Perioodi deebet": formatLedgerNumber(row.period_debit),
    "Perioodi kreedit": formatLedgerNumber(row.period_credit),
    "Saldo lõpp deebet": formatLedgerNumber(row.closing_debit),
    "Saldo lõpp kreedit": formatLedgerNumber(row.closing_credit),
  }
}

function AccountingExportPackagePanel(props: {
  packages: CoworkAccountingExportPackage[]
  loading: boolean
  hasWorkflow: boolean
  onCreate: () => Promise<void>
  onMarkSubmitted: (pkg: CoworkAccountingExportPackage) => Promise<void>
  onDownloadFile: (file: CoworkAccountingExportFile) => Promise<void>
  onDeleteFile: (file: CoworkAccountingExportFile) => Promise<void>
}) {
  const language = useLanguage()
  return (
    <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01">
      <div class="flex flex-wrap items-center justify-between gap-3 border-b border-v2-border-border-base px-4 py-3">
        <div>
          <h3 class="text-15-semibold text-v2-text-text-base">{language.t("cowork.accounting.exports.title")}</h3>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">
            {language.t("cowork.accounting.exports.description")}
          </p>
        </div>
        <button
          type="button"
          class="rounded-md bg-v2-text-text-base px-3 py-1.5 text-13-medium text-v2-background-bg-base hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!props.hasWorkflow}
          onClick={() => void props.onCreate()}
        >
          {language.t("cowork.accounting.exports.create")}
        </button>
      </div>
      <Show
        when={!props.loading}
        fallback={<p class="p-4 text-13-regular text-v2-text-text-muted">{language.t("common.loading")}</p>}
      >
        <For
          each={props.packages}
          fallback={
            <p class="p-4 text-13-regular text-v2-text-text-muted">{language.t("cowork.accounting.exports.empty")}</p>
          }
        >
          {(pkg) => (
            <div class="border-b border-v2-border-border-base px-4 py-3 last:border-b-0">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-13-medium text-v2-text-text-base">
                    {language.t("cowork.accounting.exports.packageTitle", { type: pkg.package_type })}
                  </p>
                  <p class="mt-1 text-12-regular text-v2-text-text-muted">
                    {language.t("cowork.accounting.exports.packageMeta", {
                      status: pkg.status,
                      validation: pkg.validation_status,
                      submission: pkg.manual_submission_status,
                    })}
                  </p>
                  <p class="mt-1 truncate text-11-regular text-v2-text-text-muted">
                    {language.t("cowork.accounting.exports.checksum", { checksum: pkg.checksum_sha256 })}
                  </p>
                </div>
                <button
                  type="button"
                  class="rounded-md border border-v2-border-border-base px-2 py-1 text-12-medium text-v2-text-text-base hover:bg-v2-background-bg-layer-02"
                  onClick={() => void props.onMarkSubmitted(pkg)}
                >
                  {language.t("cowork.accounting.exports.markSubmitted")}
                </button>
              </div>
              <div class="mt-3 grid gap-2 md:grid-cols-2">
                <For each={pkg.files}>
                  {(file) => (
                    <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2">
                      <div class="flex items-start justify-between gap-3">
                        <p class="min-w-0 truncate text-13-medium text-v2-text-text-base">{file.filename}</p>
                        <div class="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                            title={language.t("cowork.accounting.exports.downloadFile")}
                            aria-label={language.t("cowork.accounting.exports.downloadFile")}
                            onClick={() => void props.onDownloadFile(file)}
                          >
                            <IconV2 name="download" size="small" />
                          </button>
                          <button
                            type="button"
                            class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-danger"
                            title={language.t("cowork.accounting.exports.deleteFile")}
                            aria-label={language.t("cowork.accounting.exports.deleteFile")}
                            onClick={() => void props.onDeleteFile(file)}
                          >
                            <IconV2 name="trash" size="small" />
                          </button>
                        </div>
                      </div>
                      <p class="mt-1 text-11-regular text-v2-text-text-muted">
                        {language.t("cowork.accounting.exports.fileMeta", {
                          type: file.file_type.toUpperCase(),
                          size: file.size,
                        })}
                      </p>
                    </div>
                  )}
                </For>
              </div>
              <Show when={pkg.validation_errors.length > 0}>
                <div class="mt-3 rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-2 text-12-regular text-v2-text-text-muted">
                  <For each={pkg.validation_errors}>{(error) => <p>{error}</p>}</For>
                </div>
              </Show>
            </div>
          )}
        </For>
      </Show>
    </section>
  )
}

function AccountingTaxReportPanel(props: {
  reports: CoworkAccountingTaxReport[]
  artifacts: CoworkArtifact[]
  view: TaxViewTab
  loading: boolean
  onViewChange: (view: TaxViewTab) => void
  onDeleteReport: (report: CoworkAccountingTaxReport) => Promise<void>
  onDownloadArtifact: (artifact: CoworkArtifact) => Promise<void>
  onDeleteArtifact: (artifact: CoworkArtifact) => Promise<void>
}) {
  const language = useLanguage()
  const views: TaxViewTab[] = ["summary", "returns", "reconciliation", "report"]
  return (
    <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01">
      <div class="flex flex-wrap items-center justify-between gap-3 border-b border-v2-border-border-base px-4 py-3">
        <div>
          <h3 class="text-15-semibold text-v2-text-text-base">{language.t("cowork.accounting.tax.title")}</h3>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">{language.t("cowork.accounting.tax.description")}</p>
        </div>
      </div>
      <Show
        when={!props.loading}
        fallback={<p class="p-4 text-13-regular text-v2-text-text-muted">{language.t("common.loading")}</p>}
      >
        <div class="border-b border-v2-border-border-base px-4 py-3">
          <div class="flex flex-wrap gap-2">
            <For each={views}>
              {(view) => (
                <button
                  type="button"
                  class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-13-medium text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                  classList={{ "bg-v2-background-bg-layer-03 text-v2-text-text-base": props.view === view }}
                  onClick={() => props.onViewChange(view)}
                >
                  {language.t(`cowork.accounting.tax.view.${view}`)}
                </button>
              )}
            </For>
          </div>
        </div>
        <div class="p-4">
          <Show when={props.view === "summary"}>
            <TaxSummaryView reports={props.reports} />
          </Show>
          <Show when={props.view === "returns"}>
            <TaxReturnsView
              reports={props.reports}
              artifacts={props.artifacts}
              onDeleteReport={props.onDeleteReport}
              onDownloadArtifact={props.onDownloadArtifact}
              onDeleteArtifact={props.onDeleteArtifact}
            />
          </Show>
          <Show when={props.view === "reconciliation"}>
            <TaxReconciliationView reports={props.reports} />
          </Show>
          <Show when={props.view === "report"}>
            <TaxReportFilesView
              artifacts={props.artifacts}
              onDownload={props.onDownloadArtifact}
              onDelete={props.onDeleteArtifact}
            />
          </Show>
        </div>
      </Show>
    </section>
  )
}

function AccountingWorkflowSteps() {
  const language = useLanguage()
  const steps = [
    "upload",
    "extract",
    "findPayment",
    "match",
    "classify",
    "postings",
    "review",
    "export",
    "tax",
    "submit",
    "correct",
  ]
  return (
    <section class="mb-5 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-15-semibold text-v2-text-text-base">{language.t("cowork.accounting.workflow.title")}</h3>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">
            {language.t("cowork.accounting.workflow.description")}
          </p>
        </div>
      </div>
      <div class="flex gap-2 overflow-x-auto pb-1">
        <For each={steps}>
          {(step, index) => (
            <div class="min-w-[150px] rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
              <div class="mb-2 flex items-center gap-2">
                <span class="flex size-6 items-center justify-center rounded-full bg-v2-background-bg-layer-03 text-11-medium text-v2-text-text-base">
                  {index() + 1}
                </span>
                <span class="text-12-medium text-v2-text-text-base">
                  {language.t(`cowork.accounting.workflow.step.${step}.title`)}
                </span>
              </div>
              <p class="text-11-regular text-v2-text-text-muted">
                {language.t(`cowork.accounting.workflow.step.${step}.description`)}
              </p>
            </div>
          )}
        </For>
      </div>
    </section>
  )
}

function TaxReturnsView(props: {
  reports: CoworkAccountingTaxReport[]
  artifacts: CoworkArtifact[]
  onDeleteReport: (report: CoworkAccountingTaxReport) => Promise<void>
  onDownloadArtifact: (artifact: CoworkArtifact) => Promise<void>
  onDeleteArtifact: (artifact: CoworkArtifact) => Promise<void>
}) {
  const language = useLanguage()
  return (
    <div class="grid gap-4 lg:grid-cols-2">
      <TaxReturnCard
        title={language.t("cowork.accounting.tax.kmdSummary")}
        report={latestTaxReport(props.reports, "kmd")}
        artifacts={props.artifacts}
        onDeleteReport={props.onDeleteReport}
        onDownloadArtifact={props.onDownloadArtifact}
        onDeleteArtifact={props.onDeleteArtifact}
      />
      <TaxReturnCard
        title={language.t("cowork.accounting.tax.tsdSummary")}
        report={latestTaxReport(props.reports, "tsd")}
        artifacts={props.artifacts}
        onDeleteReport={props.onDeleteReport}
        onDownloadArtifact={props.onDownloadArtifact}
        onDeleteArtifact={props.onDeleteArtifact}
      />
      <TaxContractTable
        class="lg:col-span-2"
        title={language.t("cowork.accounting.tax.generatedDataPreview")}
        reports={props.reports.filter((report) => ["kmd", "tsd"].includes(report.report_type))}
        empty={language.t("cowork.accounting.tax.noPreviewData")}
      />
    </div>
  )
}

function TaxReturnCard(props: {
  title: string
  report?: CoworkAccountingTaxReport
  artifacts: CoworkArtifact[]
  onDeleteReport: (report: CoworkAccountingTaxReport) => Promise<void>
  onDownloadArtifact: (artifact: CoworkArtifact) => Promise<void>
  onDeleteArtifact: (artifact: CoworkArtifact) => Promise<void>
}) {
  const language = useLanguage()
  return (
    <article class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
      <div class="mb-3 flex items-start justify-between gap-3">
        <div>
          <h4 class="text-14-semibold text-v2-text-text-base">{props.title}</h4>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">
            {props.report
              ? language.t("cowork.accounting.tax.periodStatus", {
                  period: props.report.reporting_period || props.report.jurisdiction,
                  status: taxStatusLabel(props.report.validation_status, language.t),
                })
              : language.t("cowork.accounting.tax.notGenerated")}
          </p>
        </div>
        <Show when={props.report}>
          {(report) => (
            <button
              type="button"
              class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-danger"
              title={language.t("cowork.accounting.tax.deleteReport")}
              aria-label={language.t("cowork.accounting.tax.deleteReport")}
              onClick={() => void props.onDeleteReport(report())}
            >
              <IconV2 name="trash" size="small" />
            </button>
          )}
        </Show>
      </div>
      <Show
        when={props.report}
        fallback={<p class="text-12-regular text-v2-text-text-muted">{language.t("cowork.accounting.tax.askAgent")}</p>}
      >
        {(report) => (
          <>
            <div class="grid gap-2 sm:grid-cols-2">
              <TaxMetric
                label={language.t("cowork.accounting.tax.documents")}
                value={String(reportMetadataNumber(report().report_metadata, "documents_count"))}
              />
              <TaxMetric
                label={language.t("cowork.accounting.tax.approvedPostings")}
                value={String(reportMetadataNumber(report().report_metadata, "approved_posting_groups"))}
              />
              <TaxMetric
                label={
                  report().report_type === "tsd"
                    ? language.t("cowork.accounting.tax.tsdPayroll")
                    : language.t("cowork.accounting.tax.kmdVatPayable")
                }
                value={taxReportTotalAmount(report())}
              />
              <TaxMetric
                label={language.t("cowork.accounting.tax.validation")}
                value={taxStatusLabel(report().validation_status, language.t)}
              />
            </div>
            <TaxValidationNotes report={report()} />
          </>
        )}
      </Show>
    </article>
  )
}

function TaxSummaryView(props: { reports: CoworkAccountingTaxReport[] }) {
  const language = useLanguage()
  const kmd = () => latestTaxReport(props.reports, "kmd")
  const tsd = () => latestTaxReport(props.reports, "tsd")
  return (
    <div class="grid gap-4 lg:grid-cols-2">
      <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
        <h4 class="mb-3 text-14-semibold text-v2-text-text-base">
          {language.t("cowork.accounting.tax.accountingSummary")}
        </h4>
        <TaxMetric
          label={language.t("cowork.accounting.tax.kmdVatPayable")}
          value={kmd() ? taxReportTotalAmount(kmd() as CoworkAccountingTaxReport) : ""}
        />
        <TaxMetric
          label={language.t("cowork.accounting.tax.tsdPayroll")}
          value={tsd() ? taxReportTotalAmount(tsd() as CoworkAccountingTaxReport) : ""}
        />
      </section>
      <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
        <h4 class="mb-3 text-14-semibold text-v2-text-text-base">{language.t("cowork.accounting.tax.taxSummary")}</h4>
        <For
          each={props.reports}
          fallback={<p class="text-12-regular text-v2-text-text-muted">{language.t("cowork.accounting.tax.empty")}</p>}
        >
          {(report) => (
            <p class="border-b border-v2-border-border-base py-2 text-12-regular text-v2-text-text-muted last:border-b-0">
              {report.report_type.toUpperCase()} · {report.reporting_period || report.jurisdiction} ·{" "}
              {taxStatusLabel(report.validation_status, language.t)}
            </p>
          )}
        </For>
      </section>
      <TaxContractTable
        class="lg:col-span-2"
        title={language.t("cowork.accounting.tax.accountingTaxData")}
        reports={props.reports.filter((report) =>
          ["accounting_tax_summary", "tax_summary", "summary"].includes(report.report_type),
        )}
        empty={language.t("cowork.accounting.tax.noPreviewData")}
      />
    </div>
  )
}

function TaxReconciliationView(props: { reports: CoworkAccountingTaxReport[] }) {
  const language = useLanguage()
  const items = createMemo(() => props.reports.flatMap((report) => taxReconciliationItems(report, language.t)))
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
      <h4 class="mb-3 text-14-semibold text-v2-text-text-base">{language.t("cowork.accounting.tax.reconciliation")}</h4>
      <For
        each={items()}
        fallback={
          <p class="text-12-regular text-v2-text-text-muted">{language.t("cowork.accounting.tax.noReconciliation")}</p>
        }
      >
        {(item) => (
          <p class="border-b border-v2-border-border-base py-2 text-12-regular text-v2-text-text-muted last:border-b-0">
            {item}
          </p>
        )}
      </For>
      <div class="mt-4">
        <TaxContractTable
          title={language.t("cowork.accounting.tax.reconciliationData")}
          reports={props.reports.filter((report) =>
            ["accounting_reconciliation", "reconciliation"].includes(report.report_type),
          )}
          empty={language.t("cowork.accounting.tax.noPreviewData")}
        />
      </div>
    </section>
  )
}

function TaxContractTable(props: {
  title: string
  reports: CoworkAccountingTaxReport[]
  empty: string
  class?: string
}) {
  return (
    <section class={`rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3 ${props.class ?? ""}`}>
      <h4 class="mb-3 text-14-semibold text-v2-text-text-base">{props.title}</h4>
      <For each={props.reports} fallback={<p class="text-12-regular text-v2-text-text-muted">{props.empty}</p>}>
        {(report) => <TaxMetadataRows report={report} />}
      </For>
    </section>
  )
}

function TaxMetadataRows(props: { report: CoworkAccountingTaxReport }) {
  const rows = createMemo(() => metadataRowsToTable(props.report.report_metadata))
  return (
    <article class="mb-3 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3 last:mb-0">
      <h5 class="mb-2 text-13-medium text-v2-text-text-base">
        {metadataString(props.report.report_metadata, "title") ||
          `${props.report.report_type.toUpperCase()} ${props.report.reporting_period || ""}`}
      </h5>
      <Show
        when={rows().length > 1}
        fallback={<p class="text-12-regular text-v2-text-text-muted">{metadataInline(props.report.report_metadata)}</p>}
      >
        <div class="mb-2 flex justify-end">
          <TableExpandButton
            title={
              metadataString(props.report.report_metadata, "title") ||
              `${props.report.report_type.toUpperCase()} ${props.report.reporting_period || ""}`
            }
            rows={rows()}
          />
        </div>
        <div class="max-h-72 overflow-auto">
          <table class="w-full min-w-[720px] border-collapse text-left text-12-regular">
            <thead class="sticky top-0 bg-v2-background-bg-layer-01 text-v2-text-text-muted">
              <tr class="border-b border-v2-border-border-base">
                <For each={rows()[0]}>{(cell) => <th class="px-2 py-1 font-medium">{cell}</th>}</For>
              </tr>
            </thead>
            <tbody>
              <For each={rows().slice(1)}>
                {(row) => (
                  <tr class="border-b border-v2-border-border-base last:border-b-0">
                    <For each={row}>{(cell) => <td class="px-2 py-1 text-v2-text-text-base">{cell}</td>}</For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </article>
  )
}

function TaxReportFilesView(props: {
  artifacts: CoworkArtifact[]
  onDownload: (artifact: CoworkArtifact) => Promise<void>
  onDelete: (artifact: CoworkArtifact) => Promise<void>
}) {
  return (
    <TaxReportArtifacts
      artifacts={props.artifacts.filter(isTaxXmlArtifact)}
      onDownload={props.onDownload}
      onDelete={props.onDelete}
      alwaysShow
      titleKey="cowork.accounting.tax.xmlFiles"
      emptyKey="cowork.accounting.tax.noXmlFiles"
    />
  )
}

function TaxMetric(props: { label: string; value: string }) {
  return (
    <div class="rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 p-2">
      <p class="text-11-medium uppercase tracking-wide text-v2-text-text-muted">{props.label}</p>
      <p class="mt-1 text-13-medium text-v2-text-text-base">{props.value || ""}</p>
    </div>
  )
}

function TaxArtifactPreviewPanel(props: { title: string; previews: TaxArtifactPreview[] }) {
  const language = useLanguage()
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
      <h4 class="mb-3 text-14-semibold text-v2-text-text-base">{props.title}</h4>
      <For
        each={props.previews}
        fallback={
          <p class="text-12-regular text-v2-text-text-muted">{language.t("cowork.accounting.tax.noPreviewData")}</p>
        }
      >
        {(preview) => (
          <article class="mb-3 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3 last:mb-0">
            <h5 class="mb-2 text-13-medium text-v2-text-text-base">{preview.artifact.title}</h5>
            <Show when={preview.rows.length > 1} fallback={<TaxJsonPreview value={preview.json ?? preview.text} />}>
              <div class="mb-2 flex justify-end">
                <TableExpandButton title={preview.artifact.title} rows={preview.rows} />
              </div>
              <div class="max-h-72 overflow-auto">
                <table class="w-full min-w-[720px] border-collapse text-left text-12-regular">
                  <thead class="sticky top-0 bg-v2-background-bg-layer-01 text-v2-text-text-muted">
                    <tr class="border-b border-v2-border-border-base">
                      <For each={preview.rows[0]}>{(cell) => <th class="px-2 py-1 font-medium">{cell}</th>}</For>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={preview.rows.slice(1, 12)}>
                      {(row) => (
                        <tr class="border-b border-v2-border-border-base last:border-b-0">
                          <For each={row}>{(cell) => <td class="px-2 py-1 text-v2-text-text-base">{cell}</td>}</For>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Show>
          </article>
        )}
      </For>
    </section>
  )
}

function TaxJsonPreview(props: { value: unknown }) {
  const text = () => (typeof props.value === "string" ? props.value : metadataInline(props.value))
  return <p class="text-12-regular text-v2-text-text-muted">{text().slice(0, 1000)}</p>
}

function TaxValidationNotes(props: { report: CoworkAccountingTaxReport }) {
  return (
    <Show when={props.report.validation_errors.length > 0}>
      <div class="mt-3 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 p-2 text-12-regular text-v2-text-text-muted">
        <For each={props.report.validation_errors}>
          {(error) => (
            <p>
              {typeof error === "string"
                ? error
                : String((error as Record<string, unknown>).detail ?? (error as Record<string, unknown>).key ?? error)}
            </p>
          )}
        </For>
      </div>
    </Show>
  )
}

function metadataRowsToTable(metadata: Record<string, unknown>) {
  const rows = Array.isArray(metadata.rows) ? metadata.rows : []
  if (rows.every(isRecord) && rows.length > 0) {
    const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
    return [headers, ...rows.map((row) => headers.map((header) => metadataInline(row[header])))]
  }
  const summary = Array.isArray(metadata.summary) ? metadata.summary : []
  if (summary.every(isRecord) && summary.length > 0) {
    const headers = Array.from(new Set(summary.flatMap((row) => Object.keys(row))))
    return [headers, ...summary.map((row) => headers.map((header) => metadataInline(row[header])))]
  }
  const hidden = new Set(["rows", "summary", "title"])
  const entries = Object.entries(metadata).filter(([key]) => !hidden.has(key) && !isTechnicalTaxPreviewField(key))
  return entries.length
    ? [["Field", "Value"], ...entries.map(([key, value]) => [key.replaceAll("_", " "), metadataInline(value)])]
    : []
}

function latestTaxReport(reports: CoworkAccountingTaxReport[], type: string) {
  return reports
    .filter((report) => report.report_type === type)
    .toSorted((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))[0]
}

function taxStatusLabel(status: string, translate: (key: string) => string) {
  if (status === "passed") return translate("cowork.accounting.tax.status.passed")
  if (status === "failed") return translate("cowork.accounting.tax.status.failed")
  if (status === "needs_review") return translate("cowork.accounting.tax.status.needsReview")
  if (status === "pending") return translate("cowork.accounting.tax.status.pending")
  return status.replaceAll("_", " ")
}

function taxReconciliationItems(
  report: CoworkAccountingTaxReport,
  translate: (key: string, params?: Record<string, string | number>) => string,
) {
  const items = valueToStringList(
    report.report_metadata.reconciliation ?? report.report_metadata.source_pack_reconciliation,
  )
  if (items.length) return items.map((item) => `${report.report_type.toUpperCase()}: ${item}`)
  return [
    translate("cowork.accounting.tax.reconciliationFallback", {
      type: report.report_type.toUpperCase(),
      status: taxStatusLabel(report.validation_status, (key) => translate(key)),
    }),
  ]
}

async function loadTaxArtifactPreview(
  artifact: CoworkArtifact,
  translate: (key: string) => string,
): Promise<TaxArtifactPreview> {
  if (!canPreviewTaxArtifact(artifact)) return { artifact, text: "", rows: [] }
  const result = await downloadArtifact(artifact.project_id, artifact.id)
  const text = await result.blob.text()
  const json = parseJsonPreview(text)
  return { artifact, text, json, rows: json ? jsonToRows(json, translate) : parseCsvPreview(text) }
}

function canPreviewTaxArtifact(artifact: CoworkArtifact) {
  const format = artifact.format.toLowerCase()
  return ["csv", "json", "txt"].includes(format)
}

function parseJsonPreview(text: string) {
  try {
    return JSON.parse(text) as unknown
  } catch {
    return undefined
  }
}

function parseCsvPreview(text: string) {
  const lines = text.trim().split(/\r?\n/).slice(0, 50)
  return lines.map((line) => line.split(",").map((cell) => cell.trim().replace(/^"|"$/g, "")))
}

function jsonToRows(value: unknown, translate: (key: string) => string): string[][] {
  const rows = Array.isArray(value) ? value : isRecord(value) && Array.isArray(value.rows) ? value.rows : []
  if ((!Array.isArray(rows) || rows.length === 0) && isRecord(value)) {
    return [
      [translate("cowork.accounting.tax.preview.field"), translate("cowork.accounting.tax.preview.value")],
      ...Object.entries(flattenJsonPreview(value))
        .filter(([key]) => !isTechnicalTaxPreviewField(key))
        .map(([key, entry]) => [taxPreviewFieldLabel(key, translate), taxPreviewValue(entry, translate)]),
    ]
  }
  if (!Array.isArray(rows) || !rows.every(isRecord)) return []
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
  return [headers, ...rows.map((row) => headers.map((header) => metadataInline(row[header])))]
}

function taxPreviewFieldLabel(key: string, translate: (key: string) => string) {
  const i18nKey = `cowork.accounting.tax.preview.${key.replaceAll(".", "_")}`
  const translated = translate(i18nKey)
  if (translated && translated !== i18nKey) return translated
  return key
    .split(".")
    .map((part) => part.replaceAll("_", " "))
    .join(" — ")
    .replace(/^./, (char) => char.toUpperCase())
}

function isTechnicalTaxPreviewField(key: string) {
  return key === "workflow_id" || key.endsWith("_id") || key.includes("source_tax_report_id")
}

function taxPreviewValue(value: unknown, translate: (key: string) => string) {
  if (typeof value === "string") return taxPreviewStatusLabel(value, translate)
  if (typeof value === "boolean")
    return value ? translate("cowork.accounting.tax.preview.yes") : translate("cowork.accounting.tax.preview.no")
  return metadataInline(value)
}

function taxPreviewStatusLabel(value: string, translate: (key: string) => string) {
  if (value === "draft_ready_for_review") return translate("cowork.accounting.tax.preview.status.draftReadyForReview")
  if (value === "needs_review") return translate("cowork.accounting.tax.status.needsReview")
  if (value === "passed") return translate("cowork.accounting.tax.status.passed")
  if (value === "failed") return translate("cowork.accounting.tax.status.failed")
  if (value === "pending") return translate("cowork.accounting.tax.status.pending")
  return value.replaceAll("_", " ")
}

function flattenJsonPreview(value: Record<string, unknown>, prefix = "") {
  return Object.entries(value).reduce<Record<string, unknown>>((result, [key, entry]) => {
    const path = prefix ? `${prefix}.${key}` : key
    if (isRecord(entry)) Object.assign(result, flattenJsonPreview(entry, path))
    else result[path] = entry
    return result
  }, {})
}

function taxPreviewKind(preview: TaxArtifactPreview) {
  const title = preview.artifact.title.toLowerCase()
  if (title.includes("kmd and tsd draft control summary")) return "kmd_tsd_control"
  if (title.includes("accounting and tax draft summary")) return "accounting_tax_summary"
  if (title.includes("accounting reconciliation draft")) return "accounting_reconciliation"
  return "other"
}

function TaxReportArtifacts(props: {
  artifacts: CoworkArtifact[]
  onDownload: (artifact: CoworkArtifact) => Promise<void>
  onDelete: (artifact: CoworkArtifact) => Promise<void>
  alwaysShow?: boolean
  titleKey?: string
  emptyKey?: string
}) {
  const language = useLanguage()
  return (
    <Show when={props.artifacts.length > 0 || props.alwaysShow}>
      <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
        <h4 class="mb-2 text-13-medium text-v2-text-text-base">
          {language.t(props.titleKey ?? "cowork.accounting.tax.generatedFiles")}
        </h4>
        <Show
          when={props.artifacts.length > 0}
          fallback={
            <p class="text-12-regular text-v2-text-text-muted">
              {language.t(props.emptyKey ?? "cowork.accounting.tax.noGeneratedFiles")}
            </p>
          }
        >
          <div class="grid gap-2 md:grid-cols-2">
            <For each={props.artifacts}>
              {(artifact) => (
                <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 py-2">
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <p class="truncate text-12-medium text-v2-text-text-base">{artifact.title}</p>
                      <p class="mt-1 text-11-regular text-v2-text-text-muted">
                        {artifact.format.toUpperCase()} · {artifact.status}
                      </p>
                    </div>
                    <div class="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                        title={language.t("cowork.accounting.tax.downloadArtifact")}
                        aria-label={language.t("cowork.accounting.tax.downloadArtifact")}
                        onClick={() => void props.onDownload(artifact)}
                      >
                        <IconV2 name="download" size="small" />
                      </button>
                      <button
                        type="button"
                        class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-danger"
                        title={language.t("cowork.accounting.tax.deleteArtifact")}
                        aria-label={language.t("cowork.accounting.tax.deleteArtifact")}
                        onClick={() => void props.onDelete(artifact)}
                      >
                        <IconV2 name="trash" size="small" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </section>
    </Show>
  )
}

function AccountingSummaryCards(props: {
  documents: CoworkAccountingDocument[]
  reviews: CoworkAccountingReview[]
  payments: CoworkPaymentTransaction[]
  paymentMatches: CoworkPaymentMatch[]
}) {
  const language = useLanguage()
  const averageConfidence = createMemo(() => {
    const values = props.documents
      .map(
        (document) =>
          document.extraction_confidence ?? document.classification_confidence ?? document.payment_match_confidence,
      )
      .filter((value): value is number => typeof value === "number")
    if (!values.length) return 0
    return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)
  })
  return (
    <div class="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <AccountingMetricCard label={language.t("cowork.accounting.summary.documents")} value={props.documents.length} />
      <AccountingMetricCard
        label={language.t("cowork.accounting.summary.payments")}
        value={props.paymentMatches.length || props.payments.length}
      />
      <AccountingMetricCard
        label={language.t("cowork.accounting.summary.confidence")}
        value={`${averageConfidence()}%`}
      />
      <AccountingMetricCard label={language.t("cowork.accounting.summary.attention")} value={props.reviews.length} />
    </div>
  )
}

function AccountingMetricCard(props: { label: string; value: string | number }) {
  return (
    <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <p class="text-12-regular text-v2-text-text-muted">{props.label}</p>
      <p class="mt-1 text-24-semibold text-v2-text-text-base">{props.value}</p>
    </section>
  )
}

function AccountingReviewReport(props: {
  clientName: string
  clientMetadata: Record<string, unknown>
  review: CoworkAccountingReview
  paymentMatch?: CoworkPaymentMatch
  onSelect: () => void
  onApprove: () => Promise<void>
  onReject: () => Promise<void>
}) {
  const language = useLanguage()
  const document = () => props.review.document
  const posting = () => props.review.posting_group
  const analysis = createMemo(() => agentAnalysis(document()?.document_metadata))
  const lineItems = createMemo(() => reviewLineItems(document()?.document_metadata))
  const sourceReferences = createMemo(() =>
    reviewSourceReferences(props.review, document() ?? undefined, posting() ?? undefined, props.paymentMatch),
  )
  const matchEvidence = createMemo(() => reviewMatchEvidence(props.paymentMatch))
  const closingEntries = createMemo(() => reviewClosingEntries(props.review, props.paymentMatch))
  const whyItems = createMemo(() =>
    reviewWhyItems(props.review, document() ?? undefined, posting() ?? undefined, props.paymentMatch),
  )
  createEffect(() => {
    props.onSelect()
    window.dispatchEvent(
      new CustomEvent("cowork:accounting-context-selected", {
        detail: {
          reviewID: props.review.id,
          documentID: props.review.document_id,
          postingGroupID: props.review.posting_group_id,
          sourceFilename: props.review.document?.source_filename,
        },
      }),
    )
  })
  return (
    <article class="overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
      <div class="flex flex-wrap items-center justify-between gap-3 border-b border-v2-border-border-base bg-v2-background-bg-layer-01 px-4 py-3">
        <div>
          <h4 class="text-16-semibold text-v2-text-text-base">{documentSourceLabel(document() ?? undefined)}</h4>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">
            {language.t("cowork.accounting.review.itemMeta", {
              status: props.review.review_status,
              postings: posting()?.validation_status ?? language.t("cowork.accounting.postings.notGenerated"),
            })}
          </p>
        </div>
        <p class="text-13-medium text-v2-text-text-muted">
          {language.t("cowork.accounting.review.systemConfidence", {
            confidence: document()?.classification_confidence ?? document()?.extraction_confidence ?? 0,
          })}
        </p>
      </div>
      <div class="grid gap-3 p-4 xl:grid-cols-3">
        <div class="space-y-3">
          <AccountingFactsCard
            title={language.t("cowork.accounting.review.documentFacts")}
            rows={[
              [
                language.t("cowork.accounting.documents.column.type"),
                documentTypeLabel(document()?.document_type, language.t),
              ],
              [language.t("cowork.accounting.documents.column.counterparty"), document()?.counterparty_name || "-"],
              [
                language.t("cowork.accounting.review.supplierVatNumber"),
                document()?.counterparty_vat_number ||
                  metadataString(analysis(), "supplier_vat_number") ||
                  metadataString(analysis(), "seller_vat_number") ||
                  "-",
              ],
              [
                language.t("cowork.accounting.review.buyer"),
                metadataString(analysis(), "buyer_name") ||
                  metadataString(analysis(), "customer_name") ||
                  metadataString(document()?.document_metadata, "buyer_name") ||
                  props.clientName,
              ],
              [
                language.t("cowork.accounting.review.buyerVatNumber"),
                metadataString(analysis(), "buyer_vat_number") ||
                  metadataString(analysis(), "customer_vat_number") ||
                  metadataString(document()?.document_metadata, "buyer_vat_number") ||
                  clientVatNumber(props.clientMetadata),
              ],
              [language.t("cowork.accounting.documents.column.number"), document()?.document_number || "-"],
              [
                language.t("cowork.accounting.documents.column.date"),
                document()?.document_date ? formatRecordDate(document()?.document_date ?? "") : "-",
              ],
              [
                language.t("cowork.accounting.review.dueDate"),
                document()?.due_date ? formatRecordDate(document()?.due_date ?? "") : "-",
              ],
              [
                language.t("cowork.accounting.review.paymentMethod"),
                document()?.payment_method ||
                  metadataString(analysis(), "payment_method") ||
                  metadataString(analysis(), "payment_type"),
              ],
              [language.t("cowork.accounting.review.vehicle"), reviewVehicle(document() ?? undefined, analysis())],
              [
                language.t("cowork.accounting.review.net"),
                formatOptionalAmount(document()?.net_amount, document()?.currency),
              ],
              [
                language.t("cowork.accounting.review.vat"),
                formatOptionalAmount(document()?.vat_amount, document()?.currency, document()?.vat_rate),
              ],
              [
                language.t("cowork.accounting.review.rounding"),
                formatOptionalAmount(document()?.rounding_amount, document()?.currency),
              ],
              [
                language.t("cowork.accounting.documents.column.gross"),
                document() ? formatAccountingAmount(document() as CoworkAccountingDocument) : "-",
              ],
            ]}
          />
          <AccountingTextCard
            title={language.t("cowork.accounting.review.documentContent")}
            text={
              metadataString(analysis(), "document_summary") ||
              metadataString(document()?.document_metadata, "content_summary") ||
              metadataString(analysis(), "reasoning_summary")
            }
          />
          <AccountingReasonCard
            title={language.t("cowork.accounting.review.sourceReferences")}
            items={sourceReferences()}
          />
          <AccountingLineItemsCard items={lineItems()} />
        </div>
        <div class="space-y-3">
          <AccountingFactsCard
            title={language.t("cowork.accounting.review.classification")}
            rows={[
              [
                language.t("cowork.accounting.review.operationType"),
                metadataString(analysis(), "operation_type") || "-",
              ],
              [
                language.t("cowork.accounting.review.category"),
                metadataString(analysis(), "category") || metadataString(analysis(), "accounting_category"),
              ],
              [
                language.t("cowork.accounting.review.analytics"),
                metadataInline(analysis()["analytics"] ?? analysis()["analytics_json"]),
              ],
              [
                language.t("cowork.accounting.review.vehicleDimension"),
                reviewVehicle(document() ?? undefined, analysis()),
              ],
              [
                language.t("cowork.accounting.review.projectDimension"),
                metadataString(analysis(), "project") || metadataString(analysis(), "project_dimension"),
              ],
              [
                language.t("cowork.accounting.review.personDimension"),
                metadataString(analysis(), "person") || metadataString(analysis(), "employee"),
              ],
              [
                language.t("cowork.accounting.review.vatTreatment"),
                vatTreatmentLabel(metadataString(analysis(), "vat_treatment"), language.t),
              ],
              [
                language.t("cowork.accounting.review.vatRate"),
                document()?.vat_rate === undefined || document()?.vat_rate === null ? "-" : `${document()?.vat_rate}%`,
              ],
              [
                language.t("cowork.accounting.review.recommendationReason"),
                metadataString(analysis(), "classification_reason") || metadataString(analysis(), "reasoning_summary"),
              ],
            ]}
          />
          <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3">
            <h5 class="mb-3 text-13-semibold text-v2-text-text-base">
              {language.t("cowork.accounting.review.postingPreview")}
            </h5>
            <For
              each={posting()?.lines ?? []}
              fallback={
                <p class="text-12-regular text-v2-text-text-muted">
                  {language.t("cowork.accounting.postings.notGenerated")}
                </p>
              }
            >
              {(line) => (
                <div class="grid grid-cols-[.8fr_.8fr_auto_1.2fr] gap-2 border-b border-v2-border-border-base py-2 text-12-regular last:border-b-0">
                  <span>{line.debit_account || "-"}</span>
                  <span>{line.credit_account || "-"}</span>
                  <span>
                    {new Intl.NumberFormat().format(line.amount)} {line.currency}
                  </span>
                  <span class="text-v2-text-text-muted">
                    {line.description || metadataInline(line.analytics_json) || "-"}
                  </span>
                </div>
              )}
            </For>
            <Show when={posting()?.explanation}>
              <p class="mt-3 rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-2 text-12-regular text-v2-text-text-muted">
                {posting()?.explanation}
              </p>
            </Show>
          </section>
        </div>
        <div class="space-y-3">
          <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3">
            <h5 class="mb-3 text-13-semibold text-v2-text-text-base">
              {language.t("cowork.accounting.review.paymentMatch")}
            </h5>
            <Show
              when={props.paymentMatch}
              fallback={
                <p class="text-12-regular text-v2-text-text-muted">
                  {language.t("cowork.accounting.payments.noMatches")}
                </p>
              }
            >
              {(match) => (
                <div class="text-12-regular text-v2-text-text-muted">
                  <p class="text-13-medium text-v2-text-text-base">
                    {match().payment?.counterparty_name || match().payment?.description || "-"}
                  </p>
                  <p class="mt-1">{formatPaymentAmount(match().payment as CoworkPaymentTransaction)}</p>
                  <p class="mt-1">
                    {match().payment?.transaction_date
                      ? formatRecordDate(match().payment?.transaction_date ?? "")
                      : "-"}
                  </p>
                  <p class="mt-1">{match().payment?.reference || match().payment?.description || "-"}</p>
                  <p class="mt-1">
                    {language.t("cowork.accounting.payments.matchMeta", {
                      confidence: match().confidence ?? 0,
                      status: match().status,
                    })}
                  </p>
                  <AccountingMatchEvidenceList evidence={matchEvidence()} fallback={match().match_reasons} />
                </div>
              )}
            </Show>
          </section>
          <AccountingEntriesCard
            title={language.t("cowork.accounting.review.closingEntries")}
            entries={closingEntries()}
          />
          <AccountingReasonPanel items={whyItems()} />
        </div>
      </div>
    </article>
  )
}

function AccountingFactsCard(props: { title: string; rows: Array<[string, string]> }) {
  const rows = createMemo(() => props.rows.filter(([, value]) => value && value !== "-"))
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3">
      <h5 class="mb-3 text-13-semibold text-v2-text-text-base">{props.title}</h5>
      <div class="grid grid-cols-[1fr_1.2fr] gap-x-3 gap-y-2 text-12-regular">
        <For each={rows()}>
          {([label, value]) => (
            <>
              <span class="text-v2-text-text-muted">{label}</span>
              <span class="text-v2-text-text-base">{value}</span>
            </>
          )}
        </For>
      </div>
    </section>
  )
}

function AccountingTextCard(props: { title: string; text?: string }) {
  const language = useLanguage()
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3">
      <h5 class="mb-3 text-13-semibold text-v2-text-text-base">{props.title}</h5>
      <p class="text-12-regular text-v2-text-text-muted">
        {props.text || language.t("cowork.accounting.review.noBusinessData")}
      </p>
    </section>
  )
}

function AccountingLineItemsCard(props: { items: Array<Record<string, unknown>> }) {
  const language = useLanguage()
  return (
    <Show when={props.items.length > 0}>
      <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3">
        <h5 class="mb-3 text-13-semibold text-v2-text-text-base">{language.t("cowork.accounting.review.lineItems")}</h5>
        <div class="space-y-2">
          <For each={props.items.slice(0, 8)}>
            {(item) => (
              <div class="rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-2 text-12-regular">
                <p class="text-v2-text-text-base">
                  {metadataString(item, "description") ||
                    metadataString(item, "name") ||
                    metadataString(item, "item") ||
                    "-"}
                </p>
                <div class="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-v2-text-text-muted md:grid-cols-3">
                  <span>
                    {language.t("cowork.accounting.review.quantity")}: {metadataString(item, "quantity") || "-"}
                  </span>
                  <span>
                    {language.t("cowork.accounting.review.net")}:{" "}
                    {metadataString(item, "net_amount") || metadataString(item, "net") || "-"}
                  </span>
                  <span>
                    {language.t("cowork.accounting.review.vatRate")}: {metadataString(item, "vat_rate") || "-"}
                  </span>
                  <span>
                    {language.t("cowork.accounting.review.discount")}: {metadataString(item, "discount") || "-"}
                  </span>
                  <span class="md:col-span-2">
                    {language.t("cowork.accounting.review.businessPurpose")}:{" "}
                    {metadataString(item, "business_purpose") || "-"}
                  </span>
                </div>
              </div>
            )}
          </For>
        </div>
      </section>
    </Show>
  )
}

function AccountingReasonCard(props: { title: string; items: string[] }) {
  const language = useLanguage()
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3">
      <h5 class="mb-3 text-13-semibold text-v2-text-text-base">{props.title}</h5>
      <For
        each={props.items}
        fallback={
          <p class="text-12-regular text-v2-text-text-muted">{language.t("cowork.accounting.review.noBusinessData")}</p>
        }
      >
        {(item) => (
          <p class="border-b border-v2-border-border-base py-2 text-12-regular text-v2-text-text-muted last:border-b-0">
            {item}
          </p>
        )}
      </For>
    </section>
  )
}

function AccountingReasonList(props: { items: string[] }) {
  return (
    <Show when={props.items.length > 0}>
      <ul class="mt-2 space-y-1 text-12-regular text-v2-text-text-muted">
        <For each={props.items}>
          {(item) => <li class="before:mr-1 before:text-v2-text-text-success before:content-['✓']">{item}</li>}
        </For>
      </ul>
    </Show>
  )
}

function AccountingMatchEvidenceList(props: { evidence: Array<[string, string]>; fallback: string[] }) {
  const language = useLanguage()
  return (
    <Show when={props.evidence.length > 0} fallback={<AccountingReasonList items={props.fallback} />}>
      <div class="mt-3 space-y-2">
        <For each={props.evidence}>
          {([label, value]) => (
            <div class="flex items-start justify-between gap-3 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2 py-1.5 text-12-regular">
              <span class="text-v2-text-text-muted">{matchEvidenceLabel(label, language.t)}</span>
              <span class="text-right text-v2-text-text-base">
                {value || language.t("cowork.accounting.review.notAvailable")}
              </span>
            </div>
          )}
        </For>
      </div>
    </Show>
  )
}

function AccountingEntriesCard(props: { title: string; entries: Array<Record<string, unknown>> }) {
  const language = useLanguage()
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3">
      <h5 class="mb-3 text-13-semibold text-v2-text-text-base">{props.title}</h5>
      <For
        each={props.entries}
        fallback={
          <p class="text-12-regular text-v2-text-text-muted">
            {language.t("cowork.accounting.review.noClosingEntries")}
          </p>
        }
      >
        {(entry) => (
          <div class="grid grid-cols-[.8fr_.8fr_auto_1.2fr] gap-2 border-b border-v2-border-border-base py-2 text-12-regular last:border-b-0">
            <span>{metadataString(entry, "debit_account") || "-"}</span>
            <span>{metadataString(entry, "credit_account") || "-"}</span>
            <span>
              {formatOptionalAmount(metadataNumber(entry, "amount"), metadataString(entry, "currency") || "EUR")}
            </span>
            <span class="text-v2-text-text-muted">{metadataString(entry, "description") || metadataInline(entry)}</span>
          </div>
        )}
      </For>
    </section>
  )
}

function AccountingReasonPanel(props: { items: string[] }) {
  const language = useLanguage()
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3">
      <h5 class="mb-3 text-13-semibold text-v2-text-text-base">{language.t("cowork.accounting.review.why")}</h5>
      <For
        each={props.items}
        fallback={
          <p class="text-12-regular text-v2-text-text-muted">{language.t("cowork.accounting.review.noBusinessData")}</p>
        }
      >
        {(item) => (
          <p class="border-b border-v2-border-border-base py-2 text-12-regular text-v2-text-text-muted last:border-b-0">
            {item}
          </p>
        )}
      </For>
    </section>
  )
}

function AccountingReviewMiniCard(props: { review: CoworkAccountingReview; selected: boolean; onSelect: () => void }) {
  const language = useLanguage()
  return (
    <button
      type="button"
      class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3 text-left"
      classList={{ "bg-v2-background-bg-layer-03": props.selected }}
      onClick={props.onSelect}
    >
      <h5 class="truncate text-13-medium text-v2-text-text-base">
        {documentSourceLabel(props.review.document ?? undefined)}
      </h5>
      <p class="mt-2 text-12-regular text-v2-text-text-muted">
        {language.t("cowork.accounting.review.itemMeta", {
          status: props.review.review_status,
          postings:
            props.review.posting_group?.validation_status ?? language.t("cowork.accounting.postings.notGenerated"),
        })}
      </p>
      <p class="mt-2 text-12-regular text-v2-text-text-muted">
        {formatAccountingAmount(props.review.document as CoworkAccountingDocument)}
      </p>
    </button>
  )
}

function selectedReview(reviews: CoworkAccountingReview[], reviewID: string) {
  return reviews.find((review) => review.id === reviewID)
}

function filterAccountingRecords<T>(records: T[], query: string) {
  if (!query) return records
  return records.filter((record) => JSON.stringify(record).toLowerCase().includes(query))
}

function filterDocumentsByColumns(
  documents: CoworkAccountingDocument[],
  filters: Record<DocumentFilterKey, string[]>,
  translate: (key: string) => string,
) {
  return documents.filter((document) => {
    const values: Record<DocumentFilterKey, string> = {
      type: documentTypeLabel(document.document_type, translate),
      counterparty: document.counterparty_name || "-",
      currency: document.currency || "-",
      status: document.processing_status || "-",
      postings: document.posting_validation_status || document.posting_status || "-",
    }
    return (Object.keys(values) as DocumentFilterKey[]).every((key) => {
      const selected = filters[key] ?? []
      return selected.length === 0 || selected.includes(values[key])
    })
  })
}

function documentColumnFilterOptions(
  documents: CoworkAccountingDocument[],
  translate: (key: string) => string,
): Record<DocumentFilterKey, string[]> {
  return {
    type: uniqueSortedDocumentValues(documents.map((document) => documentTypeLabel(document.document_type, translate))),
    counterparty: uniqueSortedDocumentValues(documents.map((document) => document.counterparty_name || "-")),
    currency: uniqueSortedDocumentValues(documents.map((document) => document.currency || "-")),
    status: uniqueSortedDocumentValues(documents.map((document) => document.processing_status || "-")),
    postings: uniqueSortedDocumentValues(
      documents.map((document) => document.posting_validation_status || document.posting_status || "-"),
    ),
  }
}

function uniqueSortedDocumentValues(values: string[]) {
  return Array.from(new Set(values.filter(Boolean))).sort((left, right) =>
    left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" }),
  )
}

function sortAccountingDocuments(
  documents: CoworkAccountingDocument[],
  key: DocumentSortKey,
  direction: SortDirection,
  translate: (key: string) => string,
) {
  const multiplier = direction === "asc" ? 1 : -1
  return [...documents].sort(
    (left, right) =>
      compareDocumentSortValue(documentSortValue(left, key, translate), documentSortValue(right, key, translate)) *
      multiplier,
  )
}

function documentSortValue(
  document: CoworkAccountingDocument,
  key: DocumentSortKey,
  translate: (key: string) => string,
) {
  if (key === "file") return documentSourceLabel(document)
  if (key === "type") return documentTypeLabel(document.document_type, translate)
  if (key === "counterparty") return document.counterparty_name ?? ""
  if (key === "number") return document.document_number ?? ""
  if (key === "date") return document.document_date ? Date.parse(document.document_date) : 0
  if (key === "currency") return document.currency ?? ""
  if (key === "gross") return document.gross_amount ?? 0
  if (key === "status") return document.processing_status ?? ""
  if (key === "confidence")
    return (
      document.extraction_confidence ??
      document.classification_confidence ??
      document.vat_confidence ??
      document.payment_match_confidence ??
      0
    )
  if (key === "postings") return `${document.posting_validation_status ?? ""} ${document.posting_confidence ?? ""}`
  return ""
}

function compareDocumentSortValue(left: string | number, right: string | number) {
  if (typeof left === "number" && typeof right === "number") return left - right
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" })
}

function documentRowsForCsv(documents: CoworkAccountingDocument[], translate: (key: string) => string) {
  return documents.map((document) => ({
    [translate("cowork.accounting.documents.column.file")]: documentSourceLabel(document),
    [translate("cowork.accounting.documents.column.type")]: documentTypeLabel(document.document_type, translate),
    [translate("cowork.accounting.documents.column.counterparty")]: document.counterparty_name || "",
    [translate("cowork.accounting.documents.column.number")]: document.document_number || "",
    [translate("cowork.accounting.documents.column.date")]: document.document_date
      ? formatRecordDate(document.document_date)
      : "",
    [translate("cowork.accounting.documents.column.currency")]: document.currency || "",
    [translate("cowork.accounting.documents.column.gross")]: document.gross_amount ?? "",
    [translate("cowork.accounting.documents.column.status")]: accountingStatusLabel(
      document.processing_status,
      translate,
    ),
    [translate("cowork.accounting.documents.column.confidence")]: documentConfidence(document),
    [translate("cowork.accounting.documents.column.postings")]: postingStatusLabel(document, translate),
  }))
}

function downloadAccountingCsv(filename: string, rows: Array<Record<string, unknown>>) {
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
  const csv = [headers, ...rows.map((row) => headers.map((header) => accountingCsvCell(row[header])))]
    .map((row) => row.join(","))
    .join("\n")
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function accountingCsvCell(value: unknown) {
  const text = value === undefined || value === null ? "" : String(value)
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

function agentAnalysis(metadata: Record<string, unknown> | undefined) {
  const value = metadata?.agent_analysis
  return isRecord(value) ? value : {}
}

function reviewLineItems(metadata: Record<string, unknown> | undefined) {
  const analysis = agentAnalysis(metadata)
  const candidates = [analysis.line_items, analysis.items, metadata?.line_items, metadata?.items]
  const values = candidates.find(Array.isArray)
  return Array.isArray(values) ? values.filter(isRecord) : []
}

function reviewClosingEntries(review: CoworkAccountingReview, paymentMatch?: CoworkPaymentMatch) {
  const candidates = [
    review.changes_json.proposed_closing_entries,
    review.changes_json.closing_entries,
    agentAnalysis(review.document?.document_metadata).proposed_closing_entries,
    agentAnalysis(review.document?.document_metadata).payment_closing_entries,
    paymentMatch?.payment?.transaction_metadata.proposed_closing_entries,
  ]
  const values = candidates.find(Array.isArray)
  return Array.isArray(values) ? values.filter(isRecord) : []
}

function reviewSourceReferences(
  review: CoworkAccountingReview,
  document: CoworkAccountingDocument | undefined,
  posting: CoworkSuggestedPostingGroup | undefined,
  paymentMatch: CoworkPaymentMatch | undefined,
) {
  const analysis = agentAnalysis(document?.document_metadata)
  const candidates = [
    analysis.source_references,
    analysis.source_refs,
    document?.document_metadata.source_references,
    review.changes_json.source_references,
    posting?.lines
      .flatMap((line) => metadataInline(line.analytics_json.source_reference || line.analytics_json.source_references))
      .filter(Boolean),
    paymentMatch?.match_reasons,
  ]
  return uniqueStrings(candidates.flatMap((value) => valueToStringList(value)))
}

function reviewMatchEvidence(paymentMatch: CoworkPaymentMatch | undefined): Array<[string, string]> {
  if (!paymentMatch) return []
  const metadata = paymentMatch.payment?.transaction_metadata ?? {}
  const evidence = isRecord(metadata.match_evidence) ? metadata.match_evidence : {}
  return [
    [
      "supplier_matched",
      metadataString(evidence, "supplier_matched") || metadataString(evidence, "counterparty_matched"),
    ],
    ["amount_matched", metadataString(evidence, "amount_matched")],
    [
      "invoice_number_matched",
      metadataString(evidence, "invoice_number_matched") || metadataString(evidence, "reference_matched"),
    ],
    ["date_matched", metadataString(evidence, "date_matched")],
    [
      "bank_or_card_matched",
      metadataString(evidence, "bank_or_card_matched") ||
        metadataString(evidence, "card_matched") ||
        metadataString(evidence, "bank_matched"),
    ],
    [
      "confidence_explanation",
      metadataString(evidence, "confidence_explanation") || paymentMatch.match_reasons.join(", "),
    ],
  ].filter(([, value]) => value) as Array<[string, string]>
}

function reviewWhyItems(
  review: CoworkAccountingReview,
  document: CoworkAccountingDocument | undefined,
  posting: CoworkSuggestedPostingGroup | undefined,
  paymentMatch: CoworkPaymentMatch | undefined,
) {
  const analysis = agentAnalysis(document?.document_metadata)
  return [
    review.comment,
    metadataString(review.changes_json, "review_reason"),
    metadataString(analysis, "reasoning_summary"),
    metadataString(analysis, "classification_reason"),
    metadataString(analysis, "vat_reason"),
    posting?.explanation,
    ...(paymentMatch?.match_reasons ?? []),
  ].filter((item): item is string => typeof item === "string" && item.trim().length > 0)
}

function sourcePackReconciliationSections(
  documents: CoworkAccountingDocument[],
  reviews: CoworkAccountingReview[],
  taxReports: CoworkAccountingTaxReport[],
  paymentMatches: CoworkPaymentMatch[],
  translate: (key: string) => string,
) {
  const reconciliationRecords = [
    ...documents.map((document) => agentAnalysis(document.document_metadata).source_pack_reconciliation),
    ...documents.map((document) => document.document_metadata.source_pack_reconciliation),
    ...reviews.map((review) => review.changes_json.source_pack_reconciliation),
    ...taxReports.map((report) => report.report_metadata.source_pack_reconciliation),
  ].filter(isRecord)
  const section = (key: string, titleKey: string) => {
    const values = reconciliationRecords.map((record) => record[key]).filter((value) => value !== undefined)
    const items = uniqueStrings(values.flatMap(valueToStringList))
    const fallback = reconciliationFallbackItems(key, documents, reviews, taxReports, paymentMatches, translate)
    return {
      title: translate(titleKey),
      status: reconciliationStatus(values) || fallback.status,
      items: items.length ? items : fallback.items,
    }
  }
  return [
    section("journal_vs_invoices", "cowork.accounting.reconciliation.journalVsInvoices"),
    section("trial_balance_vs_journal", "cowork.accounting.reconciliation.trialBalanceVsJournal"),
    section("supplier_balances", "cowork.accounting.reconciliation.supplierBalances"),
    section("customer_balances", "cowork.accounting.reconciliation.customerBalances"),
    section("fixed_assets_depreciation", "cowork.accounting.reconciliation.fixedAssets"),
    section("payroll_tsd", "cowork.accounting.reconciliation.payrollTsd"),
    section("vat_kmd", "cowork.accounting.reconciliation.vatKmd"),
    {
      title: translate("cowork.accounting.reconciliation.paymentMatches"),
      status: paymentMatches.length ? `${paymentMatches.length}` : "",
      items: uniqueStrings(paymentMatches.flatMap((match) => match.match_reasons)),
    },
  ]
}

function reconciliationFallbackItems(
  key: string,
  documents: CoworkAccountingDocument[],
  reviews: CoworkAccountingReview[],
  taxReports: CoworkAccountingTaxReport[],
  paymentMatches: CoworkPaymentMatch[],
  translate: (key: string, params?: Record<string, string | number>) => string,
) {
  const byType = documentTypeCounts(documents)
  if (key === "journal_vs_invoices") {
    return {
      status: translate("cowork.accounting.reconciliation.fallback.ready"),
      items: [
        translate("cowork.accounting.reconciliation.fallback.reportDocs", { count: reportDocumentCount(documents) }),
        translate("cowork.accounting.reconciliation.fallback.invoiceDocs", { count: invoiceDocumentCount(documents) }),
        translate("cowork.accounting.reconciliation.fallback.reviewBlockers", { count: reviews.length }),
      ],
    }
  }
  if (key === "trial_balance_vs_journal") {
    return {
      status:
        byType.operation_journal || byType.trial_balance
          ? translate("cowork.accounting.reconciliation.fallback.ready")
          : "",
      items: [
        translate("cowork.accounting.reconciliation.fallback.operationJournals", {
          count: byType.operation_journal ?? 0,
        }),
        translate("cowork.accounting.reconciliation.fallback.trialBalances", { count: byType.trial_balance ?? 0 }),
      ],
    }
  }
  if (key === "supplier_balances")
    return {
      status: byType.supplier_balance ? translate("cowork.accounting.reconciliation.fallback.ready") : "",
      items: [
        translate("cowork.accounting.reconciliation.fallback.supplierBalanceDocs", {
          count: byType.supplier_balance ?? 0,
        }),
      ],
    }
  if (key === "customer_balances")
    return {
      status: byType.customer_balance ? translate("cowork.accounting.reconciliation.fallback.ready") : "",
      items: [
        translate("cowork.accounting.reconciliation.fallback.customerBalanceDocs", {
          count: byType.customer_balance ?? 0,
        }),
      ],
    }
  if (key === "fixed_assets_depreciation") {
    return {
      status:
        byType.fixed_asset_report || byType.depreciation_report
          ? translate("cowork.accounting.reconciliation.fallback.ready")
          : "",
      items: [
        translate("cowork.accounting.reconciliation.fallback.fixedAssetDocs", {
          count: byType.fixed_asset_report ?? 0,
        }),
        translate("cowork.accounting.reconciliation.fallback.depreciationDocs", {
          count: byType.depreciation_report ?? 0,
        }),
      ],
    }
  }
  if (key === "payroll_tsd")
    return {
      status: taxReports.some((report) => report.report_type === "tsd")
        ? translate("cowork.accounting.reconciliation.fallback.ready")
        : "",
      items: taxReports
        .filter((report) => report.report_type === "tsd")
        .map((report) =>
          translate("cowork.accounting.reconciliation.fallback.taxReport", {
            type: report.report_type.toUpperCase(),
            status: report.validation_status,
          }),
        ),
    }
  if (key === "vat_kmd")
    return {
      status: taxReports.some((report) => report.report_type === "kmd")
        ? translate("cowork.accounting.reconciliation.fallback.ready")
        : "",
      items: taxReports
        .filter((report) => report.report_type === "kmd")
        .map((report) =>
          translate("cowork.accounting.reconciliation.fallback.taxReport", {
            type: report.report_type.toUpperCase(),
            status: report.validation_status,
          }),
        ),
    }
  return { status: "", items: uniqueStrings(paymentMatches.flatMap((match) => match.match_reasons)) }
}

function documentTypeCounts(documents: CoworkAccountingDocument[]) {
  return documents.reduce<Record<string, number>>((counts, document) => {
    const key = document.document_type || "unknown"
    counts[key] = (counts[key] ?? 0) + 1
    return counts
  }, {})
}

function invoiceDocumentCount(documents: CoworkAccountingDocument[]) {
  return documents.filter((document) => ["invoice", "receipt", "payment_notice"].includes(document.document_type || ""))
    .length
}

function reportDocumentCount(documents: CoworkAccountingDocument[]) {
  return documents.filter(
    (document) => (document.document_type || "").includes("report") || document.document_type === "operation_journal",
  ).length
}

function isTaxReportArtifact(artifact: CoworkArtifact) {
  const metadata = artifact.artifact_metadata ?? {}
  const title = artifact.title.toLowerCase()
  const type = artifact.artifact_type.toLowerCase()
  const reportType = String(metadata.report_type ?? "").toLowerCase()
  return (
    type === "tax_report" ||
    reportType === "kmd" ||
    reportType === "tsd" ||
    Boolean(metadata.source_tax_report_id) ||
    title.includes("kmd") ||
    title.includes("tsd") ||
    title.includes("tax") ||
    title.includes("reconciliation")
  )
}

function taxArtifactsForReport(artifacts: CoworkArtifact[], report: CoworkAccountingTaxReport) {
  return artifacts.filter((artifact) => {
    const metadata = artifact.artifact_metadata ?? {}
    if (metadata.source_tax_report_id === report.id) return true
    const reportType = String(metadata.report_type ?? "").toLowerCase()
    const reportingPeriod = String(metadata.reporting_period ?? "")
    return (
      reportType === report.report_type.toLowerCase() &&
      (!report.reporting_period || !reportingPeriod || reportingPeriod === report.reporting_period)
    )
  })
}

function isTaxXmlArtifact(artifact: CoworkArtifact) {
  const metadata = artifact.artifact_metadata ?? {}
  const title = artifact.title.toLowerCase()
  const format = artifact.format.toLowerCase()
  const reportType = String(metadata.report_type ?? "").toLowerCase()
  return (
    format === "xml" && (reportType === "kmd" || reportType === "tsd" || title.includes("kmd") || title.includes("tsd"))
  )
}

function reconciliationStatus(values: unknown[]) {
  const statuses = values.flatMap((value) =>
    isRecord(value) ? valueToStringList(value.status ?? value.result ?? value.summary) : [],
  )
  return statuses[0] || ""
}

function valueToStringList(value: unknown): string[] {
  if (typeof value === "string") return value.trim() ? [value] : []
  if (typeof value === "number" || typeof value === "boolean") return [String(value)]
  if (Array.isArray(value)) return value.flatMap(valueToStringList)
  if (isRecord(value)) {
    const preferred = [value.summary, value.description, value.reason, value.status, value.result].flatMap(
      valueToStringList,
    )
    if (preferred.length) return preferred
    return Object.entries(value).flatMap(([key, entry]) => valueToStringList(entry).map((text) => `${key}: ${text}`))
  }
  return []
}

function uniqueStrings(values: string[]) {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)))
}

function lineItemDetails(item: Record<string, unknown>) {
  const details: Record<string, unknown> = {}
  for (const key of [
    "quantity",
    "unit",
    "net_amount",
    "vat_rate",
    "vat_amount",
    "gross_amount",
    "discount",
    "business_purpose",
  ]) {
    if (item[key] !== undefined && item[key] !== null && item[key] !== "") details[key] = item[key]
  }
  return details
}

function metadataString(metadata: Record<string, unknown> | undefined, key: string) {
  const value = metadata?.[key]
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return ""
}

function accountingClientMetadata(metadata: Record<string, unknown>) {
  for (const key of ["accounting_client", "client", "project_client", "customer"]) {
    const value = metadata[key]
    if (isRecord(value)) return value
  }
  return metadata
}

function clientVatNumber(metadata: Record<string, unknown>) {
  return (
    metadataString(metadata, "vat_number") || metadataString(metadata, "vat_id") || metadataString(metadata, "kmkr")
  )
}

function reviewVehicle(document: CoworkAccountingDocument | undefined, analysis: Record<string, unknown>) {
  const metadata = document?.document_metadata
  return (
    metadataString(analysis, "vehicle") ||
    metadataString(analysis, "license_plate") ||
    metadataString(analysis, "licence_plate") ||
    metadataString(analysis, "vehicle_registration") ||
    metadataString(metadata, "vehicle") ||
    metadataString(metadata, "license_plate") ||
    vehiclePlateFromText(
      `${document?.source_filename ?? ""} ${document?.document_number ?? ""} ${metadataInline(metadata)}`,
    )
  )
}

function vehiclePlateFromText(text: string) {
  const match = text.match(/\b\d{3}[A-Z]{3}\b/i)
  return match?.[0].toUpperCase() ?? ""
}

function vatTreatmentLabel(value: string, translate: (key: string) => string) {
  if (!value) return ""
  if (value === "domestic_input_vat") return translate("cowork.accounting.vatTreatment.domesticInput")
  if (value === "domestic_output_vat") return translate("cowork.accounting.vatTreatment.domesticOutput")
  if (value === "eu_reverse_charge") return translate("cowork.accounting.vatTreatment.euReverseCharge")
  if (value === "non_eu_import") return translate("cowork.accounting.vatTreatment.nonEuImport")
  if (value === "exempt") return translate("cowork.accounting.vatTreatment.exempt")
  if (value === "zero_rated") return translate("cowork.accounting.vatTreatment.zeroRated")
  if (value === "no_vat") return translate("cowork.accounting.vatTreatment.noVat")
  if (value === "needs_review") return translate("cowork.accounting.vatTreatment.needsReview")
  return value.replaceAll("_", " ")
}

function metadataNumber(metadata: Record<string, unknown> | undefined, key: string) {
  const value = metadata?.[key]
  if (typeof value === "number") return value
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.replace(",", "."))
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

function metadataInline(value: unknown): string {
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  if (Array.isArray(value)) return value.map(metadataInline).filter(Boolean).join(", ")
  if (!isRecord(value)) return ""
  return Object.entries(value)
    .filter(([, entry]) => entry !== undefined && entry !== null && entry !== "")
    .map(([key, entry]) => `${key}: ${metadataInline(entry) || String(entry)}`)
    .join(", ")
}

function matchEvidenceLabel(key: string, translate: (key: string) => string) {
  if (key === "supplier_matched") return translate("cowork.accounting.review.match.supplier")
  if (key === "amount_matched") return translate("cowork.accounting.review.match.amount")
  if (key === "invoice_number_matched") return translate("cowork.accounting.review.match.invoice")
  if (key === "date_matched") return translate("cowork.accounting.review.match.date")
  if (key === "bank_or_card_matched") return translate("cowork.accounting.review.match.bankCard")
  if (key === "confidence_explanation") return translate("cowork.accounting.review.match.confidence")
  return key
}

function accountingSummaryKey(tab: string) {
  if (tab === "review") return "review_items"
  if (tab === "ledger") return "ledger_entries"
  if (tab === "tax") return "tax_reports"
  if (tab === "emta") return "emta_submissions"
  return tab
}

function documentSourceLabel(document: CoworkAccountingDocument | undefined) {
  return document?.source_filename || document?.source_file_id || "-"
}

function documentConfidence(document: CoworkAccountingDocument) {
  const confidence =
    document.extraction_confidence ??
    document.classification_confidence ??
    document.vat_confidence ??
    document.payment_match_confidence
  return confidence === undefined || confidence === null ? "-" : `${confidence}%`
}

function formatAccountingAmount(document: CoworkAccountingDocument) {
  if (document.gross_amount === undefined || document.gross_amount === null) return "-"
  const suffix = document.currency ? ` ${document.currency}` : ""
  return `${new Intl.NumberFormat().format(document.gross_amount)}${suffix}`
}

function formatMoney(amount: number | null | undefined, currency = "EUR") {
  if (amount === undefined || amount === null) return ""
  return `${new Intl.NumberFormat().format(amount)} ${currency}`
}

function formatOptionalAmount(amount: number | null | undefined, currency?: string | null, rate?: number | null) {
  if (amount === undefined || amount === null) return "-"
  const suffix = currency ? ` ${currency}` : ""
  const rateSuffix = rate === undefined || rate === null ? "" : ` (${rate}%)`
  return `${new Intl.NumberFormat().format(amount)}${suffix}${rateSuffix}`
}

function formatPaymentAmount(payment: CoworkPaymentTransaction) {
  const suffix = payment.currency ? ` ${payment.currency}` : ""
  return `${new Intl.NumberFormat().format(payment.amount)}${suffix}`
}

function isPayableDocument(document: CoworkAccountingDocument) {
  const type = document.document_type || ""
  return ["invoice", "receipt", "payment_notice", "credit_note"].includes(type) && (document.gross_amount ?? 0) > 0
}

function documentDueDate(document: CoworkAccountingDocument) {
  const analysis = agentAnalysis(document.document_metadata)
  return (
    document.due_date ||
    metadataString(analysis, "due_date") ||
    metadataString(analysis, "payment_due_date") ||
    metadataString(analysis, "payment_deadline") ||
    metadataString(analysis, "maksetahtaeg") ||
    metadataString(document.document_metadata, "due_date") ||
    metadataString(document.document_metadata, "payment_due_date") ||
    extractedDueDate(document.document_metadata)
  )
}

function extractedDueDate(metadata: Record<string, unknown>) {
  const analysis = agentAnalysis(metadata)
  const values = [
    ...metadataList(analysis, "key_values"),
    ...metadataList(analysis, "display_key_values"),
    ...metadataList(metadata, "key_values"),
    ...metadataList(metadata, "display_key_values"),
  ]
  for (const item of values) {
    if (!isRecord(item)) continue
    const label = ledgerAnalytics(
      metadataString(item, "field"),
      metadataString(item, "label"),
      metadataString(item, "name"),
      metadataString(item, "key"),
      metadataString(item, "semantic_key"),
    ).toLowerCase()
    if (!isDueDateLabel(label)) continue
    const value =
      metadataString(item, "value_normalized") ||
      metadataString(item, "normalized_value") ||
      metadataString(item, "value") ||
      metadataString(item, "value_raw") ||
      metadataString(item, "raw_value")
    const normalized = normalizeDocumentDate(value)
    if (normalized) return normalized
  }
  return ""
}

function metadataList(metadata: Record<string, unknown>, key: string) {
  const value = metadata[key]
  return Array.isArray(value) ? value : []
}

function isDueDateLabel(value: string) {
  return [
    "due_date",
    "payment_due_date",
    "payment_deadline",
    "maksekuupäev",
    "maksekuupaev",
    "maksetähtaeg",
    "maksetahtaeg",
    "срок оплаты",
    "дата оплаты",
  ].some((label) => value.includes(label))
}

function normalizeDocumentDate(value: string) {
  if (!value) return ""
  const trimmed = value.trim()
  const estonian = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
  if (estonian) return `${estonian[3]}-${estonian[2].padStart(2, "0")}-${estonian[1].padStart(2, "0")}`
  return trimmed
}

function daysOverdueLabel(
  date: string | null | undefined,
  translate: (key: string, params?: Record<string, string | number>) => string,
) {
  if (!date) return translate("cowork.accounting.payments.dueDateMissing")
  const parsed = Date.parse(date)
  if (!Number.isFinite(parsed)) return translate("cowork.accounting.payments.dueDateMissing")
  const days = Math.floor((Date.now() - parsed) / 86_400_000)
  if (days <= 0) return translate("cowork.accounting.payments.notOverdue")
  return translate("cowork.accounting.payments.days", { count: days })
}

function compactMatchReasons(reasons: string[]) {
  if (reasons.length === 0) return ""
  return reasons.slice(0, 4).join(" + ")
}

function accountWithName(account: string | null | undefined, name: string | null | undefined) {
  if (!account) return ""
  return name ? `${account} ${name}` : account
}

function ledgerAnalytics(...values: Array<string | null | undefined>) {
  return values.filter((value): value is string => !!value?.trim()).join("  ")
}

function ledgerJournalInfo(row: CoworkLedgerJournalLine) {
  return ledgerAnalytics(row.document_reference, row.source_filename, row.explanation)
}

function ledgerMetadataValue(value: Record<string, unknown> | null | undefined, key: string) {
  const entry = value?.[key]
  if (entry === undefined || entry === null) return ""
  if (typeof entry === "object") return JSON.stringify(entry)
  return String(entry)
}

function formatLedgerDate(value: string | null | undefined) {
  if (!value) return ""
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  const day = String(parsed.getDate()).padStart(2, "0")
  const month = String(parsed.getMonth() + 1).padStart(2, "0")
  const year = String(parsed.getFullYear()).slice(-2)
  return `${day}.${month}.${year}`
}

function formatLedgerNumber(amount: number | null | undefined) {
  if (amount === undefined || amount === null) return ""
  if (amount === 0) return ""
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)
}

function ledgerStatusLabel(status: string, translate: (key: string) => string) {
  if (status === "draft") return translate("cowork.accounting.ledger.status.draft")
  if (status === "needs_review") return translate("cowork.accounting.ledger.status.needsReview")
  if (status === "approved") return translate("cowork.accounting.ledger.status.approved")
  if (status === "finalized") return translate("cowork.accounting.ledger.status.finalized")
  if (status === "error") return translate("cowork.accounting.ledger.status.error")
  return status
}

function accountingStatusLabel(status: string, translate: (key: string) => string) {
  if (status === "created") return translate("cowork.accounting.status.created")
  if (status === "needs_extraction") return translate("cowork.accounting.status.needsExtraction")
  if (status === "processed") return translate("cowork.accounting.status.processed")
  if (status === "failed") return translate("cowork.accounting.status.failed")
  return status
}

function documentTypeLabel(type: string | null | undefined, translate: (key: string) => string) {
  if (!type) return ""
  const key = `cowork.accounting.documentType.${type}`
  const translated = translate(key)
  if (translated && translated !== key) return translated
  return type.replaceAll("_", " ").replace(/^./, (char) => char.toUpperCase())
}

function reportMetadataNumber(metadata: Record<string, unknown>, key: string) {
  const value = metadata[key]
  return typeof value === "number" ? value : Number(value ?? 0)
}

function taxReportTotalAmount(report: CoworkAccountingTaxReport) {
  if (report.report_type === "tsd") {
    const tsd = report.report_metadata.tsd as Record<string, unknown> | undefined
    const employeePayments = reportMetadataNumber(tsd ?? {}, "employee_payments_total")
    return `${new Intl.NumberFormat().format(employeePayments)} EUR`
  }
  const kmd = report.report_metadata.kmd as Record<string, unknown> | undefined
  const vatPayable = reportMetadataNumber(kmd ?? {}, "vat_payable")
  return `${new Intl.NumberFormat().format(vatPayable)} EUR`
}

function postingStatusLabel(document: CoworkAccountingDocument, translate: (key: string) => string) {
  if (!document.posting_group_id) return translate("cowork.accounting.postings.notGenerated")
  const status = document.posting_validation_status || document.posting_status || "suggested"
  const confidence =
    document.posting_confidence === undefined || document.posting_confidence === null
      ? ""
      : ` (${document.posting_confidence}%)`
  if (status === "passed") return `${translate("cowork.accounting.postings.validated")}${confidence}`
  if (status === "failed") return `${translate("cowork.accounting.postings.needsReview")}${confidence}`
  return `${translate("cowork.accounting.postings.suggested")}${confidence}`
}

export function FilesTab(props: {
  workflow: string
  filter: string
  setFilter: (value: string) => void
  files: CoworkFile[]
  totalFiles: number | undefined
  activeFile?: CoworkFile
  periodID?: string | null
  selectFile: (fileID: string) => void
  hasContext: boolean
  onDelete: (file: CoworkFile) => Promise<void>
}) {
  const language = useLanguage()
  return (
    <div class="flex min-h-0 flex-1">
      <aside class="flex w-[280px] shrink-0 flex-col border-r border-v2-border-border-base">
        <div class="flex min-h-12 items-center justify-between gap-2 px-4">
          <div class="min-w-0">
            <p class="text-14-medium text-v2-text-text-base">{language.t("cowork.files.panel.title")}</p>
            <Show when={props.totalFiles !== undefined}>
              <p class="text-12-regular text-v2-text-text-muted">
                {language.t("cowork.files.panel.count", { count: props.totalFiles ?? 0 })}
              </p>
            </Show>
            <Show when={props.workflow}>
              <p class="truncate text-12-regular text-v2-text-text-muted">{props.workflow}</p>
            </Show>
          </div>
        </div>
        <div class="px-3 pb-2">
          <input
            class="h-8 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-13-regular outline-none"
            placeholder={language.t("cowork.files.panel.filter")}
            value={props.filter}
            onInput={(event) => props.setFilter(event.currentTarget.value)}
          />
        </div>
        <div class="min-h-0 flex-1 overflow-auto px-2 pb-2">
          <For each={props.files} fallback={<EmptyList hasContext={props.hasContext} />}>
            {(file) => (
              <FileRow file={file} active={props.activeFile?.id === file.id} onClick={() => props.selectFile(file.id)} />
            )}
          </For>
        </div>
      </aside>
      <main class="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <FileInspection
          file={props.activeFile}
          periodID={props.periodID}
          empty={<EmptyPreview />}
          document={(file) => <SelectedFilePreview file={file} onDelete={props.onDelete} />}
        />
      </main>
    </div>
  )
}

function SelectedFilePreview(props: { file: CoworkFile; onDelete: (file: CoworkFile) => Promise<void> }) {
  const language = useLanguage()
  // Keep metadata and content reads inside the preview's boundary, not the Workflow shell.
  const [detail] = createResource(() => props.file, (file) => getFile(file.project_id, file.id, file.company_id))
  return (
    <Show when={detail()} keyed>
      {(file) => (
        <div class="mx-auto max-w-4xl px-8 py-6">
          <div class="mb-6 flex items-start justify-between gap-4">
            <div class="min-w-0">
              <h2 class="truncate text-20-semibold text-v2-text-text-base">{file.filename}</h2>
              <p class="mt-1 text-13-regular text-v2-text-text-muted">
                {language.t("cowork.files.panel.fileMeta", { size: file.size, type: file.content_type })}
              </p>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <button
                type="button"
                class="flex size-8 items-center justify-center rounded-md border border-v2-border-border-base text-v2-icon-icon-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-icon-icon-base"
                aria-label={language.t("cowork.files.panel.download")}
                title={language.t("cowork.files.panel.download")}
                onClick={() => downloadFile(file)}
              >
                <IconV2 name="download" size="small" />
              </button>
              <button
                type="button"
                class="flex size-8 items-center justify-center rounded-md border border-v2-border-border-base text-v2-icon-icon-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-icon-icon-base"
                aria-label={language.t("cowork.files.panel.delete")}
                title={language.t("cowork.files.panel.delete")}
                onClick={() => void props.onDelete(file)}
              >
                <IconV2 name="trash" size="small" />
              </button>
            </div>
          </div>
          <FilePreview file={file} />
        </div>
      )}
    </Show>
  )
}

function FilePreview(props: { file: Awaited<ReturnType<typeof getFile>> }) {
  if (props.file.content_type.startsWith("image/")) return <ImageFilePreview file={props.file} />
  if (isPdfFile(props.file)) return <PdfFilePreview file={props.file} />
  if (isSpreadsheetFile(props.file)) return <SpreadsheetFilePreview file={props.file} />
  if (isCsvFile(props.file)) return <TextFilePreview file={props.file} format="csv" />
  if (isJsonFile(props.file)) return <TextFilePreview file={props.file} format="json" />
  if (isPresentationFile(props.file)) return <PresentationFilePreview file={props.file} />
  return <FilePartsPreview file={props.file} />
}

function TextFilePreview(props: { file: Awaited<ReturnType<typeof getFile>>; format: "csv" | "json" }) {
  const language = useLanguage()
  const [text] = createResource(
    () => ({ projectID: props.file.project_id, fileID: props.file.id, companyID: props.file.company_id }),
    async (input) => (await getFileContentBlob(input.projectID, input.fileID, input.companyID)).text(),
  )
  return (
    <FilePreviewSection file={props.file}>
      <Show when={!text.loading} fallback={<TextPreview text={language.t("cowork.files.panel.loadingPreview")} />}>
        {props.format === "csv" ? <CsvTextPreview text={text() ?? ""} /> : <JsonTextPreview text={text() ?? ""} />}
      </Show>
    </FilePreviewSection>
  )
}

function PresentationFilePreview(props: { file: Awaited<ReturnType<typeof getFile>> }) {
  const language = useLanguage()
  const [blob] = createResource(
    () => ({ projectID: props.file.project_id, fileID: props.file.id, companyID: props.file.company_id }),
    (input) => getFileContentBlob(input.projectID, input.fileID, input.companyID),
  )
  return (
    <FilePreviewSection file={props.file}>
      <Show when={blob()} fallback={<TextPreview text={language.t("cowork.files.panel.loadingPreview")} />}>
        {(value) => (
          <PptxPreview
            blob={value()}
            empty={language.t("cowork.files.panel.noPreview")}
            loading={language.t("cowork.files.panel.loadingPreview")}
            slideLabel={(index) => language.t("cowork.files.panel.slide", { index })}
          />
        )}
      </Show>
    </FilePreviewSection>
  )
}

function PptxPreview(props: { blob: Blob; empty: string; loading: string; slideLabel: (index: number) => string }) {
  let container: HTMLDivElement | undefined
  const [state, setState] = createStore({ failed: false, loading: true, slides: [] as PptxSlide[] })
  createEffect(() => {
    setState({ failed: false, loading: true, slides: [] })
    void renderPptx(props.blob, container)
      .then(() => setState("loading", false))
      .catch(async () => {
        setState({ failed: true, loading: false, slides: await extractPptxSlides(props.blob).catch(() => []) })
      })
  })
  return (
    <Show
      when={!state.failed}
      fallback={<PresentationDeckPreview slides={state.slides} empty={props.empty} slideLabel={props.slideLabel} />}
    >
      <div class="max-h-[720px] overflow-y-auto overflow-x-hidden rounded-md bg-[#f3f4f6] p-4 [scrollbar-color:#777_transparent] [scrollbar-width:thin]">
        <Show when={state.loading}>
          <TextPreview text={props.loading} />
        </Show>
        <div ref={container} class="mx-auto max-w-full" style={{ width: "min(100%, 640px)" }} />
      </div>
    </Show>
  )
}

function PresentationDeckPreview(props: { slides: PptxSlide[]; empty: string; slideLabel: (index: number) => string }) {
  if (props.slides.length === 0) return <TextPreview text={props.empty} />
  return (
    <div class="max-h-[720px] space-y-4 overflow-auto rounded-md bg-[#202020] p-4 [scrollbar-color:#777_transparent] [scrollbar-width:thin]">
      <For each={props.slides}>
        {(slide, index) => (
          <article class="mx-auto flex min-h-[360px] max-w-4xl flex-col rounded-xl bg-white p-8 text-[#111827] shadow-lg">
            <div class="mb-5 text-12-medium uppercase tracking-[0.2em] text-[#64748b]">
              {props.slideLabel(index() + 1)}
            </div>
            <Show when={slide.title}>
              <h4 class="mb-5 text-28-semibold leading-tight text-[#0f172a]">{slide.title}</h4>
            </Show>
            <div class="space-y-3 text-16-regular leading-relaxed text-[#1f2937]">
              <For each={slide.lines}>{(line) => <p>{line}</p>}</For>
            </div>
          </article>
        )}
      </For>
    </div>
  )
}

function FilePreviewSection(props: { file: Awaited<ReturnType<typeof getFile>>; children: JSX.Element }) {
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="truncate text-14-medium text-v2-text-text-base">{props.file.filename}</h3>
        <span class="shrink-0 text-12-regular text-v2-text-text-muted">{props.file.content_type}</span>
      </div>
      {props.children}
    </section>
  )
}

function PdfFilePreview(props: { file: Awaited<ReturnType<typeof getFile>> }) {
  const language = useLanguage()
  const [blob] = createResource(
    () => ({ projectID: props.file.project_id, fileID: props.file.id, companyID: props.file.company_id }),
    (input) => getFileContentBlob(input.projectID, input.fileID, input.companyID),
  )
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="truncate text-14-medium text-v2-text-text-base">{props.file.filename}</h3>
        <span class="shrink-0 text-12-regular text-v2-text-text-muted">{props.file.content_type}</span>
      </div>
      <Show when={blob()} fallback={<TextPreview text={language.t("cowork.files.panel.loadingPreview")} />}>
        {(value) => (
          <PdfBlobPreview blob={value()} unavailable={language.t("cowork.pipeline.report.previewUnavailable")} />
        )}
      </Show>
    </section>
  )
}

function SpreadsheetFilePreview(props: { file: Awaited<ReturnType<typeof getFile>> }) {
  const language = useLanguage()
  const [blob] = createResource(
    () => ({ projectID: props.file.project_id, fileID: props.file.id, companyID: props.file.company_id }),
    (input) => getFileContentBlob(input.projectID, input.fileID, input.companyID),
  )
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="truncate text-14-medium text-v2-text-text-base">{props.file.filename}</h3>
        <span class="shrink-0 text-12-regular text-v2-text-text-muted">{props.file.content_type}</span>
      </div>
      <Show when={blob()} fallback={<TextPreview text={language.t("cowork.files.panel.loadingPreview")} />}>
        {(value) => <SpreadsheetPreview blob={value()} />}
      </Show>
    </section>
  )
}

function isSpreadsheetFile(file: { filename: string; content_type: string }) {
  return /spreadsheet|excel/i.test(file.content_type) || /\.xlsx?$/i.test(file.filename)
}

function isPdfFile(file: { filename: string; content_type: string }) {
  return file.content_type === "application/pdf" || /\.pdf$/i.test(file.filename)
}

function isCsvFile(file: { filename: string; content_type: string }) {
  return /csv/i.test(file.content_type) || /\.csv$/i.test(file.filename)
}

function isJsonFile(file: { filename: string; content_type: string }) {
  return /json/i.test(file.content_type) || /\.json$/i.test(file.filename)
}

function isPresentationFile(file: { filename: string; content_type: string }) {
  return /presentation|powerpoint/i.test(file.content_type) || /\.pptx$/i.test(file.filename)
}

function downloadFile(file: CoworkFile) {
  void getFileContentBlob(file.project_id, file.id, file.company_id).then((blob) => {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = file.filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  })
}

function FilePartsPreview(props: { file: Awaited<ReturnType<typeof getFile>> }) {
  const language = useLanguage()
  return (
    <For
      each={props.file.parts}
      fallback={<p class="text-14-regular text-v2-text-text-muted">{language.t("cowork.files.panel.noPreview")}</p>}
    >
      {(part) => (
        <section class="mb-4 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
          <div class="mb-3 flex items-center justify-between gap-3">
            <h3 class="text-14-medium text-v2-text-text-base">{part.name}</h3>
            <span class="text-12-regular text-v2-text-text-muted">{part.part_type}</span>
          </div>
          <Show
            when={sheetRows(part)}
            fallback={<TextPreview text={part.preview_text || language.t("cowork.files.panel.noPreview")} />}
          >
            {(rows) => <CsvPreview rows={rows()} title={props.file.filename} />}
          </Show>
        </section>
      )}
    </For>
  )
}

function ImageFilePreview(props: { file: CoworkFile }) {
  const language = useLanguage()
  const [url] = createResource(
    () => ({ projectID: props.file.project_id, fileID: props.file.id, companyID: props.file.company_id }),
    async (input) => URL.createObjectURL(await getFileContentBlob(input.projectID, input.fileID, input.companyID)),
  )
  createEffect(() => {
    const value = url()
    if (!value) return
    onCleanup(() => URL.revokeObjectURL(value))
  })
  return (
    <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="truncate text-14-medium text-v2-text-text-base">{props.file.filename}</h3>
        <span class="shrink-0 text-12-regular text-v2-text-text-muted">{props.file.content_type}</span>
      </div>
      <Show when={url()} fallback={<TextPreview text={language.t("cowork.files.panel.loadingPreview")} />}>
        {(src) => (
          <img
            src={src()}
            alt={props.file.filename}
            class="max-h-[70vh] max-w-full rounded-md border border-v2-border-border-base object-contain"
          />
        )}
      </Show>
    </section>
  )
}

function PipelineTabButton(props: { active: boolean; label: string; onClick: () => void; complete?: boolean; working?: boolean }) {
  return (
    <button
      class="rounded-md px-3 py-1.5 text-13-medium text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base data-[active=true]:bg-v2-background-bg-layer-03 data-[active=true]:text-v2-text-text-base"
      data-active={props.active}
      onClick={props.onClick}
    >
      {props.label}
      <WorkflowTabIndicator complete={props.complete} working={props.working} />
    </button>
  )
}

function WorkflowPanelToggle(props: { collapsed: boolean; controls: string; onToggle: () => void }) {
  const language = useLanguage()
  const label = () => language.t(props.collapsed ? "cowork.navigation.expand" : "cowork.navigation.collapse")
  return (
    <IconButtonV2
      size="small"
      variant="ghost"
      icon={<IconV2 name="sidebar-right" classList={{ "rotate-180": !props.collapsed }} />}
      aria-label={label()}
      title={label()}
      aria-controls={props.controls}
      aria-expanded={!props.collapsed}
      onClick={props.onToggle}
    />
  )
}

export function RecordsTab(props: {
  title: string
  records: Array<CoworkExtraction | CoworkTransformation>
  kind: "extraction" | "transformation"
  files?: CoworkFile[]
}) {
  const language = useLanguage()
  const [store, setStore] = createStore({ query: "", status: "", selectedID: "", panelCollapsed: true })
  const panelID = `cowork-${props.kind}-records-panel`
  const records = createMemo(() =>
    [...props.records].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)),
  )
  const statuses = createMemo(() =>
    Array.from(
      new Set(
        records()
          .map((record) => record.status)
          .filter(Boolean),
      ),
    ),
  )
  const filtered = createMemo(() => {
    const query = store.query.trim().toLowerCase()
    return records().filter((record) => {
      const matchesStatus = !store.status || record.status === store.status
      const matchesQuery = !query || recordSearchText(record, props.kind, props.files ?? []).includes(query)
      return matchesStatus && matchesQuery
    })
  })
  const selected = createMemo(
    () =>
      filtered().find((record) => record.id === store.selectedID) ??
      filtered().find((record) => recordTableCount(record, props.kind) > 0) ??
      filtered()[0],
  )

  createEffect(() => {
    const record = selected()
    if (record && record.id !== store.selectedID) setStore("selectedID", record.id)
  })

  return (
    <div
      class="grid min-h-0 flex-1 overflow-hidden"
      classList={{
        "grid-cols-[320px_minmax(0,1fr)]": !store.panelCollapsed,
        "grid-cols-[minmax(0,1fr)]": store.panelCollapsed,
      }}
    >
      <Show when={!store.panelCollapsed}>
        <aside
          id={panelID}
          class="flex min-h-0 flex-col border-r border-v2-border-border-base bg-v2-background-bg-base p-4"
        >
        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 class="text-18-semibold text-v2-text-text-base">{props.title}</h2>
            <p class="mt-1 text-12-regular text-v2-text-text-muted">
              {language.t("cowork.pipeline.records.count", { count: filtered().length })}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <Show when={records()[0]}>
              {(record) => <StatusBadge status={record().status} label={language.t("cowork.pipeline.records.latest")} />}
            </Show>
            <WorkflowPanelToggle
              collapsed={store.panelCollapsed}
              controls={panelID}
              onToggle={() => setStore("panelCollapsed", true)}
            />
          </div>
        </div>
        <div class="mb-3 flex shrink-0 flex-col gap-2">
          <input
            class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-13-regular text-v2-text-text-base outline-none"
            value={store.query}
            placeholder={language.t("cowork.pipeline.records.search")}
            onInput={(event) => setStore("query", event.currentTarget.value)}
          />
          <select
            class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-13-regular text-v2-text-text-base outline-none"
            value={store.status}
            onChange={(event) => setStore("status", event.currentTarget.value)}
          >
            <option value="">{language.t("cowork.pipeline.records.allStatuses")}</option>
            <For each={statuses()}>{(status) => <option value={status}>{status}</option>}</For>
          </select>
        </div>
        <div class="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
          <For
            each={filtered()}
            fallback={
              <p class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4 text-13-regular text-v2-text-text-muted">
                {language.t(`cowork.pipeline.${props.kind}.empty`)}
              </p>
            }
          >
            {(record, index) => (
              <button
                type="button"
                class="w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3 text-left hover:bg-v2-background-bg-layer-02"
                classList={{ "border-blue-400/60 bg-v2-background-bg-layer-03": selected()?.id === record.id }}
                onClick={() => setStore("selectedID", record.id)}
              >
                <div class="mb-2 flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <h3 class="truncate text-13-medium text-v2-text-text-base">{recordTitle(record, props.kind)}</h3>
                    <p class="mt-1 truncate text-12-regular text-v2-text-text-muted">
                      {recordSourceLabel(record, props.kind, props.files ?? [])}
                    </p>
                  </div>
                  <Show when={index() === 0}>
                    <span class="shrink-0 rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] text-blue-300">
                      {language.t("cowork.pipeline.records.latest")}
                    </span>
                  </Show>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <StatusBadge status={record.status} />
                  <span class="text-11-regular text-v2-text-text-muted">
                    {recordTableCount(record, props.kind)} {language.t("cowork.pipeline.records.tables")}
                  </span>
                  <span class="text-11-regular text-v2-text-text-muted">{formatRecordDate(record.created_at)}</span>
                </div>
              </button>
            )}
          </For>
        </div>
        </aside>
      </Show>
      <main class="min-h-0 overflow-auto p-6">
        <Show when={store.panelCollapsed}>
          <div class="mb-3">
            <WorkflowPanelToggle
              collapsed={store.panelCollapsed}
              controls={panelID}
              onToggle={() => setStore("panelCollapsed", false)}
            />
          </div>
        </Show>
        <Show
          when={selected()}
          fallback={
            <p class="text-14-regular text-v2-text-text-muted">{language.t(`cowork.pipeline.${props.kind}.empty`)}</p>
          }
        >
          {(record) => (
            <RecordInspector record={record()} records={filtered()} kind={props.kind} files={props.files ?? []} />
          )}
        </Show>
      </main>
    </div>
  )
}

function RecordInspector(props: {
  record: CoworkExtraction | CoworkTransformation
  records: Array<CoworkExtraction | CoworkTransformation>
  kind: "extraction" | "transformation"
  files: CoworkFile[]
}) {
  const language = useLanguage()
  const tableCount = createMemo(() =>
    props.kind === "extraction"
      ? extractionTables(
          props.record as CoworkExtraction,
          (props.record as CoworkExtraction).file_id ? undefined : (props.records as CoworkExtraction[]),
        ).length
      : recordTableCount(props.record, props.kind),
  )
  return (
    <div class="mx-auto max-w-5xl">
      <section class="mb-4 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
        <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <h2 class="truncate text-20-semibold text-v2-text-text-base">{recordTitle(props.record, props.kind)}</h2>
            <p class="mt-1 text-13-regular text-v2-text-text-muted">{recordSubtitle(props.record, props.kind)}</p>
            <Show when={props.kind === "extraction"}>
              <p class="mt-1 text-13-regular text-v2-text-text-muted">
                {recordSourceLabel(props.record, props.kind, props.files)}
              </p>
            </Show>
          </div>
          <StatusBadge status={props.record.status} />
        </div>
        <div class="grid gap-3 sm:grid-cols-4">
          <MetricCard label={language.t("cowork.pipeline.records.metric.tables")} value={String(tableCount())} />
          <MetricCard label={language.t("cowork.pipeline.records.metric.status")} value={props.record.status} />
          <Show when={props.kind === "extraction"}>
            <MetricCard
              label={language.t("cowork.pipeline.records.metric.sourceFile")}
              value={recordSourceLabel(props.record, props.kind, props.files)}
            />
          </Show>
          <MetricCard
            label={language.t("cowork.pipeline.records.metric.created")}
            value={formatRecordDate(props.record.created_at)}
          />
          <MetricCard
            label={language.t("cowork.pipeline.records.metric.updated")}
            value={formatRecordDate(props.record.updated_at)}
          />
        </div>
      </section>
      {props.kind === "extraction" ? (
        <Show
          when={(props.record as CoworkExtraction).file_id}
          fallback={
            <ExtractionRecord record={props.record as CoworkExtraction} records={props.records as CoworkExtraction[]} />
          }
        >
          <Show when={props.record} keyed>
            {(record) => <ExtractionDataFields record={record as CoworkExtraction} files={props.files} />}
          </Show>
        </Show>
      ) : (
        <TransformationRecord record={props.record as CoworkTransformation} />
      )}
    </div>
  )
}

function MetricCard(props: { label: string; value: string }) {
  return (
    <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
      <div class="text-15-medium text-v2-text-text-base">{props.value}</div>
      <div class="mt-1 text-11-regular text-v2-text-text-muted">{props.label}</div>
    </div>
  )
}

function StatusBadge(props: { status: string; label?: string }) {
  return (
    <span class="inline-flex w-fit rounded-full border border-v2-border-border-base px-2 py-0.5 text-11-medium text-v2-text-text-muted">
      {props.label ? `${props.label} · ${props.status}` : props.status}
    </span>
  )
}

function recordTitle(record: CoworkExtraction | CoworkTransformation, kind: "extraction" | "transformation") {
  if (kind === "extraction") return (record as CoworkExtraction).schema_name || "Extraction"
  return (record as CoworkTransformation).instructions || "Transformation"
}

function recordSubtitle(record: CoworkExtraction | CoworkTransformation, kind: "extraction" | "transformation") {
  if (kind === "extraction") return (record as CoworkExtraction).prompt || record.id
  return stringValue((record as CoworkTransformation).output_json.summary) || record.id
}

function recordTableCount(record: CoworkExtraction | CoworkTransformation, kind: "extraction" | "transformation") {
  return kind === "extraction"
    ? extractionTables(record as CoworkExtraction).length
    : outputTables(record as CoworkTransformation).length
}

function recordSourceLabel(
  record: CoworkExtraction | CoworkTransformation,
  kind: "extraction" | "transformation",
  files: CoworkFile[],
) {
  if (kind !== "extraction") return recordSubtitle(record, kind)
  const fileID = (record as CoworkExtraction).file_id
  if (fileID) return files.find((file) => file.id === fileID)?.filename || fileID
  const sources = extractionSourceLabels(record as CoworkExtraction, files)
  if (sources.length === 1) return sources[0]
  if (sources.length > 1) return `${sources.length} source files`
  return "Multiple files"
}

function extractionSourceLabels(record: CoworkExtraction, files: CoworkFile[]) {
  const values = sourceValues(record.result_json).filter(Boolean)
  const fileIDs = new Set(files.map((file) => file.id))
  const filenames = new Set(files.map((file) => file.filename))
  const labels = values.flatMap((value) => {
    if (fileIDs.has(value)) return [files.find((file) => file.id === value)?.filename || value]
    if (filenames.has(value)) return [value]
    return []
  })
  return Array.from(new Set(labels))
}

function sourceValues(value: unknown): string[] {
  if (typeof value === "string") return [value]
  if (Array.isArray(value)) return value.flatMap(sourceValues)
  if (!isRecord(value)) return []
  return Object.entries(value).flatMap(([key, item]) => {
    const normalized = key.toLowerCase()
    if (
      [
        "file_id",
        "file_ids",
        "filename",
        "filenames",
        "source_file",
        "source_files",
        "source_filename",
        "source_filenames",
      ].includes(normalized)
    )
      return sourceValues(item)
    return sourceValues(item)
  })
}

function recordSearchText(
  record: CoworkExtraction | CoworkTransformation,
  kind: "extraction" | "transformation",
  files: CoworkFile[],
) {
  return `${recordTitle(record, kind)} ${recordSubtitle(record, kind)} ${recordSourceLabel(record, kind, files)} ${record.status} ${JSON.stringify(kind === "extraction" ? (record as CoworkExtraction).result_json : (record as CoworkTransformation).output_json)}`.toLowerCase()
}

function formatRecordDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return formatDateTime(date)
}

function ExtractionRecord(props: { record: CoworkExtraction; records: CoworkExtraction[] }) {
  const language = useLanguage()
  const [store, setStore] = createStore({ tableIndex: 0 })
  const tables = () => extractionTables(props.record, props.records)
  const selectedTable = createMemo(() => tables()[store.tableIndex] ?? tables()[0])
  const totalRows = createMemo(() => tables().reduce((total, table) => total + table.rows.length, 0))
  return (
    <section class="mb-4 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-14-medium text-v2-text-text-base">{props.record.schema_name ?? "Extraction"}</h3>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">
            {language.t("cowork.pipeline.extraction.categorySummary", { tables: tables().length, rows: totalRows() })}
          </p>
        </div>
        <StatusBadge status={props.record.status} />
      </div>
      <Show
        when={tables().length > 0}
        fallback={
          <p class="text-14-regular text-v2-text-text-muted">{language.t("cowork.pipeline.extraction.noTable")}</p>
        }
      >
        <div class="grid gap-4 xl:grid-cols-[260px_minmax(0,1fr)]">
          <aside class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
            <div class="mb-3 text-12-medium text-v2-text-text-muted">
              {language.t("cowork.pipeline.extraction.tables")}
            </div>
            <div class="max-h-[420px] space-y-2 overflow-y-auto pr-1 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-width:thin]">
              <For each={tables()}>
                {(table, index) => (
                  <button
                    type="button"
                    class="w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 py-2 text-left hover:bg-v2-background-bg-layer-02"
                    classList={{
                      "border-blue-400/60 bg-v2-background-bg-layer-03": selectedTable()?.name === table.name,
                    }}
                    onClick={() => setStore("tableIndex", index())}
                  >
                    <div class="truncate text-12-medium text-v2-text-text-base">{table.name}</div>
                    <div class="mt-1 text-11-regular text-v2-text-text-muted">
                      {language.t("cowork.pipeline.extraction.tableMeta", {
                        rows: table.rows.length,
                        columns: table.headers.length,
                      })}
                    </div>
                  </button>
                )}
              </For>
            </div>
          </aside>
          <main class="min-w-0">
            <Show keyed when={selectedTable()}>
              {(table) => (
                <div>
                  <div class="mb-3 grid gap-3 sm:grid-cols-3">
                    <MetricCard label={language.t("cowork.pipeline.extraction.table")} value={table.name} />
                    <MetricCard label={language.t("cowork.pipeline.output.rows")} value={String(table.rows.length)} />
                    <MetricCard
                      label={language.t("cowork.pipeline.output.columns")}
                      value={String(table.headers.length)}
                    />
                  </div>
                  <div class="mb-3 flex justify-end gap-1">
                    <TableExpandButton
                      title={table.name}
                      rows={[
                        table.headers,
                        ...table.rows.map((row) => table.headers.map((header) => row[header] ?? "")),
                      ]}
                    />
                    <button
                      type="button"
                      class="flex size-8 items-center justify-center rounded-md border border-v2-border-border-base text-v2-icon-icon-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-icon-icon-base"
                      aria-label={language.t("cowork.pipeline.output.downloadCsv")}
                      title={language.t("cowork.pipeline.output.downloadCsv")}
                      onClick={() => downloadExtractionTable(table)}
                    >
                      <IconV2 name="download" size="small" />
                    </button>
                  </div>
                  <CsvPreview
                    title={table.name}
                    rows={[table.headers, ...table.rows.map((row) => table.headers.map((header) => row[header] ?? ""))]}
                    showExpand={false}
                  />
                </div>
              )}
            </Show>
          </main>
        </div>
      </Show>
    </section>
  )
}

function downloadExtractionTable(table: { name: string; headers: string[]; rows: Array<Record<string, string>> }) {
  const csv = [table.headers, ...table.rows.map((row) => table.headers.map((header) => row[header] ?? ""))]
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\n")
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = `${safeFilename(table.name || "extraction")}.csv`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

function escapeCsvCell(value: string) {
  if (!/[",\n\r]/.test(value)) return value
  return `"${value.replaceAll('"', '""')}"`
}

function safeFilename(value: string) {
  return value.replace(/[^a-z0-9._-]+/gi, "_").replace(/^_+|_+$/g, "") || "extraction"
}

function TransformationRecord(props: { record: CoworkTransformation }) {
  const language = useLanguage()
  const data = () => props.record.output_json
  const tables = () => outputTables(props.record)
  const steps = () => structuredList(data().steps ?? props.record.steps_json)
  const validations = () => structuredList(data().validations)
  const sourceTables = () => stringList(data().source_tables)
  const assumptions = () => stringList(data().assumptions)
  const summary = () => stringValue(data().summary)
  return (
    <section class="mb-4 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 class="text-14-medium text-v2-text-text-base">{props.record.instructions || "Transformation"}</h3>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">{tables().length} output tables</p>
        </div>
        <span class="text-12-regular text-v2-text-text-muted">{props.record.status}</span>
      </div>
      <Show when={summary()}>
        <p class="mb-4 text-13-regular text-v2-text-text-base">{summary()}</p>
      </Show>
      <TransformationSection title={language.t("cowork.pipeline.transformation.sources")} items={sourceTables()} />
      <TransformationSteps title={language.t("cowork.pipeline.transformation.steps")} items={steps()} />
      <TransformationSteps title={language.t("cowork.pipeline.transformation.validations")} items={validations()} />
      <TransformationSection title={language.t("cowork.pipeline.transformation.assumptions")} items={assumptions()} />
    </section>
  )
}

export function OutputTab(props: { transformations: CoworkTransformation[]; artifacts: CoworkArtifact[] }) {
  const language = useLanguage()
  const [store, setStore] = createStore({ selectedKey: "", query: "", reportID: "", panelCollapsed: true })
  const panelID = "cowork-output-tables-panel"
  const reportOptions = createMemo(() => props.artifacts.filter((artifact) => artifact.artifact_type === "report"))
  const latestTransformation = createMemo(() =>
    [...props.transformations]
      .sort((a, b) => Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at))
      .find((transformation) => outputTables(transformation).length > 0),
  )
  const tables = createMemo(() =>
    [...props.transformations]
      .sort((a, b) => Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at))
      .flatMap((transformation) =>
        outputTables(transformation).map((table, index) => ({
          ...table,
          key: `${transformation.id}:${index}`,
          transformation,
          latest: transformation.id === latestTransformation()?.id,
          reports: reportsForOutputTable(props.artifacts, props.transformations, transformation, table.name),
        })),
      ),
  )
  const filteredTables = createMemo(() =>
    store.reportID
      ? tables().filter((table) => table.reports.some((report) => report.id === store.reportID))
      : tables(),
  )
  const selected = createMemo(
    () => filteredTables().find((table) => table.key === store.selectedKey) ?? filteredTables()[0],
  )
  const filteredRows = createMemo(() => {
    const table = selected()
    const query = store.query.trim().toLowerCase()
    if (!table) return []
    if (!query) return table.rows
    return table.rows.filter((row) => table.headers.some((header) => (row[header] ?? "").toLowerCase().includes(query)))
  })

  createEffect(() => {
    const table = selected()
    if (table && table.key !== store.selectedKey) setStore("selectedKey", table.key)
  })

  const copyTable = () => {
    const table = selected()
    if (!table) return
    void navigator.clipboard.writeText(tableToCsv(table.headers, filteredRows()))
  }

  const downloadTable = () => {
    const table = selected()
    if (!table) return
    const url = URL.createObjectURL(
      new Blob([tableToCsv(table.headers, filteredRows())], { type: "text/csv;charset=utf-8" }),
    )
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = `${table.name || "output"}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div
      class="grid min-h-0 flex-1 overflow-hidden"
      classList={{
        "grid-cols-[320px_minmax(0,1fr)]": !store.panelCollapsed,
        "grid-cols-[minmax(0,1fr)]": store.panelCollapsed,
      }}
    >
      <Show when={!store.panelCollapsed}>
        <aside
          id={panelID}
          class="flex min-h-0 flex-col border-r border-v2-border-border-base bg-v2-background-bg-base p-4"
        >
        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 class="text-18-semibold text-v2-text-text-base">{language.t("cowork.pipeline.output")}</h2>
            <p class="mt-1 text-12-regular text-v2-text-text-muted">
              {language.t("cowork.pipeline.output.count", { count: filteredTables().length })}
            </p>
          </div>
          <WorkflowPanelToggle
            collapsed={store.panelCollapsed}
            controls={panelID}
            onToggle={() => setStore("panelCollapsed", true)}
          />
        </div>
        <div class="mb-3 shrink-0">
          <label class="mb-1 block text-11-medium text-v2-text-text-muted" for="cowork-output-report-filter">
            {language.t("cowork.pipeline.output.filterByReport")}
          </label>
          <select
            id="cowork-output-report-filter"
            class="h-9 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-13-regular text-v2-text-text-base outline-none"
            value={store.reportID}
            onChange={(event) => setStore({ reportID: event.currentTarget.value, selectedKey: "" })}
          >
            <option value="">{language.t("cowork.pipeline.output.allReports")}</option>
            <For each={reportOptions()}>
              {(report) => <option value={report.id}>{reportOptionLabel(report)}</option>}
            </For>
          </select>
        </div>
        <div class="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
          <For
            each={filteredTables()}
            fallback={
              <p class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4 text-13-regular text-v2-text-text-muted">
                {language.t("cowork.pipeline.output.empty")}
              </p>
            }
          >
            {(table) => (
              <button
                type="button"
                class="w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3 text-left hover:bg-v2-background-bg-layer-02"
                classList={{ "border-blue-400/60 bg-v2-background-bg-layer-03": selected()?.key === table.key }}
                onClick={() => setStore("selectedKey", table.key)}
              >
                <h3 class="truncate text-13-medium text-v2-text-text-base">{table.name}</h3>
                <div class="mt-2 flex flex-wrap gap-2 text-11-regular text-v2-text-text-muted">
                  <Show when={table.latest}>
                    <span class="rounded-full bg-blue-500/10 px-2 py-0.5 text-blue-300">
                      {language.t("cowork.pipeline.records.latest")}
                    </span>
                  </Show>
                  <span>
                    {table.rows.length} {language.t("cowork.pipeline.output.rows")}
                  </span>
                  <span>
                    {table.headers.length} {language.t("cowork.pipeline.output.columns")}
                  </span>
                </div>
                <p class="mt-2 truncate text-11-regular text-v2-text-text-muted">
                  {reportUsageLabel(table.reports, language)}
                </p>
              </button>
            )}
          </For>
        </div>
        </aside>
      </Show>
      <main class="min-h-0 overflow-auto p-6">
        <Show when={store.panelCollapsed}>
          <div class="mb-3">
            <WorkflowPanelToggle
              collapsed={store.panelCollapsed}
              controls={panelID}
              onToggle={() => setStore("panelCollapsed", false)}
            />
          </div>
        </Show>
        <Show
          keyed
          when={selected()}
          fallback={<p class="text-14-regular text-v2-text-text-muted">{language.t("cowork.pipeline.output.empty")}</p>}
        >
          {(table) => (
            <div class="mx-auto max-w-6xl">
              <section class="mb-4 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
                <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div class="min-w-0">
                    <h2 class="truncate text-20-semibold text-v2-text-text-base">{table.name}</h2>
                    <p class="mt-1 text-13-regular text-v2-text-text-muted">
                      {table.transformation.instructions || language.t("cowork.pipeline.output.sourceTransformation")}
                    </p>
                  </div>
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                      title={language.t("cowork.pipeline.output.copy")}
                      aria-label={language.t("cowork.pipeline.output.copy")}
                      onClick={copyTable}
                    >
                      <IconV2 name="outline-copy" size="small" />
                    </button>
                    <button
                      type="button"
                      class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                      title={language.t("cowork.pipeline.output.downloadCsv")}
                      aria-label={language.t("cowork.pipeline.output.downloadCsv")}
                      onClick={downloadTable}
                    >
                      <IconV2 name="download" size="small" />
                    </button>
                    <TableExpandButton
                      title={table.name}
                      rows={[
                        table.headers,
                        ...filteredRows().map((row) => table.headers.map((header) => row[header] ?? "")),
                      ]}
                    />
                  </div>
                </div>
                <div class="mb-4 grid gap-3 sm:grid-cols-4">
                  <MetricCard label={language.t("cowork.pipeline.output.rows")} value={String(table.rows.length)} />
                  <MetricCard
                    label={language.t("cowork.pipeline.output.columns")}
                    value={String(table.headers.length)}
                  />
                  <MetricCard
                    label={language.t("cowork.pipeline.records.metric.status")}
                    value={table.transformation.status}
                  />
                  <MetricCard
                    label={language.t("cowork.pipeline.records.metric.updated")}
                    value={formatRecordDate(table.transformation.updated_at)}
                  />
                </div>
                <div class="mb-4 grid gap-3 sm:grid-cols-2">
                  <MetricCard
                    label={language.t("cowork.pipeline.output.version")}
                    value={
                      table.latest
                        ? language.t("cowork.pipeline.records.latest")
                        : formatRecordDate(table.transformation.created_at)
                    }
                  />
                  <MetricCard
                    label={language.t("cowork.pipeline.output.reportUsage")}
                    value={reportUsageLabel(table.reports, language)}
                  />
                </div>
                <input
                  class="h-9 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-13-regular text-v2-text-text-base outline-none"
                  value={store.query}
                  placeholder={language.t("cowork.pipeline.output.search")}
                  onInput={(event) => setStore("query", event.currentTarget.value)}
                />
              </section>
              <CsvPreview
                title={table.name}
                rows={[table.headers, ...filteredRows().map((row) => table.headers.map((header) => row[header] ?? ""))]}
                showExpand={false}
              />
            </div>
          )}
        </Show>
      </main>
    </div>
  )
}

function reportsForOutputTable(
  artifacts: CoworkArtifact[],
  transformations: CoworkTransformation[],
  transformation: CoworkTransformation,
  tableName: string,
) {
  return artifacts.filter((artifact) => {
    if (artifact.artifact_type !== "report") return false
    return reportUsesOutputTable(artifact, transformations, transformation, tableName)
  })
}

function reportUsesOutputTable(
  artifact: CoworkArtifact,
  transformations: CoworkTransformation[],
  transformation: CoworkTransformation,
  tableName: string,
) {
  const metadata = artifact.artifact_metadata ?? {}
  const hasTransformationRefs = metadataHasTransformationReferences(metadata)
  const hasTableRefs = metadataHasTableReferences(metadata)
  if (hasTransformationRefs)
    return (
      metadataReferencesTransformation(metadata, transformation.id) &&
      (!hasTableRefs || metadataReferencesTable(metadata, tableName))
    )
  if (!hasTableRefs) return false
  const inferred = inferReportTransformation(artifact, transformations)
  if (inferred) return inferred.id === transformation.id && metadataReferencesTable(metadata, tableName)
  return metadataReferencesTable(metadata, tableName)
}

function inferReportTransformation(artifact: CoworkArtifact, transformations: CoworkTransformation[]) {
  const reportTableNames = metadataTableNames(artifact.artifact_metadata ?? {})
  if (reportTableNames.length === 0) return undefined
  const reportCreatedAt = Date.parse(artifact.created_at)
  const candidates = transformations
    .filter(
      (transformation) => Number.isNaN(reportCreatedAt) || Date.parse(transformation.created_at) <= reportCreatedAt,
    )
    .filter((transformation) => {
      const names = outputTables(transformation).map((table) => normalizeTableName(table.name))
      return reportTableNames.every((name) => names.includes(name))
    })
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
  return candidates[0]
}

function metadataHasTransformationReferences(metadata: Record<string, unknown>) {
  return Boolean(
    metadata.source_transformation_id ||
      metadata.transformation_id ||
      nonEmptyArray(metadata.source_transformation_ids) ||
      nonEmptyArray(metadata.transformation_ids),
  )
}

function metadataHasTableReferences(metadata: Record<string, unknown>) {
  return metadataTableNames(metadata).length > 0
}

function metadataReferencesTransformation(metadata: Record<string, unknown>, transformationID: string) {
  const values = [metadata.source_transformation_id, metadata.transformation_id]
  const lists = [metadata.source_transformation_ids, metadata.transformation_ids]
  return (
    values.some((value) => value === transformationID) ||
    lists.some((value) => Array.isArray(value) && value.includes(transformationID))
  )
}

function metadataReferencesTable(metadata: Record<string, unknown>, tableName: string) {
  const normalized = normalizeTableName(tableName)
  return metadataTableNames(metadata).includes(normalized)
}

function metadataTableNames(metadata: Record<string, unknown>) {
  return [metadata.source_output_tables, metadata.output_tables, metadata.source_tables, metadata.tables].flatMap(
    (value) => {
      if (!Array.isArray(value)) return []
      return value.flatMap((item) => {
        const name =
          typeof item === "string"
            ? item
            : isRecord(item)
              ? stringValue(item.name) || stringValue(item.table_name) || stringValue(item.title)
              : ""
        return name ? [normalizeTableName(name)] : []
      })
    },
  )
}

function nonEmptyArray(value: unknown) {
  return Array.isArray(value) && value.length > 0
}

function reportUsageLabel(reports: CoworkArtifact[], language: ReturnType<typeof useLanguage>) {
  if (reports.length === 0) return language.t("cowork.pipeline.output.notUsedInReport")
  if (reports.length === 1) return language.t("cowork.pipeline.output.usedInReport", { title: reports[0].title })
  return language.t("cowork.pipeline.output.usedInReports", { count: reports.length })
}

function reportOptionLabel(report: CoworkArtifact) {
  return `${report.title} · ${formatRecordDate(report.created_at)}`
}

type ExtractedTable = {
  name: string
  headers: string[]
  rows: Array<Record<string, string>>
}

function extractionTables(record: CoworkExtraction, relatedRecords?: CoworkExtraction[]): ExtractedTable[] {
  const primary = extractionTablesFromData(record)
  const related = relatedRecords?.filter((item) => item.workflow_id === record.workflow_id) ?? []
  if (related.length <= 1) return primary
  return mergeExtractionTables(related.length > 0 ? related : [record])
}

function extractionTablesFromData(record: CoworkExtraction): ExtractedTable[] {
  const data = record.result_json
  const contract = tablesFromCollection(data.tables)
  if (contract.length > 0) return contract
  const nested = nestedTableCollections(data)
  if (nested.length > 0) return nested
  const direct = directTableRows(data).map((table) => ({ ...table, name: record.schema_name ?? table.name }))
  if (direct.length > 0) return direct
  const complianceTables = [...nonFundTables(data.non_funds), ...fundTables(data.funds)]
  if (complianceTables.length > 0) return complianceTables
  return nestedCategoryTables(data.data ?? data).map((table) => ({
    ...table,
    name: record.schema_name && table.name === "Extracted table" ? record.schema_name : table.name,
  }))
}

function mergeExtractionTables(records: CoworkExtraction[]): ExtractedTable[] {
  const byName = new Map<string, { table: ExtractedTable; updatedAt: number }>()
  const ordered = [...records].sort(
    (a, b) => Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at),
  )
  for (const record of ordered) {
    const updatedAt = Date.parse(record.updated_at || record.created_at) || 0
    for (const table of extractionTablesFromData(record)) {
      const key = normalizeTableName(table.name)
      const existing = byName.get(key)
      if (
        !existing ||
        table.rows.length > existing.table.rows.length ||
        (table.rows.length === existing.table.rows.length && updatedAt > existing.updatedAt)
      ) {
        byName.set(key, { table, updatedAt })
      }
    }
  }
  return Array.from(byName.values()).map((entry) => entry.table)
}

function normalizeTableName(value: string) {
  return (
    value
      .toLowerCase()
      .replace(/^[^·]+·\s*/, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "table"
  )
}

function transformationTables(record: CoworkTransformation): ExtractedTable[] {
  const data = record.output_json
  const direct = directTableRows(data)
  if (direct.length > 0) return direct.map((table) => ({ ...table, name: outputTableName(record, table.name) }))
  const tableCollections = [data.tables, data.output_tables, data.outputs, data.result]
  const nested = tableCollections.flatMap((value) => tablesFromCollection(value))
  if (nested.length > 0) return nested.map((table) => ({ ...table, name: outputTableName(record, table.name) }))
  return Object.entries(data).flatMap(([name, value]) => rowsToTable(name, value))
}

function outputTables(record: CoworkTransformation): ExtractedTable[] {
  const tables = tablesFromCollection(record.output_json.output_tables)
  if (tables.length > 0) return tables
  return transformationTables(record)
}

function TransformationSection(props: { title: string; items: string[] }) {
  return (
    <Show when={props.items.length > 0}>
      <div class="mb-4">
        <h4 class="mb-2 text-13-medium text-v2-text-text-base">{props.title}</h4>
        <ul class="list-disc space-y-1 pl-5 text-13-regular text-v2-text-text-muted">
          <For each={props.items}>{(item) => <li>{item}</li>}</For>
        </ul>
      </div>
    </Show>
  )
}

function TransformationSteps(props: {
  title: string
  items: Array<{ label: string; description: string; status?: string }>
}) {
  return (
    <Show when={props.items.length > 0}>
      <div class="mb-4">
        <h4 class="mb-2 text-13-medium text-v2-text-text-base">{props.title}</h4>
        <div class="space-y-2">
          <For each={props.items}>
            {(item) => (
              <div class="rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-3">
                <div class="flex items-center justify-between gap-3">
                  <div class="text-13-medium text-v2-text-text-base">{item.label}</div>
                  <Show when={item.status}>
                    {(status) => <span class="text-12-regular text-v2-text-text-muted">{status()}</span>}
                  </Show>
                </div>
                <Show when={item.description}>
                  <p class="mt-1 text-12-regular text-v2-text-text-muted">{item.description}</p>
                </Show>
              </div>
            )}
          </For>
        </div>
      </div>
    </Show>
  )
}

function structuredList(value: unknown): Array<{ label: string; description: string; status?: string }> {
  if (!Array.isArray(value)) return []
  return value.filter(isRecord).map((item, index) => ({
    label: stringValue(item.label) || stringValue(item.name) || `Step ${index + 1}`,
    description: stringValue(item.description) || stringValue(item.details),
    status: stringValue(item.status) || undefined,
  }))
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) =>
      typeof item === "string" ? item : isRecord(item) ? stringValue(item.name) || stringValue(item.label) : "",
    )
    .filter(Boolean)
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function outputTableName(record: CoworkTransformation, fallback: string) {
  const metadataName = record.output_json.table_name ?? record.output_json.name ?? record.output_json.title
  return typeof metadataName === "string" && metadataName.trim() ? metadataName : fallback
}

function tableToCsv(headers: string[], rows: Array<Record<string, string>>) {
  return [headers, ...rows.map((row) => headers.map((header) => row[header] ?? ""))]
    .map((row) => row.map(csvCell).join(","))
    .join("\n")
}

function csvCell(value: string) {
  return /[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value
}

function tablesFromCollection(value: unknown): ExtractedTable[] {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => {
      if (!isRecord(item)) return []
      const name =
        typeof item.name === "string"
          ? item.name
          : typeof item.table_name === "string"
            ? item.table_name
            : `Output ${index + 1}`
      const rows = rowsToTable(name, item.rows ?? item.data ?? item.values)
      const columns = Array.isArray(item.columns)
        ? item.columns.filter((column): column is string => typeof column === "string")
        : []
      if (columns.length === 0 || rows.length === 0) return rows
      return rows.map((table) => ({ ...table, headers: columns }))
    })
  }
  if (isRecord(value)) {
    return Object.entries(value).flatMap(([name, rows]) => {
      const nested = tablesFromCollection(rows)
      if (nested.length > 0) return nested.map((table) => ({ ...table, name: `${prettyLabel(name)} · ${table.name}` }))
      return rowsToTable(name, rows)
    })
  }
  return []
}

function nestedTableCollections(value: unknown, prefix = "", depth = 0): ExtractedTable[] {
  if (depth > 5) return []
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => nestedTableCollections(item, prefix || `Item ${index + 1}`, depth + 1))
  }
  if (!isRecord(value)) return []
  const label =
    stringValue(value.filename) ||
    stringValue(value.file_name) ||
    stringValue(value.name) ||
    stringValue(value.title) ||
    prefix
  const tableKeys = ["tables", "extracted_tables", "worksheets", "sheets", "output_tables"]
  const direct = tableKeys
    .flatMap((key) => tablesFromCollection(value[key]))
    .map((table) => ({
      ...table,
      name: label && !table.name.startsWith(`${label} ·`) ? `${label} · ${table.name}` : table.name,
    }))
  if (direct.length > 0) return direct
  return Object.entries(value).flatMap(([key, item]) =>
    nestedTableCollections(item, label || prettyLabel(key), depth + 1),
  )
}

function nestedCategoryTables(value: unknown): ExtractedTable[] {
  if (Array.isArray(value)) return rowsToTable("Extracted table", value)
  if (!isRecord(value)) return []
  return Object.entries(value).flatMap(([name, section]) => {
    const table = rowsToTable(prettyLabel(name), section)
    if (table.length > 0) return table
    return []
  })
}

function prettyLabel(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase())
}

function nonFundTables(value: unknown): ExtractedTable[] {
  if (!isRecord(value)) return []
  const data = isRecord(value.data) ? value.data : {}
  return Object.entries(data).flatMap(([name, rows]) => rowsToTable(name, rows))
}

function fundTables(value: unknown): ExtractedTable[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((fund) => {
    if (!isRecord(fund) || !isRecord(fund.data)) return []
    const fundName = typeof fund.name === "string" ? fund.name : "Fund"
    return Object.entries(fund.data).flatMap(([tableName, rows]) => rowsToTable(`${fundName} · ${tableName}`, rows))
  })
}

function directTableRows(data: Record<string, unknown>): ExtractedTable[] {
  const candidates = [data.rows, data.data]
  return candidates.flatMap((rows) => rowsToTable("Extracted table", rows))
}

function rowsToTable(name: string, rows: unknown): ExtractedTable[] {
  if (!Array.isArray(rows) || rows.length === 0) return []
  const normalized = rows
    .filter(isRecord)
    .map((row) => Object.fromEntries(Object.entries(row).map(([key, value]) => [key, cellText(value)])))
  if (normalized.length === 0) return []
  const headers = Array.from(new Set(normalized.flatMap((row) => Object.keys(row))))
  if (headers.length === 0) return []
  return [{ name, headers, rows: normalized }]
}

function cellText(value: unknown): string {
  if (isRecord(value) && "v" in value) return value.v == null ? "" : String(value.v)
  if (value == null) return ""
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function ReportTab(props: {
  artifacts: CoworkArtifact[]
  details?: (artifact: CoworkArtifact) => JSX.Element
  loading: boolean
  onRefresh: () => void
  onDelete: (artifact: CoworkArtifact) => Promise<void>
}) {
  const language = useLanguage()
  const dialog = useDialog()
  const [store, setStore] = createStore({ selectedArtifactID: "", downloading: false, error: false })
  let mounted = true
  onCleanup(() => { mounted = false })
  const selectedArtifact = createMemo(
    () => props.artifacts.find((artifact) => artifact.id === store.selectedArtifactID) ?? props.artifacts[0],
  )
  createEffect(() => {
    const selected = selectedArtifact()
    if (selected && selected.id !== store.selectedArtifactID) setStore("selectedArtifactID", selected.id)
    if (!selected && store.selectedArtifactID) setStore("selectedArtifactID", "")
  })
  const download = async (artifact: CoworkArtifact) => {
    if (store.downloading) return
    setStore({ downloading: true, error: false })
    try {
      const result = await downloadArtifact(artifact.project_id, artifact.id, artifact.company_id)
      const normalized = await normalizedArtifactDownload(result.blob, artifact.format, result.filename)
      if (!mounted) return
      const url = URL.createObjectURL(normalized.blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = artifactDownloadFilename(artifact, result.filename)
      anchor.click()
      URL.revokeObjectURL(url)
    } catch {
      if (mounted) setStore("error", true)
    } finally {
      if (mounted) setStore("downloading", false)
    }
  }
  return (
    <div class="report-workspace">
      <div class="report-workspace-toolbar">
        <div class="min-w-0">
          <h2 class="text-20-semibold text-v2-text-text-base">{language.t("cowork.pipeline.report")} <span class="text-12-regular text-v2-text-text-muted">{props.artifacts.length}</span></h2>
          <Show when={selectedArtifact()}>
            {(artifact) => (
              <div class="mt-1 min-w-0">
                <p class="truncate text-13-medium text-v2-text-text-base" title={reportDisplayTitle(artifact().title)}>
                  {reportDisplayTitle(artifact().title)}
                </p>
                <p class="mt-0.5 truncate text-12-regular text-v2-text-text-muted">{reportFormatLabel(artifact().format, language.t("cowork.pipeline.report"))}</p>
              </div>
            )}
          </Show>
        </div>
        <div class="report-workspace-actions">
          <Show when={selectedArtifact()}>
            {(artifact) => (
              <>
                <ReportPreviewLink projectID={artifact().project_id} artifactID={artifact().id} companyID={artifact().company_id} />
                <button type="button" class="report-workspace-action" onClick={() => {
                  const selected = artifact()
                  dialog.show(() => <ReportShareDialog artifact={selected} active={() => mounted && selectedArtifact()?.id === selected.id} />)
                }}>{language.t("cowork.enterprise.reports.share")}</button>
                <Show when={editableSpreadsheetReport(artifact())}>
                  <button type="button" class="report-workspace-action" onClick={() => openReportEditor(artifact())}>
                    {language.t("common.edit")}
                  </button>
                </Show>
                <button type="button" class="report-workspace-action" onClick={() => dialog.show(() => <ReportExportDialog artifact={artifact()} />)}>
                  {language.t("cowork.report.export")}
                </button>
                <button type="button" class="report-workspace-action" disabled={store.downloading} aria-busy={store.downloading} onClick={() => void download(artifact())}>
                  {language.t("cowork.pipeline.report.download")}
                </button>
                <ReportDeleteButton artifact={artifact()} onDelete={props.onDelete} />
                <ReportExpandButton artifact={artifact()} />
              </>
            )}
          </Show>
          <button type="button" class="report-workspace-action" disabled={props.loading} aria-busy={props.loading} onClick={props.onRefresh}>
            {language.t("cowork.pipeline.report.refresh")}
          </button>
        </div>
      </div>
      <Show when={store.error}><p role="alert" class="text-12-regular text-v2-state-fg-danger">{language.t("cowork.report.share.downloadError")}</p></Show>
      <Show
        when={props.artifacts.length > 0}
        fallback={<p role="status" class="text-14-regular text-v2-text-text-muted">{language.t(props.loading ? "common.loading" : "cowork.pipeline.report.empty")}</p>}
      >
        <nav class="report-workspace-cards" aria-label={language.t("cowork.report.workspace.choose")}>
          <For each={props.artifacts}>
            {(artifact) => (
              <button
                type="button"
                class="report-workspace-card"
                aria-pressed={selectedArtifact()?.id === artifact.id}
                onClick={() => setStore("selectedArtifactID", artifact.id)}
                title={reportDisplayTitle(artifact.title)}
              >
                <FileIcon node={{ path: artifactDisplayTitle(artifact), type: "file" }} class="size-6 shrink-0" />
                <span class="min-w-0"><span class="block truncate text-12-medium">{reportDisplayTitle(artifact.title)}</span>
                <span class="mt-0.5 block text-11-regular text-v2-text-text-muted">{reportFormatLabel(artifact.format, language.t("cowork.pipeline.report"))}</span></span>
              </button>
            )}
          </For>
        </nav>
        <Show when={selectedArtifact()?.id} keyed>
          {(_id) => (
            <section class="report-workspace-preview">
              {props.details?.(selectedArtifact()!)}
              <Show when={shouldRenderEChartsDashboard(selectedArtifact()!)} fallback={<ReportPreview artifact={selectedArtifact()!} />}>
                <EChartsDashboard charts={echartsCharts(selectedArtifact()!)} />
              </Show>
            </section>
          )}
        </Show>
      </Show>
    </div>
  )
}

const reportExportFormats = ["pdf", "xlsx", "html", "docx", "csv", "json", "txt"] as const

function ReportExportDialog(props: { artifact: CoworkArtifact }) {
  const language = useLanguage()
  const dialog = useDialog()
  const exportReport = (format: string) => {
    const prompt = language.t("cowork.report.export.prompt", {
      title: artifactDisplayTitle(props.artifact),
      currentFormat: props.artifact.format.toUpperCase(),
      targetFormat: format.toUpperCase(),
    })
    window.dispatchEvent(new CustomEvent("cowork:report-export", { detail: { prompt } }))
    dialog.close()
  }
  return (
    <DialogV2 fit containerClass="!h-auto max-h-[calc(100vh_-_16px)] !w-[min(calc(100vw_-_16px),520px)]">
      <DialogHeader closeLabel={language.t("common.close")}>
        <DialogTitle>{language.t("cowork.report.export.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody class="flex-none gap-0 p-4">
        <p class="mb-4 text-13-regular text-v2-text-text-muted">
          {language.t("cowork.report.export.description", { title: reportDisplayTitle(artifactDisplayTitle(props.artifact)) })}
        </p>
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <For each={reportExportFormats}>
            {(format) => (
              <button
                type="button"
                class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 py-2 text-13-medium text-v2-text-text-base hover:bg-v2-background-bg-layer-02 disabled:cursor-not-allowed disabled:opacity-45"
                disabled={format === props.artifact.format.toLowerCase()}
                onClick={() => exportReport(format)}
              >
                {reportFormatLabel(format, language.t("cowork.pipeline.report"))}
              </button>
            )}
          </For>
        </div>
      </DialogBody>
    </DialogV2>
  )
}

function artifactDisplayTitle(artifact: CoworkArtifact) {
  const metadata = artifact.artifact_metadata ?? {}
  const filename = metadata.filename || metadata.file_name || metadata.download_filename
  return typeof filename === "string" && filename.trim() ? filename : `${artifact.title}.${artifact.format}`
}

function editableSpreadsheetReport(artifact: CoworkArtifact) {
  return /^(xlsx|xlsm|excel|spreadsheet|workbook)$/i.test(artifact.format.replace(/^\./, ""))
}

function openReportEditor(artifact: CoworkArtifact) {
  const query = new URLSearchParams({ companyID: artifact.company_id })
  window.open(
    workspaceHref(`/reports/${encodeURIComponent(artifact.project_id)}/${encodeURIComponent(artifact.id)}/edit?${query}`),
    "_blank",
    "noopener,noreferrer",
  )
}

function ReportExpandButton(props: { artifact: CoworkArtifact }) {
  const language = useLanguage()
  const [store, setStore] = createStore({ open: false })
  return (
    <>
      <button type="button" class="report-workspace-action" onClick={() => setStore("open", true)}>
        {language.t("cowork.report.expand")}
      </button>
      <Show when={store.open}>
        <Portal>
          <div
            class="fixed inset-0 z-[1000] overflow-y-auto bg-black/75 px-4 py-10 backdrop-blur-sm"
            onClick={() => setStore("open", false)}
          >
            <section
              class="mx-auto flex h-[min(88vh,920px)] w-[min(96vw,1440px)] flex-col overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <header class="flex shrink-0 items-center justify-between gap-3 border-b border-v2-border-border-base px-4 py-3">
                <div class="min-w-0">
                  <h3 class="truncate text-16-semibold text-v2-text-text-base">{reportDisplayTitle(props.artifact.title)}</h3>
                  <p class="mt-0.5 text-12-regular text-v2-text-text-muted">{reportFormatLabel(props.artifact.format, language.t("cowork.pipeline.report"))}</p>
                </div>
                <div class="flex items-center gap-2">
                  <Show when={editableSpreadsheetReport(props.artifact)}>
                    <button
                      type="button"
                      class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                      title={language.t("common.edit")}
                      aria-label={language.t("common.edit")}
                      onClick={() => openReportEditor(props.artifact)}
                    >
                      <IconV2 name="edit" size="normal" />
                    </button>
                  </Show>
                  <button
                    type="button"
                    class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                    title={language.t("common.close")}
                    aria-label={language.t("common.close")}
                    onClick={() => setStore("open", false)}
                  >
                    <IconV2 name="close" size="normal" />
                  </button>
                </div>
              </header>
              <div class="min-h-0 flex-1 overflow-auto p-4">
                <ReportPreview artifact={props.artifact} />
              </div>
            </section>
          </div>
        </Portal>
      </Show>
    </>
  )
}

export function ReportPreview(props: {
  artifact: CoworkArtifact
  load?: () => Promise<{ blob: Blob; filename?: string }>
}) {
  const language = useLanguage()
  const format = () => normalizedReportPreviewFormat(props.artifact.format)
  const [download] = createResource(
    () => (previewableReportFormat(format()) ? [props.artifact.company_id, props.artifact.project_id, props.artifact.id, props.artifact.updated_at, format()].join(":") : undefined),
    () => props.load?.() ?? downloadArtifact(props.artifact.project_id, props.artifact.id, props.artifact.company_id),
  )
  return (
    <Show when={!download.loading} fallback={<ReportPreviewLoading />}>
      <Show when={!download.error} fallback={
        <p role="alert" class="p-4 text-13-regular">
          {language.t(format() === "docx" ? "cowork.pipeline.report.docxPreviewError" : "cowork.pipeline.report.previewUnavailable")}
        </p>
      }>
        <Show when={download()?.blob} keyed fallback={
          <FormatPreview artifact={props.artifact} text={reportPreviewText(props.artifact)} format={format()} />
        }>
          {(blob) => (
            <Show when={format() === "docx"} fallback={
              <DownloadedReportPreview artifact={props.artifact} blob={blob} format={format()} />
            }>
              <DocxPreview blob={blob} title={language.t("cowork.pipeline.report.docxPreviewTitle", { title: props.artifact.title })}
                loading={language.t("cowork.files.panel.loadingPreview")} error={language.t("cowork.pipeline.report.docxPreviewError")}
                zoomIn={language.t("cowork.pipeline.report.zoomIn")} zoomOut={language.t("cowork.pipeline.report.zoomOut")}
                scaleLabel={(scale) => language.t("cowork.pipeline.report.previewScale", { scale })} />
            </Show>
          )}
        </Show>
      </Show>
    </Show>
  )
}

function ReportPreviewLoading() {
  const language = useLanguage()
  return (
    <div role="status" class="grid min-h-40 place-items-center p-4 text-13-regular text-v2-text-text-muted">
      {language.t("cowork.files.panel.loadingPreview")}
    </div>
  )
}

function DownloadedReportPreview(props: { artifact: CoworkArtifact; blob: Blob; format: string }) {
  const language = useLanguage()
  const [state, setState] = createStore({ text: "", url: "", loading: true, error: false })

  createEffect(() => {
    const blob = props.blob
    const format = props.format
    let active = true
    let url = ""
    onCleanup(() => {
      active = false
      if (url) URL.revokeObjectURL(url)
    })
    setState({ text: "", url: "", loading: true, error: false })
    void (async () => {
      try {
        let text = ""
        if (format === "html") {
          url = URL.createObjectURL(new Blob([blob], { type: "text/html;charset=utf-8" }))
        } else if (format === "pdf" && await artifactBlobIsPdf(blob)) {
          if (!active) return
          url = URL.createObjectURL(blob)
        } else if (format !== "xlsx" && format !== "pptx") {
          text = await blob.text()
        }
        if (active) setState({ text, url, loading: false })
      } catch {
        if (active) setState({ error: true, loading: false })
      }
    })()
  })

  return (
    <Show when={!state.loading} fallback={<ReportPreviewLoading />}>
      <Show when={!state.error} fallback={
        <p role="alert" class="p-4 text-13-regular">{language.t("cowork.pipeline.report.previewUnavailable")}</p>
      }>
    <Show
      when={props.format === "html"}
      fallback={
        <Show
          when={props.format === "xlsx"}
          fallback={
            <Show
              when={props.format === "pptx"}
              fallback={
                <Show
                  when={props.format === "pdf"}
                  fallback={<FormatPreview artifact={props.artifact} text={state.text} format={props.format} />}
                >
                  <Show
                    when={state.url}
                    fallback={
                      <FormatPreview
                        artifact={props.artifact}
                        text={state.text}
                        format={fallbackReportFormat(state.text)}
                      />
                    }
                  >
                    {(src) => (
                      <iframe
                        class="h-[720px] w-full rounded-md border border-v2-border-border-base bg-white"
                        src={src()}
                      />
                    )}
                  </Show>
                </Show>
              }
            >
              <PptxPreview
                blob={props.blob}
                empty={language.t("cowork.files.panel.noPreview")}
                loading={language.t("cowork.files.panel.loadingPreview")}
                slideLabel={(index) => language.t("cowork.files.panel.slide", { index })}
              />
            </Show>
          }
        >
          <SpreadsheetPreview blob={props.blob} />
        </Show>
      }
    >
      <Show
        when={state.url}
        fallback={
          <FormatPreview artifact={props.artifact} text={state.text} format={fallbackReportFormat(state.text)} />
        }
      >
        {(src) => <ScaledHtmlPreview src={src()} />}
      </Show>
    </Show>
      </Show>
    </Show>
  )
}

function SpreadsheetPreview(props: { blob: Blob }) {
  return <SpreadsheetGridPreview blob={props.blob} />
}

function PdfBlobPreview(props: { blob: Blob; unavailable: string }) {
  const [state, setState] = createStore({ text: "", url: "" })
  createEffect(() => {
    const previous = untrack(() => state.url)
    if (previous) URL.revokeObjectURL(previous)
    void artifactBlobIsPdf(props.blob).then((isPdf) => {
      if (isPdf) {
        setState({ text: "", url: URL.createObjectURL(props.blob) })
        return
      }
      void props.blob.text().then((text) => setState({ text: text || props.unavailable, url: "" }))
    })
  })
  onCleanup(() => {
    const previous = untrack(() => state.url)
    if (previous) URL.revokeObjectURL(previous)
  })
  return (
    <Show when={state.url} fallback={<TextPreview text={state.text || props.unavailable} />}>
      {(src) => <iframe class="h-[720px] w-full rounded-md border border-v2-border-border-base bg-white" src={src()} />}
    </Show>
  )
}

function SpreadsheetGridPreview(props: { blob: Blob }) {
  const language = useLanguage()
  const [state, setState] = createStore({
    sheet: "",
    sheets: [] as string[],
    rowsBySheet: {} as Record<string, string[][]>,
    error: "",
    loading: true,
  })
  const rows = createMemo(() => state.rowsBySheet[state.sheet] ?? [])
  const columnCount = createMemo(() => Math.max(1, ...rows().map((row) => row.length)))
  const columns = createMemo(() => Array.from({ length: columnCount() }, (_, index) => spreadsheetColumnName(index)))

  createEffect(() => {
    const source = props.blob
    let active = true
    onCleanup(() => { active = false })
    setState({ loading: true, error: "" })
    void (async () => {
      try {
        const blob = await normalizedBinaryArtifactBlob(source, "xlsx")
        const XLSX = await loadXlsxModule()
        const workbook = XLSX.read(await blob.arrayBuffer(), { type: "array", sheetRows: 250 })
        const rowsBySheet = Object.fromEntries(
          workbook.SheetNames.map((sheet) => {
            const rows = XLSX.utils
              .sheet_to_json<unknown[]>(workbook.Sheets[sheet], { header: 1, defval: "", blankrows: false })
              .slice(0, 200)
            return [sheet, trimSpreadsheetRows(rows.map((row) => row.map((cell) => String(cell))))]
          }),
        )
        if (!active) return
        setState({
          sheet: firstVisibleSheet(workbook.SheetNames, rowsBySheet),
          sheets: workbook.SheetNames,
          rowsBySheet,
          error: "",
        })
      } catch (error) {
        if (active) setState("error", error instanceof Error ? error.message : String(error))
      } finally {
        if (active) setState("loading", false)
      }
    })()
  })

  return (
    <Show when={!state.loading} fallback={<ReportPreviewLoading />}>
    <div class="overflow-hidden rounded-md border border-v2-border-border-base bg-[#1f1f1f] text-[#f3f3f3]">
      <div class="flex items-center gap-2 border-b border-[#3a3a3a] bg-[#191919] px-3 py-2">
        <div class="rounded bg-[#2a2a2a] px-2 py-1 text-11-medium text-[#d8d8d8]">A1</div>
        <div class="flex min-w-0 flex-1 items-center rounded border border-[#3a3a3a] bg-[#101010] px-2 py-1">
          <span class="mr-2 text-11-medium italic text-[#70ad47]">fx</span>
          <span class="truncate text-11-regular text-[#bdbdbd]">
            {state.sheet || language.t("cowork.pipeline.report")}
          </span>
        </div>
        <span class="hidden text-11-regular text-[#a6a6a6] sm:inline">
          {state.sheets.length} {language.t("cowork.pipeline.records.tables")} · {Math.min(rows().length, 200)}{" "}
          {language.t("cowork.pipeline.output.rows")}
        </span>
      </div>
      <Show when={!state.error} fallback={<TextPreview text={state.error} />}>
        <Show
          when={rows().length > 0}
          fallback={<TextPreview text={language.t("cowork.pipeline.report.previewUnavailable")} />}
        >
          <div
            class="max-h-[720px] overflow-auto [scrollbar-color:#777_transparent] [scrollbar-width:thin]"
            style={{ "background-color": "#ffffff" }}
          >
            <table class="border-separate border-spacing-0 text-left text-12-regular text-[#1f1f1f]">
              <thead>
                <tr>
                  <th class="sticky left-0 top-0 z-30 h-7 min-w-12 border-b border-r border-[#d0d0d0] bg-[#f3f3f3]" />
                  <For each={columns()}>
                    {(column) => (
                      <th class="sticky top-0 z-20 h-7 min-w-[140px] border-b border-r border-[#d0d0d0] bg-[#f3f3f3] px-2 text-center text-11-medium text-[#555]">
                        {column}
                      </th>
                    )}
                  </For>
                </tr>
              </thead>
              <tbody>
                <For each={rows()}>
                  {(row, rowIndex) => (
                    <tr>
                      <th class="sticky left-0 z-10 h-7 min-w-12 border-b border-r border-[#d0d0d0] bg-[#f3f3f3] px-2 text-right text-11-medium text-[#555]">
                        {rowIndex() + 1}
                      </th>
                      <For each={columns()}>
                        {(_, columnIndex) => (
                          <td
                            class="h-7 min-w-[140px] max-w-[360px] whitespace-pre-wrap border-b border-r border-[#d9d9d9] px-2 py-1 align-top"
                            style={{ "background-color": "#ffffff", color: "#1f1f1f" }}
                          >
                            {row[columnIndex()] ?? ""}
                          </td>
                        )}
                      </For>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>
      <Show when={state.sheets.length > 0}>
        <div class="flex gap-1 overflow-x-auto border-t border-[#3a3a3a] bg-[#191919] px-3 py-2 [scrollbar-color:#777_transparent] [scrollbar-width:thin]">
          <For each={state.sheets}>
            {(sheet) => (
              <button
                type="button"
                class="shrink-0 rounded-t border border-[#3a3a3a] px-3 py-1 text-11-medium"
                classList={{
                  "border-[#217346] bg-[#217346] text-white": state.sheet === sheet,
                  "bg-[#2a2a2a] text-[#d8d8d8] hover:bg-[#333]": state.sheet !== sheet,
                }}
                onClick={() => setState("sheet", sheet)}
              >
                {sheet}
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
    </Show>
  )
}

function trimSpreadsheetRows(rows: string[][]) {
  const nonEmptyRows = rows.filter((row) => row.some((cell) => cell.trim()))
  const columnIndexes = Array.from(
    { length: Math.max(0, ...nonEmptyRows.map((row) => row.length)) },
    (_, index) => index,
  ).filter((index) => nonEmptyRows.some((row) => (row[index] ?? "").trim()))
  return nonEmptyRows.map((row) => columnIndexes.map((index) => row[index] ?? ""))
}

function firstVisibleSheet(sheets: string[], rowsBySheet: Record<string, string[][]>) {
  return sheets.find((sheet) => rowsBySheet[sheet]?.some((row) => row.some((cell) => cell.trim()))) ?? sheets[0] ?? ""
}

function spreadsheetColumnName(index: number): string {
  const next = Math.floor(index / 26) - 1
  const letter = String.fromCharCode(65 + (index % 26))
  return next >= 0 ? `${spreadsheetColumnName(next)}${letter}` : letter
}

type XlsxModule = {
  read: (
    data: ArrayBuffer,
    options: { type: "array"; sheetRows: number },
  ) => { SheetNames: string[]; Sheets: Record<string, unknown> }
  utils: {
    sheet_to_json: <T>(worksheet: unknown, options: { header: 1; defval: string; blankrows: false }) => T[]
  }
}

function loadXlsxModule() {
  const url = "https://esm.sh/xlsx@0.18.5"
  return import(/* @vite-ignore */ url) as Promise<XlsxModule>
}

type JsZipModule = {
  default: {
    loadAsync(
      data: ArrayBuffer,
    ): Promise<{ files: Record<string, { dir: boolean; async(type: "string"): Promise<string> }> }>
  }
}
type PptxPreviewModule = {
  init(
    container: HTMLElement,
    options: { width: number; height: number },
  ): { preview(data: ArrayBuffer): Promise<void> | void }
}
type PptxSlide = { title: string; lines: string[] }

function loadJsZipModule() {
  const url = "https://esm.sh/jszip@3.10.1"
  return import(/* @vite-ignore */ url) as Promise<JsZipModule>
}

function loadPptxPreviewModule() {
  const url = "https://esm.sh/pptx-preview@1.0.7"
  return import(/* @vite-ignore */ url) as Promise<PptxPreviewModule>
}

async function renderPptx(blob: Blob, container: HTMLDivElement | undefined) {
  if (!container) throw new Error("Missing PPTX preview container")
  container.replaceChildren()
  const module = await loadPptxPreviewModule()
  await module.init(container, { width: 640, height: 360 }).preview(await blob.arrayBuffer())
}

async function extractPptxSlides(blob: Blob): Promise<PptxSlide[]> {
  const JSZip = await loadJsZipModule()
  const archive = await JSZip.default.loadAsync(await blob.arrayBuffer())
  const slidePaths = Object.keys(archive.files)
    .filter((path) => /^ppt\/slides\/slide\d+\.xml$/.test(path))
    .sort((a, b) => Number(a.match(/slide(\d+)\.xml/)?.[1] ?? 0) - Number(b.match(/slide(\d+)\.xml/)?.[1] ?? 0))
  const slides = await Promise.all(
    slidePaths.map(async (path) => {
      const xml = await archive.files[path].async("string")
      const lines = Array.from(xml.matchAll(/<a:t>([\s\S]*?)<\/a:t>/g))
        .map((match) => decodeXmlText(match[1]).trim())
        .filter(Boolean)
      return lines.length > 0 ? { title: lines[0], lines: lines.slice(1) } : undefined
    }),
  )
  return slides.filter((slide): slide is PptxSlide => Boolean(slide))
}

function decodeXmlText(value: string) {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
}

function artifactDownloadFilename(artifact: CoworkArtifact, filename: string) {
  if (filename && filename !== "report") return filename
  const format = artifact.format.toLowerCase()
  return artifact.title.toLowerCase().endsWith(`.${format}`) ? artifact.title : `${artifact.title}.${format}`
}

async function normalizedArtifactDownload(blob: Blob, format: string, filename: string) {
  if (["xlsx", "xlsm"].includes(format.toLowerCase())) return { blob: await normalizedBinaryArtifactBlob(blob, "xlsx") }
  if (!filename.toLowerCase().endsWith(".pdf") || (await artifactBlobIsPdf(blob))) return { blob, filename }
  const text = await blob.text()
  const fallbackFormat = fallbackReportFormat(text)
  return {
    blob: new Blob([text], {
      type: fallbackFormat === "html" ? "text/html;charset=utf-8" : "text/plain;charset=utf-8",
    }),
    filename: filename.replace(/\.pdf$/i, `.${fallbackFormat === "html" ? "html" : "txt"}`),
  }
}

async function artifactBlobIsPdf(blob: Blob) {
  const bytes = new Uint8Array(await blob.slice(0, 5).arrayBuffer())
  return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46 && bytes[4] === 0x2d
}

async function normalizedBinaryArtifactBlob(blob: Blob, format: "xlsx") {
  if (format === "xlsx" && (await artifactBlobIsZip(blob))) return blob
  const text = (await blob.text()).trim()
  try {
    const bytes = Uint8Array.from(atob(text), (char) => char.charCodeAt(0))
    if (format === "xlsx" && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04) {
      return new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
    }
  } catch {
    return blob
  }
  return blob
}

async function artifactBlobIsZip(blob: Blob) {
  const bytes = new Uint8Array(await blob.slice(0, 4).arrayBuffer())
  return bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04
}

function fallbackReportFormat(text: string) {
  if (csvRows(text)) return "csv"
  return reportTextLooksLikeHtml(text) ? "html" : "text"
}

function reportTextLooksLikeHtml(text: string) {
  const value = text.trim().toLowerCase()
  return (
    value.startsWith("<") ||
    /<\/?(?:html|body|section|div|table|thead|tbody|tr|td|th|p|h[1-6]|span|style|header|main|article)\b/.test(value)
  )
}

function FormatPreview(props: { artifact: CoworkArtifact; text: string; format: string }) {
  const language = useLanguage()
  if (props.format === "html" && !props.text && props.artifact.preview_html)
    return <HtmlTextPreview text={props.artifact.preview_html} />
  if (props.format === "json") return <JsonTextPreview text={props.text} />
  if (props.format === "xml") return <XmlTextPreview text={props.text} />
  if (props.format === "csv") return <CsvTextPreview text={props.text} />
  if (props.format === "text") return <TextPreview text={props.text} />
  if (props.format === "pdf") return <TextPreview text={language.t("cowork.pipeline.report.pdfLoading")} />
  if (props.format === "html") return <HtmlTextPreview text={props.text} />
  if (props.artifact.preview_html) return <HtmlTextPreview text={props.artifact.preview_html} />
  return (
    <div class="rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-4">
      <p class="text-13-regular text-v2-text-text-muted">{language.t("cowork.pipeline.report.previewUnavailable")}</p>
      <JsonPreview data={props.artifact.artifact_metadata} />
    </div>
  )
}

function JsonTextPreview(props: { text: string }) {
  return <TextPreview text={prettyJson(props.text)} />
}

function XmlTextPreview(props: { text: string }) {
  return <TextPreview text={prettyXml(props.text)} />
}

function CsvTextPreview(props: { text: string }) {
  const rows = csvRows(props.text)
  if (rows) return <CsvPreview rows={rows} />
  return <TextPreview text={props.text} />
}

function HtmlTextPreview(props: { text: string }) {
  if (props.text.trim().startsWith("<")) return <ScaledHtmlPreview srcdoc={props.text} />
  return <TextPreview text={props.text} />
}

function ScaledHtmlPreview(props: { src?: string; srcdoc?: string }) {
  return <ReportHtmlPreview {...props} />
}

function reportPreviewText(artifact: CoworkArtifact) {
  const metadata = artifact.artifact_metadata
  const candidates = [
    metadata.preview_text,
    metadata.content_preview,
    metadata.content,
    metadata.json,
    metadata.xml,
    metadata.csv,
  ]
  return candidates.find((value): value is string => typeof value === "string" && value.trim().length > 0) ?? ""
}

function previewableReportFormat(format: string) {
  return ["html", "json", "xml", "csv", "pdf", "xlsx", "pptx", "docx"].includes(format)
}

function normalizedReportPreviewFormat(format: string) {
  const normalized = format.toLowerCase().replace(/^\./, "")
  if (["excel", "spreadsheet", "workbook", "xlsm"].includes(normalized)) return "xlsx"
  if (["slides", "presentation", "powerpoint"].includes(normalized)) return "pptx"
  if (["word", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(normalized)) return "docx"
  return normalized
}

function prettyJson(text: string) {
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch {
    return text || "{}"
  }
}

function prettyXml(text: string) {
  return text.replace(/></g, ">\n<") || "<xml />"
}

function EChartsDashboard(props: { charts: EChartsReportChart[] }) {
  return (
    <div class="grid gap-4 lg:grid-cols-2">
      <For each={props.charts}>
        {(chart) => (
          <section class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-4">
            <div class="mb-3 flex items-center justify-between gap-3">
              <h4 class="text-13-medium text-v2-text-text-base">{chart.title}</h4>
              <Show when={chart.type}>
                {(type) => <span class="text-12-regular text-v2-text-text-muted">{type()}</span>}
              </Show>
            </div>
            <EChartsCanvas option={chart.option} />
          </section>
        )}
      </For>
    </div>
  )
}

function EChartsCanvas(props: { option: Record<string, unknown> }) {
  let element: HTMLDivElement | undefined
  let chart:
    | { setOption: (option: Record<string, unknown>) => void; resize: () => void; dispose: () => void }
    | undefined
  let resizeObserver: ResizeObserver | undefined

  createEffect(() => {
    if (!element) return
    void import("echarts").then((echarts) => {
      if (!element) return
      chart?.dispose()
      chart = echarts.init(element, "dark")
      chart.setOption(props.option)
      resizeObserver?.disconnect()
      resizeObserver = new ResizeObserver(() => chart?.resize())
      resizeObserver.observe(element)
    })
  })

  onCleanup(() => {
    resizeObserver?.disconnect()
    chart?.dispose()
  })

  return <div ref={element} class="h-80 w-full" />
}

type EChartsReportChart = {
  title: string
  type?: string
  option: Record<string, unknown>
}

function echartsCharts(artifact: CoworkArtifact): EChartsReportChart[] {
  if (artifact.artifact_metadata.preview_type !== "echarts") return []
  const charts = artifact.artifact_metadata.charts
  if (!Array.isArray(charts)) return []
  return charts.filter(isRecord).flatMap((chart, index) => {
    const option = chart.echarts_option
    if (!isRecord(option)) return []
    return [
      {
        title: typeof chart.title === "string" ? chart.title : `Chart ${index + 1}`,
        type: typeof chart.type === "string" ? chart.type : undefined,
        option,
      },
    ]
  })
}

function shouldRenderEChartsDashboard(artifact: CoworkArtifact) {
  if (artifact.format.toLowerCase() === "html") return false
  return echartsCharts(artifact).length > 0
}

export function ApprovalTab(props: {
  approvals: CoworkApproval[]
  artifacts: CoworkArtifact[]
  onDetails?: (approval: CoworkApproval) => void
  hideReportPicker?: boolean
  onRequest: (artifactID: string, emails: string[], message?: string) => Promise<void>
  onUpdate: (approvalID: string, email: string) => Promise<void>
  onDelete: (approvalID: string) => Promise<void>
}) {
  const language = useLanguage()
  const [store, setStore] = createStore({
    artifactID: "",
    query: "",
    filter: "all",
    sent: false,
    editing: false,
    editingID: "",
    editingEmail: "",
    updating: "",
    error: "",
  })
  const selected = createMemo(() => props.artifacts.find((artifact) => artifact.id === store.artifactID) ?? props.artifacts[0])
  const selectedID = createMemo(() => selected()?.id)
  const reviewers = createMemo(() => props.approvals.filter((approval) => !selected() || approval.report_artifact_id === selected()!.id))
  const visible = createMemo(() => reviewers().filter((approval) => {
    const status = approvalStatusKind(approval.status)
    return (store.filter === "all" || (store.filter === "pending" ? status === "pending" || status === "viewed" : status === store.filter)) &&
      `${approval.approver_email} ${approval.comments ?? ""}`.toLowerCase().includes(store.query.trim().toLowerCase())
  }))
  const summary = createMemo(() => approvalSummary(reviewers()))
  const editedRecipient = createMemo(() => approvalRecipients(store.editingEmail, reviewers().filter((item) => item.id !== store.editingID).map((item) => item.approver_email)))
  createEffect(() => { selectedID(); setStore({ editingID: "", editingEmail: "", query: "", filter: "all", sent: false, error: "" }) })
  return (
    <div class="approval-workspace min-h-0 flex-1 overflow-auto p-6">
      <div class="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 class="text-20-semibold text-v2-text-text-base">{language.t("cowork.pipeline.approval")}</h2>
          <p class="mt-1 max-w-2xl text-13-regular text-v2-text-text-muted">
            {language.t("cowork.approval.description")}
          </p>
        </div>
        <div class="flex gap-2">
          <button
            class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-12-medium text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base disabled:opacity-50"
            disabled={props.approvals.length === 0}
            onClick={() => setStore("editing", (value) => !value)}
          >
            {store.editing ? language.t("cowork.approval.doneEditing") : language.t("cowork.approval.editReviewers")}
          </button>
        </div>
      </div>

      <div class="mb-4 flex flex-wrap items-end gap-3">
        <Show when={!props.hideReportPicker}>
        <label class="flex min-w-0 flex-1 flex-col gap-2 text-12-medium">{language.t("cowork.approval.request.report")}
          <select class="h-10 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3" value={selected()?.id ?? ""}
            disabled={!!store.updating} onChange={(event) => setStore("artifactID", event.currentTarget.value)}>
            <For each={props.artifacts}>{(artifact) => <option value={artifact.id}>{reportDisplayTitle(artifact.title)}</option>}</For>
          </select>
        </label>
        </Show>
        <Show when={selected()}>{(artifact) => <ReportPreviewLink projectID={artifact().project_id} artifactID={artifact().id} companyID={artifact().company_id} />}</Show>
      </div>
      <div class="approval-workspace-progress">
        <div class="approval-workspace-step" data-complete={!!selected()}>{language.t("cowork.approval.workspace.ready")}</div>
        <div class="approval-workspace-step" data-complete={!!summary().total}>{language.t("cowork.approval.workspace.assignedStep")}</div>
        <div class="approval-workspace-step" data-complete={!!summary().total && !summary().pending}>{language.t("cowork.approval.workspace.decision")}</div>
      </div>
      <Show when={store.error}>
        <p role="alert" class="mb-5 text-12-regular text-red-300">{store.error}</p>
      </Show>

      <div class="mb-5 grid gap-3 md:grid-cols-4">
        <For each={(["all", "pending", "approved", "rejected"] as const)}>{(filter) => <button type="button" aria-pressed={store.filter === filter}
          class="rounded-xl text-left aria-pressed:ring-2 aria-pressed:ring-v2-border-border-focus" onClick={() => setStore("filter", filter)}>
          <ApprovalMetric label={language.t(filter === "all" ? "cowork.approval.metric.reviewers" : `cowork.approval.metric.${filter}`)} value={String(filter === "all" ? summary().total : summary()[filter])} />
        </button>}</For>
      </div>

      <div class="approval-workspace-layout">
        <section class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 class="text-15-medium text-v2-text-text-base">{language.t("cowork.approval.package.title")}</h3>
              <p class="mt-1 text-12-regular text-v2-text-text-muted">
                {language.t("cowork.approval.workspace.reportScoped")}
              </p>
            </div>
            <ApprovalBadge status={summary().overallStatus} />
          </div>

          <input type="search" class="mb-4 h-10 w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 text-13-regular"
            aria-label={language.t("cowork.approval.workspace.search")} placeholder={language.t("cowork.approval.workspace.search")}
            value={store.query} onInput={(event) => setStore("query", event.currentTarget.value)} />

          <div class="overflow-hidden rounded-lg border border-v2-border-border-base bg-v2-background-bg-base">
            <For
              each={visible()}
              fallback={
                <div class="px-3 py-8 text-center text-13-regular text-v2-text-text-muted">
                  {language.t(reviewers().length ? "cowork.approval.workspace.noMatches" : "cowork.pipeline.approval.empty")}
                </div>
              }
            >
              {(approval) => (
                <div class="approval-workspace-reviewer flex flex-wrap items-center justify-between gap-3 text-13-regular">
                  <Show
                    when={store.editing && store.editingID === approval.id}
                    fallback={
                      <div class="min-w-0">
                        <div class="truncate text-v2-text-text-base">{approval.approver_email}</div>
                        <div class="truncate text-12-regular text-v2-text-text-muted">
                          {language.t("cowork.approval.reviewer.role")}
                        </div>
                      </div>
                    }
                  >
                    <input
                      class="h-8 min-w-0 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-13-regular text-v2-text-text-base outline-none"
                      value={store.editingEmail}
                      onInput={(event) => setStore("editingEmail", event.currentTarget.value)}
                    />
                  </Show>
                  <CopyableReportLink href={`/review/${encodeURIComponent(approval.id)}`} review />
                  <Show when={props.onDetails}><button type="button" class="report-workspace-action" onClick={() => props.onDetails?.(approval)}>{language.t("cowork.enterprise.approvals.details")}</button></Show>
                  <ApprovalBadge status={approval.status} />
                  <Show
                    when={store.editing}
                    fallback={<span class="text-v2-text-text-muted">{formatApprovalDate(approval.updated_at)}</span>}
                  >
                    <div class="flex justify-end gap-2">
                      <Show
                        when={store.editingID === approval.id}
                        fallback={
                          <IconButtonV2
                            size="small"
                            variant="ghost"
                            icon={<IconV2 name="edit" size="small" />}
                            aria-label={language.t("common.edit")}
                            title={language.t("common.edit")}
                            onClick={() =>
                              setStore({ editingID: approval.id, editingEmail: approval.approver_email, error: "" })
                            }
                          />
                        }
                      >
                        <button
                          class="rounded-md border border-v2-border-border-base px-2 py-1 text-12-medium text-v2-text-text-muted hover:text-v2-text-text-base disabled:opacity-50"
                          disabled={editedRecipient().emails.length !== 1 || !!editedRecipient().invalid.length || !!editedRecipient().assigned.length || !!store.updating}
                          onClick={() => {
                            setStore({ updating: approval.id, error: "" })
                            void props
                              .onUpdate(approval.id, editedRecipient().emails[0])
                              .then(() => setStore({ editingID: "", editingEmail: "" }))
                              .catch((error) =>
                                setStore("error", error instanceof Error ? error.message : String(error)),
                              )
                              .finally(() => setStore("updating", ""))
                          }}
                        >
                          {language.t("common.save")}
                        </button>
                      </Show>
                      <button
                        class="rounded-md border border-v2-border-border-base px-2 py-1 text-12-medium text-v2-text-text-muted hover:text-v2-text-text-base disabled:opacity-50"
                        disabled={store.updating === approval.id}
                        onClick={() => {
                          setStore({ updating: approval.id, error: "" })
                          void props
                            .onDelete(approval.id)
                            .catch((error) => setStore("error", error instanceof Error ? error.message : String(error)))
                            .finally(() => setStore("updating", ""))
                        }}
                      >
                        {language.t("common.delete")}
                      </button>
                    </div>
                  </Show>
                  <Show when={store.editingID === approval.id && editedRecipient().assigned.length}><p role="alert" class="w-full text-12-regular">{language.t("cowork.approval.workspace.duplicate")}</p></Show>
                  <Show when={approval.comments}><details class="w-full text-12-regular"><summary>{language.t("cowork.approval.workspace.feedback")}</summary><p class="mt-2 whitespace-pre-wrap break-words">{approval.comments}</p></details></Show>
                </div>
              )}
            </For>
          </div>
        </section>

        <aside class="approval-workspace-request">
          <Show when={selected()?.id} keyed>{(_id) => <ApprovalRequestForm artifacts={selected() ? [selected()!] : []} hideReportPicker
            assignedEmails={reviewers().map((item) => item.approver_email)} onRequest={async (artifactID, emails, message) => {
              await props.onRequest(artifactID, emails, message)
              if (selected()?.id === artifactID) setStore("sent", true)
            }} />}</Show>
          <Show when={store.sent}><p role="status" class="mt-3 text-12-regular text-v2-state-fg-success">{language.t("cowork.approval.workspace.sent")}</p></Show>
        </aside>
      </div>
    </div>
  )
}

function ApprovalMetric(props: { label: string; value: string }) {
  return (
    <div class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
      <div class="text-22-semibold text-v2-text-text-base">{props.value}</div>
      <div class="mt-1 text-12-regular text-v2-text-text-muted">{props.label}</div>
    </div>
  )
}

function ApprovalBadge(props: { status: string }) {
  const language = useLanguage()
  const kind = approvalStatusKind(props.status)
  return (
    <span
      class="inline-flex w-fit items-center rounded-full border px-2 py-1 text-11-medium"
      classList={{
        "border-emerald-500/30 bg-emerald-500/10 text-emerald-300": kind === "approved",
        "border-red-500/30 bg-red-500/10 text-red-300": kind === "rejected",
        "border-blue-500/30 bg-blue-500/10 text-blue-300": kind === "viewed",
        "border-v2-border-border-base bg-v2-background-bg-base text-v2-text-text-muted": kind === "pending",
      }}
    >
      {language.t(`cowork.approval.status.${kind}`)}
    </span>
  )
}

function approvalSummary(approvals: CoworkApproval[]) {
  const approved = approvals.filter((approval) => approvalStatusKind(approval.status) === "approved").length
  const rejected = approvals.filter((approval) => approvalStatusKind(approval.status) === "rejected").length
  const pending = approvals.length - approved - rejected
  return {
    total: approvals.length,
    approved,
    rejected,
    pending,
    overallStatus: rejected ? "rejected" : approvals.length > 0 && pending === 0 ? "approved" : "pending",
  }
}

function approvalStatusKind(status: string) {
  const normalized = status.toLowerCase()
  if (normalized.includes("approve")) return "approved"
  if (normalized.includes("reject") || normalized.includes("decline")) return "rejected"
  if (normalized.includes("view")) return "viewed"
  return "pending"
}

function formatApprovalDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return formatDateTime(date, { dateStyle: "short", timeStyle: "medium" })
}

function JsonPreview(props: { data: Record<string, unknown> }) {
  const rows = tableRows(props.data)
  if (rows) return <CsvPreview rows={rows} />
  return <TextPreview text={JSON.stringify(props.data, null, 2)} />
}

function tableRows(data: Record<string, unknown>) {
  const rows = Array.isArray(data.rows)
    ? data.rows
    : Array.isArray(data.data)
      ? data.data
      : Array.isArray(data)
        ? data
        : undefined
  if (!rows?.length || typeof rows[0] !== "object" || rows[0] === null) return undefined
  const headers = Object.keys(rows[0] as Record<string, unknown>)
  return [
    headers,
    ...rows.slice(0, 50).map((row) => headers.map((header) => String((row as Record<string, unknown>)[header] ?? ""))),
  ]
}

function TextPreview(props: { text: string }) {
  return (
    <pre class="max-h-[520px] overflow-auto whitespace-pre-wrap rounded-md bg-v2-background-bg-base p-3 text-12-regular text-v2-text-text-base">
      {props.text}
    </pre>
  )
}

function CsvPreview(props: { rows: string[][]; title?: string; showExpand?: boolean }) {
  const language = useLanguage()
  const title = () => props.title || language.t("cowork.table.preview")
  return (
    <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
      <Show when={props.showExpand !== false}>
        <div class="flex justify-end border-b border-v2-border-border-base p-1">
          <TableExpandButton title={title()} rows={props.rows} />
        </div>
      </Show>
      <TableGrid rows={props.rows} maxHeight="max-h-[560px]" />
    </div>
  )
}

function TableExpandButton(props: { title: string; rows: string[][] }) {
  const language = useLanguage()
  const [store, setStore] = createStore({ open: false })
  return (
    <>
      <button
        type="button"
        class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
        title={language.t("cowork.table.expand")}
        aria-label={language.t("cowork.table.expand")}
        onClick={() => setStore("open", true)}
      >
        <IconV2 name="expand" size="small" />
      </button>
      <Show when={store.open}>
        <Portal>
          <div
            class="fixed inset-0 z-[1000] overflow-y-auto bg-black/75 px-4 py-10 backdrop-blur-sm"
            onClick={() => setStore("open", false)}
          >
            <section
              class="mx-auto flex h-[min(82vh,860px)] w-[min(96vw,1440px)] flex-col overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <header class="flex shrink-0 items-center justify-between gap-3 border-b border-v2-border-border-base px-4 py-3">
                <h3 class="min-w-0 truncate text-16-semibold text-v2-text-text-base">{props.title}</h3>
                <button
                  type="button"
                  class="rounded p-1 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                  title={language.t("common.close")}
                  aria-label={language.t("common.close")}
                  onClick={() => setStore("open", false)}
                >
                  <IconV2 name="close" size="normal" />
                </button>
              </header>
              <div class="min-h-0 flex-1 p-4">
                <TableGrid rows={props.rows} maxHeight="max-h-full" />
              </div>
            </section>
          </div>
        </Portal>
      </Show>
    </>
  )
}

function TableGrid(props: { rows: string[][]; maxHeight: string }) {
  const [header, ...body] = props.rows
  return (
    <div
      class={`${props.maxHeight} overflow-auto rounded-lg bg-v2-background-bg-base [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]`}
    >
      <table class="min-w-full border-separate border-spacing-0 text-left text-12-regular text-v2-text-text-base">
        <thead class="sticky top-0 z-10 bg-v2-background-bg-layer-02 text-v2-text-text-muted shadow-[0_1px_0_var(--v2-border-border-base)]">
          <tr>
            <th class="sticky left-0 z-20 w-10 border-r border-v2-border-border-base bg-v2-background-bg-layer-02 px-2 py-2 text-center font-medium text-v2-text-text-faint">
              #
            </th>
            <For each={header ?? []}>
              {(cell) => (
                <th class="max-w-[260px] whitespace-nowrap border-r border-v2-border-border-base px-3 py-2 font-medium last:border-r-0">
                  <span class="block truncate">{cell}</span>
                </th>
              )}
            </For>
          </tr>
        </thead>
        <tbody>
          <For each={body}>
            {(row, index) => (
              <tr class="odd:bg-v2-background-bg-layer-01/40 hover:bg-v2-background-bg-layer-02/70">
                <td class="sticky left-0 z-[1] w-10 border-r border-b border-v2-border-border-base bg-inherit px-2 py-2 text-center text-v2-text-text-faint">
                  {index() + 1}
                </td>
                <For each={normalizedTableRow(row, header?.length ?? row.length)}>
                  {(cell) => (
                    <td class="max-w-[260px] whitespace-pre-wrap border-r border-b border-v2-border-border-base px-3 py-2 align-top leading-5 last:border-r-0">
                      {cell || "-"}
                    </td>
                  )}
                </For>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
  )
}

function recordsToTableRows(headers: string[], rows: Array<Record<string, unknown>>) {
  return [headers, ...rows.map((row) => headers.map((header) => String(row[header] ?? "")))]
}

function recordsToTableRowsAuto(rows: Array<Record<string, unknown>>) {
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
  return recordsToTableRows(headers, rows)
}

function normalizedTableRow(row: string[], length: number) {
  return Array.from({ length }, (_, index) => row[index] ?? "")
}

function csvRows(text?: string | null) {
  if (!text?.trim()) return undefined
  const lines = normalizedCsvText(text)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
  const headerIndex = lines.findIndex((line) => line.includes(","))
  if (headerIndex < 0) return undefined
  const header = parseCsvLine(lines[headerIndex])
  const width = header?.length ?? 0
  if (width < 2) return undefined
  const rows = lines
    .slice(headerIndex + 1)
    .filter((line) => line.includes(","))
    .map((line) => normalizedCsvRow(parseCsvLine(line), width))
  if (rows.length === 0) return undefined
  return [normalizedTableRow(header, width), ...rows]
}

function normalizedCsvRow(row: string[], width: number) {
  if (row.length <= width) return normalizedTableRow(row, width)
  return [...row.slice(0, width - 1), row.slice(width - 1).join(", ")]
}

function normalizedCsvText(text: string) {
  const value = text.trim()
  return value.replaceAll("\\r\\n", "\n").replaceAll("\\n", "\n").replaceAll("\\r", "\n").replace(/\r\n?/g, "\n")
}

function sheetRows(part: { part_type: string; preview_text?: string | null }) {
  if (part.part_type !== "sheet") return undefined
  return csvRows(part.preview_text)
}

function parseCsvLine(line: string) {
  const result: string[] = []
  let current = ""
  let quoted = false
  for (const char of line) {
    if (char === '"') {
      quoted = !quoted
      continue
    }
    if (char === "," && !quoted) {
      result.push(current.trim())
      current = ""
      continue
    }
    current += char
  }
  result.push(current.trim())
  return result
}

function FileRow(props: { file: CoworkFile; active: boolean; onClick: () => void }) {
  return (
    <button
      class="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-v2-background-bg-layer-02 data-[active=true]:bg-v2-background-bg-layer-03"
      data-active={props.active}
      onClick={props.onClick}
    >
      <FileIcon node={{ path: props.file.filename, type: "file" }} class="size-4 shrink-0" />
      <span class="min-w-0 flex-1 truncate text-13-regular text-v2-text-text-base">{props.file.filename}</span>
    </button>
  )
}

function EmptyList(props: { hasContext: boolean }) {
  const language = useLanguage()
  return (
    <p class="px-2 py-3 text-13-regular text-v2-text-text-muted">
      {language.t(props.hasContext ? "cowork.files.panel.empty" : "cowork.files.panel.noContext")}
    </p>
  )
}

function EmptyPreview() {
  const language = useLanguage()
  return (
    <div class="flex h-full items-center justify-center text-center">
      <div>
        <IconV2 name="workspace" class="mx-auto mb-3 text-v2-icon-icon-muted" />
        <p class="text-14-medium text-v2-text-text-base">{language.t("cowork.files.panel.noFileTitle")}</p>
        <p class="mt-2 text-13-regular text-v2-text-text-muted">{language.t("cowork.files.panel.noFileDescription")}</p>
      </div>
    </div>
  )
}
